module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('h1', {
    staticClass: "chapter-title",
    attrs: {
      "id": "ch-1"
    }
  }, [_vm._v("Introduction to Ezra and Nehemiah")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The events behind Ezra-Nehemiah are situated at a watershed in Jewish history and, consequently, at a turning point in the story of the people of God. Landless and templeless, Jewish exiles experienced the trauma of loss of autonomy due to the Babylonian exile; however, they survived against heavy odds. Their very identity hung in the balance, but they forged new associations while retaining ties to their ancient past. The light of hope shone in the darkness of exile from the unexpected orders of a Persian emperor who allowed Jews to return to their homeland. This is the story of a new Israel forged out of the old. It is the text of a people clinging to its genealogical past and attempting to preserve its heritage while walking forward into uncharted territory. It is also the perennial story of God’s providence and guidance of his people through unusual leaders in times of crisis. The impact of the decisions they made on the subsequent community of faith cannot be overestimated.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This commentary seeks to bring into relief the Second Temple context in which Ezra-Nehemiah was written. Certain concepts, such as institutional penitential prayer and the holiness of the city of Jerusalem, begin in this period, and their embryonic stage is reflected in Ezra-Nehemiah. These and other important religious notions are present in the textual seedbed of Ezra-Nehemiah and continue among later Jewish communities of this era. Some of this development stems from the impact of the text of Ezra-Nehemiah, while some is simply an outgrowth of the times and circumstances of the exilic and postexilic Jewish situation. Whatever the trajectory, Ezra-Nehemiah provides an early repository of valuable information regarding key concepts in Second Temple Judaism. These ideas surface in texts from a variety of Jewish communities, including, but not limited to, the Dead Sea Scrolls and the New Testament.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1c",
    attrs: {
      "id": "sec-1"
    }
  }, [_vm._v("I. THE TEXT")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The original text of Ezra-Nehemiah was not in two books, as in the current version, but was written on one scroll. The language reflects the languages in current use by Jews during the Persian period: Hebrew and Aramaic. Scholars continue to debate the nature of the original version of the text and its underlying sources. The work was copied by the writers of the Dead Sea Scrolls and the Septuagint (LXX) and was included in the early Jewish canon of Scripture.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-1"
    }
  }, [_vm._v("A. LANGUAGE")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The language of Ezra-Nehemiah is predominantly Hebrew, but two substantial sections of Ezra—largely accounts from official documents—are written in Imperial Aramaic (Ezra 4:8–6:18; 7:12–26). This dialect of Aramaic was first internationalized by the Assyrians and remained in use between 700 and 200 BCE."), _c('a', {
    attrs: {
      "href": "#/#foot-note-969",
      "id": "footnote-969"
    }
  }, [_c('sup', [_vm._v("1")])]), _vm._v(" Since Aramaic was the official language of the Babylonian Empire, it became the spoken language of the Jews during the period of their exile in Babylon. Many Jewish names of this period reflect direct Babylonian influence (e.g., Zerubbabel, “seed of Babylon”). Isolated Aramaic phrases occur in various parts of the Bible (see Gen 31:47; Jer 10:11), but only the books of Ezra and Daniel contain large sections in the language.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Elephantine documents of the late fifth century BCE also attest to Imperial Aramaic. The Jewish community in Elephantine, an island of the Nile called Yeb in Aramaic, appears to have been a colony of mercenaries founded sometime in the sixth century BCE, after the destruction of Jerusalem."), _c('a', {
    attrs: {
      "href": "#/#foot-note-968",
      "id": "footnote-968"
    }
  }, [_c('sup', [_vm._v("2")])]), _vm._v(" By the time of Ezra and Nehemiah, this was a community of families with property. The Elephantine papyri, which include over a hundred documents and many ostraca representing correspondence between this Jewish colony in Egypt and the governors of Samaria and Yehud, reveal much regarding the politics, laws, and customs of the time, in particular the syncretistic character of the community’s religion. A Jewish temple was built there, where pagan deities were worshiped alongside Yahweh."), _c('a', {
    attrs: {
      "href": "#/#foot-note-967",
      "id": "footnote-967"
    }
  }, [_c('sup', [_vm._v("3")])]), _vm._v(" One Elephantine letter refers to Sanballat, governor of Samaria (cf. Neh 2:10). Another letter, addressed to the Jews at Elephantine, reveals a Persian attempt to enforce laws regarding the Jewish feasts of Unleavened Bread and Passover.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-2"
    }
  }, [_vm._v("B. SOURCES")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Many of the sources of Ezra-Nehemiah are easy to identify. These include official documents, letters, memoirs, and lists (e.g., genealogies, lists of priests, lists of villages). About 70 percent of the book is made up of lists and other archival material."), _c('a', {
    attrs: {
      "href": "#/#foot-note-966",
      "id": "footnote-966"
    }
  }, [_c('sup', [_vm._v("4")])]), _vm._v(" Below is a list of the most obvious sources under each of the three major sections of the work: Prologue, Ezra Memoir, Nehemiah Memoir.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Prologue: (1) the decree of Cyrus (Ezra 1:2–4); (2) an inventory of temple articles (Ezra 1:9–11); (3) a list of returnees (Ezra 2:1–3:1); (4) two letters of opposition noted (Ezra 4:6–7); (5) the accusation of Rehum against the Jews (Ezra 4:11–16); (6) the reply of Artaxerxes I (Ezra 4:17–22); (7) a report from Tattenai (Ezra 5:7–17); (8) Darius’s reply to Tattenai, including a memorandum of Cyrus’s decree (Ezra 6:2b–22).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra Memoir: (1) a copy of Artaxerxes’s letter to Ezra (Ezra 7:12–26); (2) a list of the returnees with Ezra (Ezra 8:1–14); (3) an inventory of vessels (Ezra 8:26–27); (4) Ezra’s prayer (Ezra 9:6–15); (5) a list of those who had intermarried with foreigners (Ezra 10:18–44). Some scholars place the sources found in Nehemiah 8–10 within the Ezra Memoir, but this is not necessary (see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03a#exc17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 17, “Should Nehemiah 8 follow Ezra 8?,” pp. 355–57")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nehemiah Memoir: (1) a list of returnees (Neh 7:6–72a); (2) the reading of the Law (Neh 7:73b–8:18); (3) Nehemiah’s prayer of confession (Neh 9:6–37); (4) the list of covenant signatories (Neh 10:1–28); (5) the covenant itself (Neh 10:28–39); (6) a list of the residents of Jerusalem (Neh 11:3–24); (7) a list of villages of Judah and Benjamin (Neh 11:25–36); and (8) a list of priests and Levites (Neh 12:1–26). Genealogies and lists of names and towns were important in this period when people were returning to their ancestral homes and needed to prove their claims and status in the new community.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The language and style of Ezra-Nehemiah’s sources reflect that of the Persian period. C. Hensley compared these sources with thirty-two Persian documents known to date and concluded that, linguistically, stylistically, and historically, the Ezran documents correspond to the non-biblical documents of the Achaemenid period."), _c('a', {
    attrs: {
      "href": "#/#foot-note-965",
      "id": "footnote-965"
    }
  }, [_c('sup', [_vm._v("5")])]), _vm._v(" K. Hoglund, too, claims “general authenticity” for these citations based on comparison with Achaemenid documents."), _c('a', {
    attrs: {
      "href": "#/#foot-note-964",
      "id": "footnote-964"
    }
  }, [_c('sup', [_vm._v("6")])]), _vm._v(" S. Mowinckel argued that the Nehemiah Memoir reveals parallels with ancient Near Eastern royal inscriptions that commemorate achievements. Others have compared the “remember” sections to votive inscriptions in various Aramaic documents."), _c('a', {
    attrs: {
      "href": "#/#foot-note-963",
      "id": "footnote-963"
    }
  }, [_c('sup', [_vm._v("7")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-3"
    }
  }, [_vm._v("C. SECTIONS")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The book of Ezra-Nehemiah contains three large sections: the Prologue (Ezra 1–6), the Ezra Memoir (Ezra 7–10), and the Nehemiah Memoir (Nehemiah 1–13). All three sections are written in a straightforward narrative style rather than in the mythical, epic, or annalistic styles of the ancient Near East."), _c('a', {
    attrs: {
      "href": "#/#foot-note-962",
      "id": "footnote-962"
    }
  }, [_c('sup', [_vm._v("8")])]), _vm._v(" The text moves from narrative prose in Ezra 1–6 to partly autobiographical narrative in the Ezra Memoir to mostly autobiographical narrative in the Nehemiah Memoir. The sections of Ezra (Ezra 1–6 and Ezra 7–10) transition more smoothly than those in Nehemiah, which is sometimes disjunct in subject, literary style, and story line."), _c('a', {
    attrs: {
      "href": "#/#foot-note-961",
      "id": "footnote-961"
    }
  }, [_c('sup', [_vm._v("9")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-1"
    }
  }, [_vm._v("1. Ezra Memoir")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The last four chapters of Ezra are often known as the Ezra Memoir. Some of the material represents a memoir style (Ezra 7:27–9:15) while the rest is written in third person (Ezra 7:1–26; 10). The latter may reflect the hand of an editor, or it may derive from an independent source."), _c('a', {
    attrs: {
      "href": "#/#foot-note-960",
      "id": "footnote-960"
    }
  }, [_c('sup', [_vm._v("10")])]), _vm._v(" It is also possible that the change of form is just stylistic (cf. Isa 6–7; Hos 1, 3). Elias Bickerman compares the change of person to other ancient Near Eastern texts and concludes that it is a device which “served to authenticate the narrative and came into historical writing from the diplomatic style, where exactness of quotation was absolutely necessary.” He points to the Cyrus Cylinder, where the author recounts the poor reign of Nabonidus and Cyrus’s subsequent conquest of the city of Babylon and then abruptly changes from third person to first with “I am Cyrus …” and continues with Cyrus’s personal account of the events."), _c('a', {
    attrs: {
      "href": "#/#foot-note-959",
      "id": "footnote-959"
    }
  }, [_c('sup', [_vm._v("11")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Ezra Memoir chronicles the second return of a large group of Jews from exile in Babylonia under the leadership of Ezra, who is both priest and scribe. Ezra is distinguished by his dedication to study the Torah, observe it, and teach it to the people of Israel (Ezra 7:10). Artaxerxes I, the Persian king, supports Ezra’s mission with financial aid and provisions for the temple service. He also issues a royal edict to Ezra to appoint judges and teach Jewish law (Ezra 7:12–26). The remainder of the Ezra Memoir is devoted to the crisis of intermarriage with those outside of the community. Apparently, Ezra had taught the law sufficiently that by this point the officers understood that certain restrictions applied to intermarriage. Conflating verses from Deuteronomy (e.g., Ezra 9:1–2; Deut 7:1–3; 23:1–9), the officers report to Ezra that there was a large number of intermarriages within the community. Appalled, Ezra applies an unusually strict interpretation of the law to prohibit all forms of intermarriage, and he even orders the dissolution of existing unions with foreigners (Ezra 10:11).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Most scholars think that Neh 8, an account of Ezra reading the Torah to the people, was originally included in the Ezra Memoir (see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03a#exc17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 17, “Should Nehemiah 8 follow Ezra 8?,” pp. 355–57")]), _vm._v("). Although this suggestion has been the reigning theory, recently several scholars have argued that there are better reasons to leave Nehemiah 8 in its present location. This commentary follows that view.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It is curious that the Ezra Memoir (without the addition of Neh 8) does not mention Nehemiah. Williamson suggests that the Ezra Memoir was compiled from temple archives by circles that were favorable to the reforms themselves but less supportive of Nehemiah. He claims that Ezra wrote the original narrative to “show as nearly as possible how his activity conformed to his commission.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-958",
      "id": "footnote-958"
    }
  }, [_c('sup', [_vm._v("12")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-2"
    }
  }, [_vm._v("2. Nehemiah Memoir")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The Nehemiah Memoir, a first-person account by Nehemiah or at his direction, includes, according to most scholars, Neh 1:1–7:5 and large parts of chs. 12–13."), _c('a', {
    attrs: {
      "href": "#/#foot-note-957",
      "id": "footnote-957"
    }
  }, [_c('sup', [_vm._v("13")])]), _vm._v(" The author, either Nehemiah or a later compiler, utilizes lists of cities, inhabitants, builders, signatories, and cultic personnel throughout the work. The long section of Neh 7:5–10:40(39) has been spliced into the Nehemiah Memoir. The first seam is evident at the end of Neh 7:4, where the text raises the issue of repopulating Jerusalem but is interrupted by the presentation of a list of first returnees from exile under Zerubbabel (Neh 7:5–72) and a narrative section reporting Ezra’s public reading of the Torah along with the covenant renewal (Nehemiah 8–10). The author does not resume discussion of the city of Jerusalem until the second seam at the beginning of Neh 11:1."), _c('a', {
    attrs: {
      "href": "#/#foot-note-956",
      "id": "footnote-956"
    }
  }, [_c('sup', [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Nehemiah Memoir was probably originally a report from Nehemiah on his commission as governor and especially on the commission to rebuild the wall of Jerusalem. As Williamson notes, however, it could not have been the original document, since that would have been in Aramaic and probably would not have included the prayers. Nehemiah’s achievements and defense against the neighboring leaders’ criticisms, however, would have been vital for the report to the king."), _c('a', {
    attrs: {
      "href": "#/#foot-note-955",
      "id": "footnote-955"
    }
  }, [_c('sup', [_vm._v("15")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It is interesting to compare the Nehemiah Memoir with other ancient Near Eastern texts. Egyptian votive texts emphasize prayers and donations to temples. Nehemiah reminds the reader of Udjahorresnet, a pious Egyptian under the rules of Cambyses and Darius I. Both Nehemiah and Udjahorresnet adopt an apologetic tone in telling the deity of their goals and the difficulties they have overcome in achieving them."), _c('a', {
    attrs: {
      "href": "#/#foot-note-954",
      "id": "footnote-954"
    }
  }, [_c('sup', [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nehemiah 1–6 has much in common with Ezra 1–6. Both begin with a construction project (temple or wall) inspired and facilitated by the hand of God. Janzen gives an account of the similarities: In both,")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("a Persian king commissions the work; returnees carry it out; they are opposed by neighbors, at least some of whom worship Yahweh [in some way]; but are successful. In both cases, the community leaders inform their adversaries that they have no right to participate in the building project (Ezra 4.1–3 and Neh 2.20). And to emphasize this statement in the context of the wall construction, Neh 3.1–32 gives a list of the Israelites who build the wall, marking them out as ones who, unlike their enemies, do have shares and rights and historical claims in the city (cf. Neh 2.20). In this story the wall, like the city and temple, is for Israel and by Israel. It marks them out and constructs a boundary around them."), _c('a', {
    attrs: {
      "href": "#/#foot-note-953",
      "id": "footnote-953"
    }
  }, [_c('sup', [_vm._v("17")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The central focus of the Nehemiah Memoir as it stands in the full text of Ezra-Nehemiah, however, is not the wall but the rebuilding of the community by means of the Torah and covenant renewal."), _c('a', {
    attrs: {
      "href": "#/#foot-note-952",
      "id": "footnote-952"
    }
  }, [_c('sup', [_vm._v("18")])]), _vm._v(" After Nehemiah secures the city with the wall (Neh 1–6), Israel can then focus on the more significant matter of rededication to keeping God’s law (Neh 8–10). It is only after the reading of the Torah (Neh 8) and the people’s promise to keep it (Neh 9–10) that the people are fit to move in and repopulate the holy city of Jerusalem (Neh 11:1).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Nehemiah Memoir contains a long gap between the wall building and the events of ch. 13. Nehemiah was apparently governor of Jerusalem for twelve years, after which he went back to Babylon and then returned later to Jerusalem. Similarly, Arsames, satrap of Egypt, took a three-year leave of absence from his duties."), _c('a', {
    attrs: {
      "href": "#/#foot-note-951",
      "id": "footnote-951"
    }
  }, [_c('sup', [_vm._v("19")])]), _vm._v(" The Nehemiah Memoir describes approximately one year of Nehemiah’s governorship and then treats several isolated topics from as many as fifteen years later during his “second term.” A lay reformer, Nehemiah mobilizes the lower class into a stable society by instituting various reforms reminiscent of the work of Solon of Athens a century earlier."), _c('a', {
    attrs: {
      "href": "#/#foot-note-950",
      "id": "footnote-950"
    }
  }, [_c('sup', [_vm._v("20")])]), _vm._v(" While the particular reforms do not appear long-lived, the stability Nehemiah afforded the Jews at this point made it possible for the Torah to develop and become the guiding authority for the entire Second Temple period and beyond."), _c('a', {
    attrs: {
      "href": "#/#foot-note-949",
      "id": "footnote-949"
    }
  }, [_c('sup', [_vm._v("21")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-3"
    }
  }, [_vm._v("3. Prologue")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The first six chapters of Ezra are an account of the earliest return from exile under the policy of Cyrus II, the Persian king. The events described occurred decades before the ministry of Ezra. According to the narrative, Cyrus decides to rebuild the temple at Jerusalem in gratitude for divine aid in establishing his empire, and he encourages the Jews in exile to return and fulfill this desire. In response, the Jewish leaders Sheshbazzar and Zerubbabel facilitate a massive return of Jews to Jerusalem in order to rebuild the temple and resettle the land. The text describes the opposition this group faced from the local population in Judah, whom they refused to include in the project (Ezra 4:1–5). The text also records similar kinds of opposition in later years (Ezra 4:6–24a). The Prologue concludes with the resurgence of building activity inspired by the prophets Haggai and Zechariah, its endorsement by Darius I, and finally the completion of the temple in 515/516 BCE.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("H. G. M. Williamson has argued persuasively that the author of Ezra-Nehemiah composed Ezra 1–6 after the rest of the book."), _c('a', {
    attrs: {
      "href": "#/#foot-note-948",
      "id": "footnote-948"
    }
  }, [_c('sup', [_vm._v("22")])]), _vm._v(" According to Williamson, the purpose of the addition was, first, “concerned to trace lines of continuity between the restored community and the Israel of preexilic times, between the second and first temples, and so on. Legitimacy is clearly a fundamental issue.” Second, it was “to justify the rejection of the offer of northern participation in the restoration itself.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-947",
      "id": "footnote-947"
    }
  }, [_c('sup', [_vm._v("23")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra, Nehemiah, or someone else combined these three sections (Prologue, Ezra Memoir, and Nehemiah Memoir) into one document. Williamson’s theory, which has had a large following among scholars, traces three stages of writing: (1) the primary sources contemporary with the events; (2) the compilation of the Ezra Memoir, the Nehemiah Memoir, and other sources to form most of Ezra 7–10 and Neh 1–13 (11:21–12:26 were added separately); and (3) the later addition of the Prologue in Ezra 1–6."), _c('a', {
    attrs: {
      "href": "#/#foot-note-946",
      "id": "footnote-946"
    }
  }, [_c('sup', [_vm._v("24")])]), _vm._v(" For Williamson, the author’s purpose in composing the work was to countermand the claims of the Samaritans who had recently built a temple on Mt. Gerizim (in ca. 300 BCE), as well as to demonstrate, by the story of Ezra and Nehemiah, the continuity between the author’s community and that of the First Temple period. Alternatively, some scholars are using a less documentary approach and adopting, as T. C. Eskenazi puts it, “a more gradual, incremental process in which scribal activities responded in a continuous fashion to issues internal to the text and those external, historical circumstances that unfolded.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-945",
      "id": "footnote-945"
    }
  }, [_c('sup', [_vm._v("25")])]), _vm._v(" In any case, the concerns of the temple and the continuity of the holy people are central in Ezra-Nehemiah; but, just as importantly and maybe more so, rebuilding the community by means of the Torah is a primary emphasis (for further discussion, see "), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#sec2-19"
    }
  }, [_vm._v("“Torah,”")]), _vm._v(" "), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#sec2-20"
    }
  }, [_vm._v("“Sanctuary: The Second Temple,”")]), _vm._v(" and "), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#sec2-21"
    }
  }, [_vm._v("“Holy People,” pp. 62–88")]), _vm._v(").")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-4"
    }
  }, [_vm._v("D. CANONICITY")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Ezra-Nehemiah was apparently held in high regard among Second Temple Jews long before the traditional canon was determined. The work is included in LXX and the Dead Sea Scrolls, and it is alluded to in the writings of Josephus.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("First Esdras, the version of Ezra included in LXX, is a divergent account of Ezra-Nehemiah written in eloquent Greek of the same era as Judith, Sirach and Maccabees—probably the late second or early first century BCE."), _c('a', {
    attrs: {
      "href": "#/#foot-note-944",
      "id": "footnote-944"
    }
  }, [_c('sup', [_vm._v("26")])]), _vm._v(" The compilation is primarily the book of Ezra but also draws from parts of Chronicles and Nehemiah. Chronology is sometimes confused in the book; for example, the author presents the Persian emperor Artaxerxes as the successor to Cyrus. He also places Neh 8 after Ezra 10.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In a striking addition to the biblical text, the author recounts a story about a contest at the court of Darius where Zerubbabel and two other bodyguards of the king are asked the question, “What is the strongest thing?” Zerubbabel wins by stating that women are stronger even than the king because men owe their lives to women and make fools of themselves for them. He also gives another answer suggesting that Truth is the strongest thing of all. Darius is so impressed with the second answer that he gives Zerubbabel his request. Zerubbabel reminds the king of his vow to give permission for the Jews to return to their homeland and restore their temple. Contrary to the biblical text, in which the beneficence of the Persians is stressed, 1 Esdras gives credit to the wisdom of Zerubbabel. T. C. Eskenazi claims that the story’s purpose was to exalt Zerubbabel and to encourage the rebuilding of the new community under a Davidic descendant."), _c('a', {
    attrs: {
      "href": "#/#foot-note-943",
      "id": "footnote-943"
    }
  }, [_c('sup', [_vm._v("27")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("First Esdras is important for translators of Ezra-Nehemiah. It supports the canonical Masoretic Text (MT) over LXX in all but one case (1 Esd. 2:25; Ezra 4:23). It also reveals an attempt to put in order the seemingly mislocated account of Torah reading in Neh 7:72–8:12. The author places this section in 1 Esd 8:88–9:36, thereby closing the thirteen-year gap between Ezra’s commission and his partnership with Nehemiah. Other than this section, 1 Esdras ignores Nehemiah completely.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-parab"
  }, [_vm._v("Ezra-Nehemiah is one book, Esdras B, in LXX; 1 Esdras is Esdras A. The table below sets forth the variety of texts and titles.")]), _vm._v(" "), _c('table', [_c('tbody', [_c('tr', [_c('td', {
    staticClass: "wid25"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Text Type")])]), _vm._v(" "), _c('td', {
    staticClass: "wid70"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Book")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid25"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("MT")])]), _vm._v(" "), _c('td', {
    staticClass: "wid70"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra")]), _vm._v(" "), _c('p', {
    staticClass: "t-left"
  }, [_vm._v("Nehemiah")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid25"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("LXX")])]), _vm._v(" "), _c('td', {
    staticClass: "wid70"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Esdras A (noted as 1 Esdras in this commentary)")]), _vm._v(" "), _c('p', {
    staticClass: "t-left"
  }, [_vm._v("Esdras B (including all of Ezra-Nehemiah and noted as LXX in this commentary)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid25"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Vulg.")])]), _vm._v(" "), _c('td', {
    staticClass: "wid70"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("1 Esdras (Ezra)")]), _vm._v(" "), _c('p', {
    staticClass: "t-left"
  }, [_vm._v("2 Esdras (Nehemiah)")]), _vm._v(" "), _c('p', {
    staticClass: "t-left"
  }, [_vm._v("3 Esdras (1 Esdras)")]), _vm._v(" "), _c('p', {
    staticClass: "t-left"
  }, [_vm._v("4 Esdras (a noncanonical apocalypse in the Protestant Apocrypha)")])])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("Four fragments of Ezra-Nehemiah (Ezra 4:2–6, 9–11; Ezra 5:17; Ezra 6:1–5; Neh 3:14–15) are extant in the Dead Sea Scrolls. Most of them contain text from Ezra and are labeled 4QEzra. Comparison of the traditional text with that of the Scrolls reveals a faithful preservation of the canonical text. As E. Ulrich puts it, “4QEzra again simply demonstrates that the Masoretic "), _c('em', [_vm._v("textus receptus")]), _vm._v(" of each of the books has been very faithfully preserved from one of the plural forms of the text which circulated in the Second Temple period.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-942",
      "id": "footnote-942"
    }
  }, [_c('sup', [_vm._v("28")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The fragment of Nehemiah (4QNeh) has been only recently identified, and although similar to 4QEzra’s handwriting in some ways, it is clearly from a separate scroll."), _c('a', {
    attrs: {
      "href": "#/#foot-note-941",
      "id": "footnote-941"
    }
  }, [_c('sup', [_vm._v("29")])]), _vm._v(" 4QNeh indicates that Malchijah repaired the Dung Gate with the help of his sons (Neh 3:14). The text reflects a proto-Masoretic type but preserves the reading “and his sons” (Neh 3:14), which is found in LXX but not in MT. There is evidence on the scroll, however, that a scribe changed the reading to “he built it” perhaps to bring it into line with the proto-Masoretic tradition. The spelling of these terms in Hebrew could be easily confused and interchanged (see the commentary on Neh 3:14).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra-Nehemiah apparently made an impact on the sectarian writers of the Scrolls. Language from the book surfaces in some sectarian works, especially in the matter of intermarriage. Ezra’s narrow interpretation of the Torah, which rejects all forms of intermarriage as acts of “sacrilege” because of the character of God’s people as “holy seed” (Ezra 9:2), is reflected in several works (e.g., 4QMMT"), _c('sup', [_vm._v("b")]), _vm._v(" 75–82; 4Q418 101 ii 5; 4QALD i 17–18; see T. Levi 9:9–10)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-940",
      "id": "footnote-940"
    }
  }, [_c('sup', [_vm._v("30")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-2"
    }
  }, [_vm._v("II. DATE")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Any good hypothesis regarding the final date of Ezra-Nehemiah hinges on answers to the following questions: (1) the order and date of the memoirs of Ezra and Nehemiah; (2) the date of the final compilation of Ezra-Nehemiah; (3) the relationship of this work to Chronicles; and (4) the relationship of this work to the Pentateuch.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-5"
    }
  }, [_vm._v("A. ORDER")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("One major issue dividing scholars on the dating of Ezra and Nehemiah is the chronological order in which they lived. While there is general agreement that Nehemiah worked under Artaxerxes I, the jury is still out on the placement of Ezra. The text states that he began his ministry during the seventh year of Artaxerxes, but it is unclear if Artaxerxes I or II is intended. If he began to work under Artaxerxes I, his ministry would begin in 458 BCE; if under Artaxerxes II, then it would start in 398 BCE.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The clearest reading of the Scripture without emendation indicates that Ezra and Nehemiah were not only contemporaries but worked together (Neh 8:9; 12:26, 36), thus, a joint career under Artaxerxes I is most likely. Nevertheless, not all scholars agree. Some claim that the compiler placed Ezra in a few places in the text of Nehemiah in order to give the two leaders the same level of honor and that they were not, in fact, contemporaries."), _c('a', {
    attrs: {
      "href": "#/#foot-note-939",
      "id": "footnote-939"
    }
  }, [_c('sup', [_vm._v("31")])]), _vm._v(" Arguments for both sides are compelling and have been published repeatedly; thus, only the most significant ones will be presented below."), _c('a', {
    attrs: {
      "href": "#/#foot-note-938",
      "id": "footnote-938"
    }
  }, [_c('sup', [_vm._v("32")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-parab"
  }, [_vm._v("First, the correlation of Ezra and Nehemiah with the list of high priests recorded in Neh 12:10–12:22 is argued in opposite ways. According to the text, the following priests held office in the order listed below:"), _c('a', {
    attrs: {
      "href": "#/#foot-note-937",
      "id": "footnote-937"
    }
  }, [_c('sup', [_vm._v("33")])])]), _vm._v(" "), _c('table', [_c('tbody', [_c('tr', [_c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("High Priests in Jerusalem")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Governors of Yehud"), _c('a', {
    attrs: {
      "href": "#/#foot-note-936",
      "id": "footnote-936"
    }
  }, [_c('sup', [_vm._v("34")])])])]), _vm._v(" "), _c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Emperors over Persia")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Jeshua/Joshua (533–ca. 500 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Sheshbazzar (ca. 538–? BCE); Zerubbabel (ca. 536?–ca. 515 BCE); Elnathan (late sixth century BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Cyrus the Great (538–530 BCE); Cambyses (529–522 BCE); Darius I (521–486 BCE)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Joiakim (ca. 500–470 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Yehoezer (early fifth century BCE); Ahzai (early fifth century BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Darius I (521–486 BCE); Xerxes/Ahasuerus (485–465 BCE)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Eliashib (ca. 470–433 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Nehemiah (445–432 BCE; 429/8–? BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Artaxerxes I (464–24 BCE)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Joiada (ca. 433–410 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Nehemiah (445–432; 429/8–? BCE); Bagohi/Bagoas (ca. 407 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Artaxerxes I (464–24 BCE); Darius II (423–405 BCE)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Johanan/Jehohanan"), _c('a', {
    attrs: {
      "href": "#/#foot-note-935",
      "id": "footnote-935"
    }
  }, [_c('sup', [_vm._v("35")])]), _vm._v("/Jonathan (Neh 12:11) (ca. 410–370 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Nehemiah (445–432; 429/8–? BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Darius II (423–405 BCE); Artaxerxes II (404–359 BCE)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Jaddua (ca. 370–330 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Hezekiah (330 BCE)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Artaxerxes II (404–359 BCE); Artaxerxes III (358–338 BCE); Arses (337–336 BCE); Darius III (335–331 BCE); Alexander the Great (336–323 BCE)")])])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("The list of priests in Neh 12:22 substitutes Johanan for Jonathan (Neh 12:11). Indeed, the Elephantine papyri (410 BCE) also include a letter addressed to Jehohanan as high priest ("), _c('em', [_vm._v("TAD")]), _vm._v(" 1:68–75). Furthermore, a recently discovered coin from the Persian period gives further evidence of a Johanan who was the high priest in the early fourth century BCE. An inscription on the coin reads, “Yo"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ḥ")]), _vm._v("anan the priest.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-934",
      "id": "footnote-934"
    }
  }, [_c('sup', [_vm._v("36")])]), _vm._v(" Among the priests, only the high priest would have had the authority to issue coins.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Proponents of a late date for Ezra note that he visits the room of “Jehohanan, the son of Eliashib” (Ezra 10:6) and thus argue that Ezra’s ministry occurs in the early fourth century."), _c('a', {
    attrs: {
      "href": "#/#foot-note-933",
      "id": "footnote-933"
    }
  }, [_c('sup', [_vm._v("37")])]), _vm._v(" However, the names “Eliashib” and “Johanan” were popular during this period and do not necessarily indicate a relationship with the high priest. Johanan (also called Jonathan), who served as the high priest from ca. 410 BCE–ca. 370 BCE, was the son of Joiada (Neh 12:11).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Some scholars suggest that the lists of Neh 12 are incomplete. Rather than emending them to agree with each other and trying to cover 200 years with six high priests, Frank Cross notes that papponymy, or the practice of naming a son after his grandfather, was popular and thus that more than one Eliashib and Johanan probably served as high priest during this time."), _c('a', {
    attrs: {
      "href": "#/#foot-note-932",
      "id": "footnote-932"
    }
  }, [_c('sup', [_vm._v("38")])]), _vm._v(" This would account for the different referents in Ezra-Nehemiah bearing the same name but having different fathers. In fact, Josephus refers to five Johanans, some of whom may be the same person. In the same vein, Josephus’s reference to a high priest named Jaddua, who was a contemporary of Alexander the Great, may be to a later Jaddua than the one referred to in Neh 12."), _c('a', {
    attrs: {
      "href": "#/#foot-note-931",
      "id": "footnote-931"
    }
  }, [_c('sup', [_vm._v("39")])]), _vm._v(" This understanding of the list, while possible, is not necessary. The terms of the six high priests, who are installed in the office for life, could have spanned the entire two hundred years."), _c('a', {
    attrs: {
      "href": "#/#foot-note-930",
      "id": "footnote-930"
    }
  }, [_c('sup', [_vm._v("40")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A second issue that is often raised is that if Ezra began his ministry in 458 BCE, there would be a thirteen-year gap before his public reading of the law (Neh 8:1–8). Would this mean that Ezra waited thirteen years before going public with the Torah reading? Proponents of an Ezra who postdates Nehemiah take him out of Neh 8, but this emendation is not necessary either. Ezra could have still been teaching the law during the thirteen-year period before Neh 8. The reading of the law in Neh 8 could have been the climax, not the beginning, of a sustained program of education in the law."), _c('a', {
    attrs: {
      "href": "#/#foot-note-929",
      "id": "footnote-929"
    }
  }, [_c('sup', [_vm._v("41")])]), _vm._v(" Others point out that Ezra may have returned to Mesopotamia during this gap due to his position as a sort of foreign minister for Jewish affairs."), _c('a', {
    attrs: {
      "href": "#/#foot-note-928",
      "id": "footnote-928"
    }
  }, [_c('sup', [_vm._v("42")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Third, the mixed-marriage crisis also raises an issue regarding the chronology. Ezra calls for dissolution of mixed marriages (Ezra 10), but Nehemiah simply calls for doing it right in the future (Neh 13). Some argue that the most logical order is the less severe decree before the more extreme one. According to this reasoning, Ezra’s harsh injunction follows the failure of Nehemiah’s milder approach. Also, one wonders how the children in Nehemiah’s time could be raised by foreign mothers when Ezra had just dissolved mixed marriages, unless his measure simply failed.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nehemiah’s approach to those who had intermarried with foreigners, however, is not gentle. Rather, he becomes physical in his opposition by beating and cursing the offenders (Neh 13:25). It is unknown whether he dissolves the marriages or not. In any case, as the text stands, there is a gap of several years between Ezra’s initial return (458 BCE) and the second tenure of Nehemiah, when he deals with intermarriage (see Neh 13:6, 23–30). The intermarriage problem under Nehemiah was probably at least twenty-six years after Ezra’s decree, in ca. 432 BCE. This calculation allows time for Nehemiah to return to the Persian king for a while and then take on another term of office in Yehud, correcting several other social and religious problems that had occurred in his absence. The matter of intermarriage, often an issue in the Second Temple period, may have simply cropped up again. Nehemiah may have reassessed Ezra’s response as too drastic and not as successful in the long term.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The major difficulty with dating Ezra at 398 BCE is that Ezra would have had no contact with Nehemiah, a contradiction of the text as it stands. Ezra and Nehemiah are mentioned together in Neh 8:9 at the reading of law and in Neh 12:26, 36 at the dedication of the wall. Apparently, they worked together. Since Nehemiah’s name is missing in the 1 Esd 9:49 parallel, some think it is a gloss in Neh 8:9, but that still leaves two references in Neh 12 unaccounted for. The reference to Ezra in Neh 12:36 is clearly not a gloss since deleting Ezra’s name would leave one of the processions without a leader. It is curious that Nehemiah is not mentioned in the book of Ezra, but a similar situation exists in the writings of Haggai and Zechariah. Although the two prophets worked together (Ezra 5:1), neither mentions the other in the separate works attributed to them.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In this commentary, I take the commission of Ezra as predating Nehemiah and adopt the simplest reading of the texts, which presents them as contemporaries with overlapping ministries. It would appear that both worked under Artaxerxes I."), _c('a', {
    attrs: {
      "href": "#/#foot-note-927",
      "id": "footnote-927"
    }
  }, [_c('sup', [_vm._v("43")])]), _vm._v(" As Hoglund points out, the juridical role of Ezra in establishing legal reforms more likely precedes the administrative role of Nehemiah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-926",
      "id": "footnote-926"
    }
  }, [_c('sup', [_vm._v("44")])]), _vm._v(" Ezra brings the law formally to the people and teaches them to observe it in practice. Nehemiah builds upon this effort, assuming the authority of the law as he works to strengthen the community. To argue otherwise requires emendations that prove to be unnecessary. In sum, there are good reasons for placing Nehemiah before Ezra. The majority of scholars continue to support the traditional order."), _c('a', {
    attrs: {
      "href": "#/#foot-note-925",
      "id": "footnote-925"
    }
  }, [_c('sup', [_vm._v("45")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-6"
    }
  }, [_vm._v("B. EZRA-NEHEMIAH AND CHRONICLES")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("As to the relationship between Ezra-Nehemiah and Chronicles, scholars are again divided. Some see Ezra-Nehemiah as part of a larger work including Chronicles, while others regard Ezra-Nehemiah as a separate compilation, and still others regard all three as separate books with different authors."), _c('a', {
    attrs: {
      "href": "#/#foot-note-924",
      "id": "footnote-924"
    }
  }, [_c('sup', [_vm._v("46")])]), _vm._v(" In the literature regarding authorship of Ezra-Nehemiah, many of the arguments focus on language and theme."), _c('a', {
    attrs: {
      "href": "#/#foot-note-923",
      "id": "footnote-923"
    }
  }, [_c('sup', [_vm._v("47")])]), _vm._v(" Language experts have made lists of parallel and dissimilar vocabulary between the books, and literary scholars have tried to uncover underlying themes that connect the books or to argue that the agenda of each book is too different for single authorship.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Following the Talmud (b. B. Bat. 15a), Albright and Bright argued that Ezra was the Chronicler, but few scholars today follow this view."), _c('a', {
    attrs: {
      "href": "#/#foot-note-922",
      "id": "footnote-922"
    }
  }, [_c('sup', [_vm._v("48")])]), _vm._v(" Arguments supporting this notion include (1) the common verses at the end of 2 Chronicles and the beginning of Ezra; (2) the common words (e.g"), _c('em', [_vm._v(".")]), _vm._v(", “Netinim,” “singer,” “gatekeeper,” “father’s houses” (for families), and “house of God,” which is Late Hebrew language); and (3) the common themes (e.g., emphasis on the temple with a similar description of its music, sacrifices, sacred vessels, Levitical personnel, and religious festivals; also, endowment is by ancestral house rather than just the royal house as in Kings). Joseph Blenkinsopp explains that the “author” may stand for “a plurality or school rather than an individual, that is, a group sharing the same concerns, convictions, and idioms, perhaps active over several generations.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-921",
      "id": "footnote-921"
    }
  }, [_c('sup', [_vm._v("49")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The purpose of the supposed compilation of Chronicles-Ezra-Nehemiah has been discussed as well. According to Jacob Myers, “The purpose of the Chronicler was to present the history of Israel (Judah) from a cultic point of view, showing how the nation prospered when the cultus was maintained in purity and vitality and how it fared when that was not the case.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-920",
      "id": "footnote-920"
    }
  }, [_c('sup', [_vm._v("50")])]), _vm._v(" Indeed, the cult, with its emphasis on the priesthood, Levites, ritual, holiness, and worship, plays a strong role in both Ezra-Nehemiah and Chronicles.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nevertheless, the view that Chronicles and Ezra-Nehemiah share the same author or editor has been strongly contested in recent years, especially by S. Japhet and H. G. M. Williamson."), _c('a', {
    attrs: {
      "href": "#/#foot-note-919",
      "id": "footnote-919"
    }
  }, [_c('sup', [_vm._v("51")])]), _vm._v(" Japhet points out that many words used in Ezra-Nehemiah are not found in Chronicles and that some that are in both works are used differently."), _c('a', {
    attrs: {
      "href": "#/#foot-note-918",
      "id": "footnote-918"
    }
  }, [_c('sup', [_vm._v("52")])]), _vm._v(" Theophoric names are used in short form in Ezra-Nehemiah (e.g., Joram) but both short and long forms in Chronicles (e.g., Joram/Jehoram). The separatist theology so obvious in Ezra-Nehemiah is absent in Chronicles, which has an international flavor with a much more open attitude toward the Northern tribes."), _c('a', {
    attrs: {
      "href": "#/#foot-note-917",
      "id": "footnote-917"
    }
  }, [_c('sup', [_vm._v("53")])]), _vm._v(" While the mixed-marriage issue is life-threatening in Ezra-Nehemiah, Chronicles downplays its effect, mentioning only one of Solomon’s foreign marriages (2 Chr 8:11). This stands in contrast to the accusation of 1 Kings that Solomon’s efforts to please his pagan wives brought about Solomon’s own apostasy (1 Kgs 11:1–13); Nehemiah repeats the view of Kings (Neh 13:26). Prophecy is more emphasized in Chronicles than in Ezra-Nehemiah, although it is present in both works. The Sabbath is important to Nehemiah but noticeably absent in Chronicles (see 2 Chr 36:11–16). Furthermore, the prominent emphasis on David and his royal dynasty in Chronicles is absent in Ezra-Nehemiah and, conversely, the prominent exodus traditions in Ezra-Nehemiah are lacking in Chronicles. Finally, the Chronicler’s sense of immediate retribution is lacking in Ezra-Nehemiah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-916",
      "id": "footnote-916"
    }
  }, [_c('sup', [_vm._v("54")])]), _vm._v(" Overall, the disparity of vision between the two works is too great for them to derive from the same author.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The book of Chronicles was probably penned later than Ezra-Nehemiah, following the order of the Hebrew canon. Steinmann argues on orthographic grounds that 2 Chr 36:22–23 is copied from Ezra 1:1–3."), _c('a', {
    attrs: {
      "href": "#/#foot-note-915",
      "id": "footnote-915"
    }
  }, [_c('sup', [_vm._v("55")])]), _vm._v(" This would place the redaction of Chronicles after the Prologue of Ezra, which, according to many scholars, was the last section to be included in the book of Ezra-Nehemiah (see "), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#sec3-3"
    }
  }, [_vm._v("“Prologue,” pp. 7–9")]), _vm._v(").")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-7"
    }
  }, [_vm._v("C. SEPARATE AUTHORSHIP THEORY")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("A current trend on the thorny subject of authorship is simply to separate all three books, giving each its own author."), _c('a', {
    attrs: {
      "href": "#/#foot-note-914",
      "id": "footnote-914"
    }
  }, [_c('sup', [_vm._v("56")])]), _vm._v(" J. VanderKam offers a list of linguistic differences between Ezra and Nehemiah and posits a different perspective for each, such as is found in the four Gospels. Moreover, David Kraemer points out ideological differences between the two books. His main thesis is that “the book of Ezra is a priestly book; its concerns are the Temple, the priesthood and Levites, and purity—that is, the cult. The book of Nehemiah, in contrast, is a lay book, sometimes exhibiting antagonism to priestly concerns and supporting, instead, what might be called scribal values.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-913",
      "id": "footnote-913"
    }
  }, [_c('sup', [_vm._v("57")])]), _vm._v(" He also notes different motifs. Ezra is more concerned with the temple-building project and Nehemiah with the wall; Ezra emphasizes the holiness of lineage, and Nehemiah enacts Torah observance. According to Kraemer, in Ezra, governors and scribes serve priestly purposes; in Nehemiah, priests serve the governors and scribes."), _c('a', {
    attrs: {
      "href": "#/#foot-note-912",
      "id": "footnote-912"
    }
  }, [_c('sup', [_vm._v("58")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Other features of the separate authorship view include: (1) Nehemiah’s introduction, which suggests an independent composition;"), _c('a', {
    attrs: {
      "href": "#/#foot-note-911",
      "id": "footnote-911"
    }
  }, [_c('sup', [_vm._v("59")])]), _vm._v(" (2) the identical lists of returnees that appear in Ezra 2 and Neh 7 (this argument can go both ways); (3) that it is easier to explain the absence of Ezra in Neh 1–7 if one does not attach Ezra 1–10 to Nehemiah (this argument, too, can be reversed, since Ezra’s presence in Neh 8 is better understood with Ezra 1–10 attached); (4) stylistic differences such as Ezra, but not Nehemiah, including Aramaic and the Ezra Memoir being primarily written in first person, whereas Ezra material in Neh 8 is presented in third person; and (5) the opinion of some (at least by Origen’s time) that they should be two books.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nevertheless, Jewish tradition supports the notion that Ezra and Nehemiah were originally one work separate from Chronicles. The earliest roster of the Hebrew Bible composed by Melito of Sardis in the second century CE regards Ezra and Nehemiah as one unit, as does LXX."), _c('a', {
    attrs: {
      "href": "#/#foot-note-910",
      "id": "footnote-910"
    }
  }, [_c('sup', [_vm._v("60")])]), _vm._v(" The Masoretes give their traditional paragraph count only at the end of Nehemiah, and it includes both Ezra and Nehemiah; their midpoint is at Neh 3:32, not at the end of Ezra. Josephus ("), _c('em', [_vm._v("Against Apion")]), _vm._v(" 1.40) and the Talmud (b. B. Bat. 15a) refer only to one work (i.e., Ezra). L. S. Fried expresses the implausibility of one of these books without the other: “Moreover, it is inconceivable that a story of Ezra would have been complete without an account of Ezra reading the law to the gathered populace. That story is told only in the book of Nehemiah. Nor could the story of Ezra’s reading the Torah have been told in the book of Nehemiah without an account of Ezra’s coming to Jerusalem. That story is not in Nehemiah, but only in the book of Ezra.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-909",
      "id": "footnote-909"
    }
  }, [_c('sup', [_vm._v("61")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Thus, each book is needed in order to understand the other. Ezra and Nehemiah were originally simply “Ezra” in the earliest Hebrew manuscripts from the tenth century. Origen distinguishes between 1 Ezra and 2 Ezra but says that in Hebrew they are one book. Jerome, too, separates Nehemiah, “the liber secundus Esdrae,” from Ezra. Thus, the division apparently begins with the church fathers.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In a monograph on Levitical authorship of both Ezra and Nehemiah, Kyung-Jin Min skillfully argues against the separate books view."), _c('a', {
    attrs: {
      "href": "#/#foot-note-908",
      "id": "footnote-908"
    }
  }, [_c('sup', [_vm._v("62")])]), _vm._v(" He points out that, as it stands, part of the story is in one book and part in the other. The introduction in Neh 1:1 is paralleled by subheadings in the Psalms, and in fact the second half of 1:1 is a defective date with no mention of the king, which must be assumed from Ezra. Also, the heading indicates the following first-person material will now be given by Nehemiah, not Ezra. Min endorses two scholarly views for the repetition of the lists. First, “repetition of the lists was devised to show the continuity between the community which first returned and built the temple (Ezra 2) and the purified community (Nehemiah 7).” Second, the lists provide authorization for those who would rebuild the temple (Ezra 2) as well as supply a genealogical register for those who would take up residence in Jerusalem and other cities of Judah (Neh 7). Against VanderKam’s different word lists, Min notes that the two books share technical terms too—for example, “Temple Servants” or “Netinim” ("), _c('em', [_vm._v("n")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("tînîm")]), _vm._v(") and “the hand of God upon” ("), _c('em', [_vm._v("yad-’elōhîm ‘āl")]), _vm._v(") a person; both are rarely found elsewhere in the Hebrew Bible."), _c('a', {
    attrs: {
      "href": "#/#foot-note-907",
      "id": "footnote-907"
    }
  }, [_c('sup', [_vm._v("63")])]), _vm._v(" Also, while the house of God is a less-emphasized "), _c('em', [_vm._v("fait accompli")]), _vm._v(" in Nehemiah, the theme of the restoration of the community is shared by both books.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Other scholars, too, have argued for the connection of Ezra and Nehemiah in one compilation based on narrative integrity."), _c('a', {
    attrs: {
      "href": "#/#foot-note-906",
      "id": "footnote-906"
    }
  }, [_c('sup', [_vm._v("64")])]), _vm._v(" The inclusion of an Ezra story within Nehemiah, by its assumption that the reader is familiar with Ezra, supports the idea that Nehemiah and Ezra are a unitary compilation. Eskenazi uncovers underlying themes throughout the two books. Throntveit’s work on literary devices (“concentricity, parallel panels, and repetitive resumptions”) has also further demonstrated the link between the two books (see "), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#sec3-5"
    }
  }, [_vm._v("“Literary Techniques,” pp. 29–30")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The two books use parallel collocations as well. For instance, the use of “the hand of Yahweh my God was upon me” (Ezra 7:28) parallels “the good hand of my God that was upon me” (Neh 2:8). Other hand imagery in both books includes stretching out the hand in force against someone ("), _c('em', [_vm._v("dî yiśla")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _vm._v(" "), _c('em', [_vm._v("y")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("dēh")]), _vm._v(", “will stretch out his hand,” Ezra 6:12; "), _c('em', [_vm._v("yād")]), _vm._v(" "), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("ešla")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _vm._v(" "), _c('em', [_vm._v("bākem")]), _vm._v(", “I will stretch out a hand against you,” Neh 13:21) and efforts to “strengthen hands” or “weaken” them (i.e., encourage or discourage; Ezra 4:4; 6:22; Neh 6:9).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In addition to the above arguments for the compilation of Ezra-Nehemiah without Chronicles, the priestly material of the Bible uses technical language that conveys specific meanings in the context of the Israelite cult. In Ezra-Nehemiah, this vocabulary is used quite differently than in earlier Jewish traditions, and this distinct usage is shared between both books."), _c('a', {
    attrs: {
      "href": "#/#foot-note-905",
      "id": "footnote-905"
    }
  }, [_c('sup', [_vm._v("65")])]), _vm._v(" One concept shared by both but otherwise unique in the Hebrew Bible is the sacrilege of intermarriage (see Ezra 9:2; 10:2; Neh 13:27; see "), _c('span', {
    attrs: {
      "href": "#/012_Chapter02c#exc9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 9, “The Sacrilege of Intermarriage,” pp. 244–54")]), _vm._v("). The root "), _c('em', [_vm._v("qdš")]), _vm._v(", “to be holy,” is used in an expansive way. Ezra represents the holiness of the entire community of returned exiles, not just the priests, as biological by the reference to "), _c('em', [_vm._v("the holy seed")]), _vm._v(" ("), _c('em', [_vm._v("zera")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _vm._v(" "), _c('em', [_vm._v("haqqōdeš")]), _vm._v(", Ezra 9:2), and Nehemiah corroborates this outlook (Neh 9:2). Like the holiness of the priests, the “holy seed” can be desecrated ("), _c('em', [_vm._v("ma")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("al")]), _vm._v("; Ezra 9:2; 10:2, 10; Neh 1:8; 13:27), an innovation over against earlier norms. Ezra records the dimensions of the temple as larger than Solomon’s (Ezra 6:3). Over the course of the two books, the holiness of the temple reaches out to encompass the entire city of Jerusalem, requiring purification of its walls (Neh 7:1; 13:22). The people find themselves “in the house of God,” although they are merely within the walls of the city, not the sanctuary itself (Neh 12:40). More striking than earlier traditions, the label of impurity is applied directly to outsiders. Unlike the purity system of Lev 11–15, separating ("), _c('em', [_vm._v("bādal")]), _vm._v(") from impurity in both Ezra and Nehemiah usually means disassociating from “foreigners” or “people of the land(s)” (Ezra 6:21; 9:11; 10:11; Neh 10:28; 13:26, 30). Furthermore, the term "), _c('em', [_vm._v("gā")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("al")]), _vm._v(" (“nauseating, revolting, defiling”) describes the defilement of the priests by intermarriage (Ezra 2:61–62; Neh 7:64; 13:29).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-8"
    }
  }, [_vm._v("D. COMPILATION")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The date of the final compilation of Ezra-Nehemiah continues to trouble scholars. The matter revolves, to a great extent, around the genealogical lists of Neh 12. Jaddua of Neh 12:10–11 may be the same Jaddua, known from Josephus, who came into contact with Alexander the Great. Further support for a Hellenistic context may come from the word used for coins in Ezra 2:69; 8:27 and Neh 7:70–72, which may refer to the Greek drachma."), _c('a', {
    attrs: {
      "href": "#/#foot-note-904",
      "id": "footnote-904"
    }
  }, [_c('sup', [_vm._v("66")])]), _vm._v(" From a philosophical perspective, the tone of Ezra-Nehemiah ends with greater skepticisim than the earlier optimism under Cyrus and Darius and may reflect a Hellenistic rather than a Persian context."), _c('a', {
    attrs: {
      "href": "#/#foot-note-903",
      "id": "footnote-903"
    }
  }, [_c('sup', [_vm._v("67")])]), _vm._v(" On the other hand, there is no mention in Ezra-Nehemiah of Alexander’s conquest or even the suffering of the mid-fourth century BCE, when Judah joined the Phoenician rebellion and was harshly put down by Artaxerxes III."), _c('a', {
    attrs: {
      "href": "#/#foot-note-902",
      "id": "footnote-902"
    }
  }, [_c('sup', [_vm._v("68")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The redaction and final compilation of Ezra-Nehemiah remains a matter of debate among scholars. According to some, large identifiable sources were compiled at different times during the fifth to fourth centuries BCE; others regard the process as incremental, arguing that there was a continual process of editing as scribes responded to various issues of the community."), _c('a', {
    attrs: {
      "href": "#/#foot-note-901",
      "id": "footnote-901"
    }
  }, [_c('sup', [_vm._v("69")])]), _vm._v(" In my view, an early editor combined material regarding Ezra and Nehemiah (of whom Nehemiah lived later) no earlier than 430 BCE and no later than the death of Artaxerxes in 424 BCE. One example of an early setting for this material is its emphasis on Judah and Benjamin, a postexilic rather than a Hellenistic issue."), _c('a', {
    attrs: {
      "href": "#/#foot-note-900",
      "id": "footnote-900"
    }
  }, [_c('sup', [_vm._v("70")])]), _vm._v(" Nevertheless, later editing is also discernible (e.g., Neh 12:10). The lists of the high priests of Neh 12 discussed above reveals that the book’s final compilation could not have been before “Darius the Persian”—most likely Darius III (335–331 BCE).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The motive for this compilation revolves around matters of identity and ethnicity. In the Persian period, and even more so in the Hellenistic period, Jews were increasingly concerned with legitimacy and continuity. “Who is a Jew?” becomes a key question, and Ezra-Nehemiah provides the earliest narrative dealing with the matter. Ezra and Nehemiah are heroes who maintain an ethnically pure Jewish community that is legitimately linked to the Israelite past, attestable by genealogy, and guided by the Torah in the present despite persecution and the dominance of a foreign regime.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-3"
    }
  }, [_vm._v("III. METHODOLOGY")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The increasing frustration with a diachronic approach to Ezra-Nehemiah has led many scholars to examine other methodologies. The diachronic method views the writing of Ezra-Nehemiah as a process with many stages of adding or editing literary sources. This long-standing method is helpful in that it often provides an answer for internal inconsistencies by pointing to various sources, text layers, and editing. However, the disadvantage of the diachronic method is that emendations and large rearrangements of the text are often subjective, as the scholar tries to create a smooth, chronological narrative from a modern point of view.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-4"
    }
  }, [_vm._v("IV. SETTING")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The narrative of Ezra-Nehemiah takes place primarily within the Persian province of Yehud (= Judah, Judea). The geographical boundaries and population of this province, especially that of its central city Jerusalem, continue to be a matter of debate among scholars. According to O. Lipschits, Yehud lost about 60 percent of its population in the Babylonian deportations of the sixth century BCE. Compared with about 110,000 before the Babylonian conquest, Yehud in the time of Zerubbabel contained only about 60,000 persons."), _c('a', {
    attrs: {
      "href": "#/#foot-note-899",
      "id": "footnote-899"
    }
  }, [_c('sup', [_vm._v("71")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-9"
    }
  }, [_vm._v("A. YEHUD")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The Persians organized Yehud as part of a huge administrative district, or satrapy, that included Babylon and all of the Near East west of the Euphrates River. The satrapy was given the name, "), _c('em', [_vm._v("Babili-Ebirnari")]), _vm._v(", “Babylon and Across the River” (i.e., the River Euphrates; see Ezra 8:36; Neh 2:7, 9; 3:7). Herodotus describes it as “the Fifth Satrapy,” which included the nations of Syria, Phoenicia, Cyprus, and Israel to the border of Egypt ("), _c('em', [_vm._v("Histories")]), _vm._v(" 1.114). According to Ezra-Nehemiah, Judah was surrounded by hostile enemies: Samaria to the north, governed by Sanballat; Ammon-Gilead on the east, governed by Tobiah; and Arabia-Idumea in the south, where Geshem was in control. To the west lay the province of Ashdod."), _c('a', {
    attrs: {
      "href": "#/#foot-note-898",
      "id": "footnote-898"
    }
  }, [_c('sup', [_vm._v("72")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Yehud was much smaller than the Judah of the Babylonian period, encompassing approximately 800 square miles. Its boundaries, according to the biblical rosters of place names, are confirmed to a large degree by the presence of (1) forts on the southern and western boundaries, and (2) coins and seal impressions stamped with the name “Yehud.” E. Stern states the boundaries as follows: “The southern border of this area of distribution would appear to be at Beth-Zur; the northern limit is at Tell en-Nasbeh; the eastern limit is a line extending from Jericho to En-Gedi; in the west, some seal impressions were recovered at Gezer, which is definitely located in the district of Lod, and at Tel Harasim, which belonged to the Keilah district. Thus, the distribution of coins and impressions of the Judaean province encompasses the entire area mentioned in the Bible.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-897",
      "id": "footnote-897"
    }
  }, [_c('sup', [_vm._v("73")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The expanded version of Yehud’s boundaries has been contested. O. Lipschits estimates a somewhat smaller area of settlement in Persian Yehud, especially on the western extent."), _c('a', {
    attrs: {
      "href": "#/#foot-note-896",
      "id": "footnote-896"
    }
  }, [_c('sup', [_vm._v("74")])]), _vm._v(" Admittedly, archaeological remains do not establish clear boundaries of the province of Yehud but simply provide isolated pieces of evidence."), _c('a', {
    attrs: {
      "href": "#/#foot-note-895",
      "id": "footnote-895"
    }
  }, [_c('sup', [_vm._v("75")])]), _vm._v(" (See the "), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#m1"
    }
  }, [_vm._v("map on p. 96")]), _vm._v(".)")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Discussion continues regarding the status of Yehud as a province. A. Alt believed that Yehud was under the rulership of the province of Samaria and that the establishment of Yehud as an independent province was a new idea which began with the appointment of Nehemiah as governor."), _c('a', {
    attrs: {
      "href": "#/#foot-note-894",
      "id": "footnote-894"
    }
  }, [_c('sup', [_vm._v("76")])]), _vm._v(" This would explain the hostility of the Samarians during this time. However, Scripture refers to both Sheshbazzar (Ezra 5:14) and Zerubbabel (Hag 1:1, 14; 2:2, 21) as “governors” and Yehud as some kind of independent province ("), _c('em', [_vm._v("m")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("dînâ")]), _vm._v("; see Esth 1:1)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-893",
      "id": "footnote-893"
    }
  }, [_c('sup', [_vm._v("77")])]), _vm._v(" In addition, over seventy bullae (clay seal impressions) were found in a jar near Jerusalem made from twelve seals, with the name of the province Yehud. Several of them contain the name of a Jew who was designated “governor.” Also, two seals, possibly from the sixth century BCE, were discovered, one of which belonged to “Shelomith, maidservant of Elnathan the Governor.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-892",
      "id": "footnote-892"
    }
  }, [_c('sup', [_vm._v("78")])]), _vm._v(" Nehemiah also points to governors prior to him that, unlike himself, took the “governor’s allowance of food,” and “laid a heavy burden on the people” (Neh 5:14–15). Thus, it appears Yehud was an independent province as early as the late sixth century BCE.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-10"
    }
  }, [_vm._v("B. JERUSALEM")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("There was a large difference in the impact of the deportations on residents of the hill country and of Jerusalem. While large estates in the rural areas were relatively untouched, Jerusalem and its environs were devastated. Nehemiah describes such ruin that his animal can barely find footing while he investigates the site (Neh 2:14). It was his vision and self-imposed mission to repopulate and secure the city with a defensive wall.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Scripture gives some hint as to the extent of Jerusalem in its description of the city’s repopulation under Nehemiah’s administration (Neh 11:1–24). One out of ten Judean settlers volunteer to move into its borders. Steinmann, pointing out that the total of those associated with the city’s government in Neh 11 comes to 3,044, estimates a total population of between 10,000 and 15,000 persons, which allows for the families of these men and for others who were not leaders."), _c('a', {
    attrs: {
      "href": "#/#foot-note-891",
      "id": "footnote-891"
    }
  }, [_c('sup', [_vm._v("79")])]), _vm._v(" On the other hand, Broshi, on the basis of the area of Jerusalem being 120 dunams, estimates its population at 40 persons a dunam to a total of about 4,800 people in Jerusalem in Nehemiah’s time."), _c('a', {
    attrs: {
      "href": "#/#foot-note-890",
      "id": "footnote-890"
    }
  }, [_c('sup', [_vm._v("80")])]), _vm._v(" In either case, the population of Jerusalem is drastically less than during the time of the first return. According to the earlier lists of Ezra-Nehemiah, the number of men alone whose ancestry is associated with Jerusalem in Zerubbabel’s day was around 16,000 (Ezra 2:3–20; Neh 7:8–25). (See the image of "), _c('a', {
    attrs: {
      "href": "#/#fig1"
    }
  }, [_vm._v("Nehemiah’s Jerusalem on p. 97")]), _vm._v(".)")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("A large factor in determining the population of Jerusalem is the extent of its area in early Persian times. A few scholars place the wall in the late Hellenistic period and identify it with the Hasmonean city wall. However, this view runs into difficulty because the various toponyms described in the text (e.g., the temple with an adjacent royal palace, the upper king’s house, the courtyard of the guard, the Horse Gate) reflect the layout of the city in early Second Temple, not Hasmonean, times (see Neh 3:25–28)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-889",
      "id": "footnote-889"
    }
  }, [_c('sup', [_vm._v("81")])]), _vm._v(" According to the “minimalists,” who are the majority of scholars, the area of Jerusalem in the Persian period was limited to the eastern hill."), _c('a', {
    attrs: {
      "href": "#/#foot-note-888",
      "id": "footnote-888"
    }
  }, [_c('sup', [_vm._v("82")])]), _vm._v(" They note that excavations of the Jewish Quarter on the western hill reveal Hellenistic remains directly on top of First Temple finds. Most buildings in this period were constructed in an open-court style—that is, as a courtyard with rooms surrounding it on all or most sides, a typical design for the Assyrian, Babylonian, and Persian periods."), _c('a', {
    attrs: {
      "href": "#/#foot-note-887",
      "id": "footnote-887"
    }
  }, [_c('sup', [_vm._v("83")])]), _vm._v(" Along with the lack of archaeological finds from the Persian period on the southwestern hill, the description of the wall building also supports the minimalist position. The list of task forces is roughly equal on both sides of the wall, divided on a north/south axis from the Sheep Gate in the north to the Dung Gate in the south, with nineteen crews on the west side of the city and twenty-two crews on the east side. Many more crews would have been needed on the west side if it included the southwestern hill."), _c('a', {
    attrs: {
      "href": "#/#foot-note-886",
      "id": "footnote-886"
    }
  }, [_c('sup', [_vm._v("84")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The matter of Jerusalem’s physical size, however, is not a closed case. The scholars referred to as “maximalists” argue that the borders of the preexilic city included not only the eastern hill with its Temple Mount and City of David but the southwestern hill as well."), _c('a', {
    attrs: {
      "href": "#/#foot-note-885",
      "id": "footnote-885"
    }
  }, [_c('sup', [_vm._v("85")])]), _vm._v(" Proponents of this view point out that there is not sufficient evidence for a wall on the western side of the City of David dating to Nehemiah’s period. David Ussishkin explains that there is evidence in the southern section of the western boundary of the City of David that it was fortified in the Middle Bronze II period but that it was not fortified at all in later times."), _c('a', {
    attrs: {
      "href": "#/#foot-note-884",
      "id": "footnote-884"
    }
  }, [_c('sup', [_vm._v("86")])]), _vm._v(" Rather, the preexilic size of Jerusalem, from the eighth century forward, included the southwestern hill. He also points to the large number of gates in the wall described in Nehemiah, which makes better sense for a larger perimeter. Also, the biblical description of the site before Jerusalem’s repopulation seems to fit better with a settlement larger than the eastern hill, “The city was wide and spacious but the people in it were few and the houses had not been rebuilt” (Neh 7:4). According to Nehemiah 11, one-tenth of the Jewish population moved inside the city walls (Neh 11:1–2).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The crux remains that archaeologists have not uncovered clear evidence of Jewish settlement on the southwestern hill during the Persian period. Whether or not Nehemiah’s wall extended to the western hill, the population of the city was still concentrated on the eastern hill. The city, even at the minimalist size, could have easily accommodated one to two thousand people."), _c('a', {
    attrs: {
      "href": "#/#foot-note-883",
      "id": "footnote-883"
    }
  }, [_c('sup', [_vm._v("87")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-11"
    }
  }, [_vm._v("C. LITERARY CRITICISM")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Literary criticism goes beyond historical concerns and begs for the rationale behind the work as a whole. It asks why the book exists in its present form. The work is not just the next stop on a train of sources that were compiled and edited at different moments in history. Instead, like scholars of other literatures, literary critics take note of literary conventions in the text, seeking undergirding themes as well as overarching frameworks and plots. They are also sensitive to symbols and their meanings, which highlight key values of these early Jewish communities."), _c('a', {
    attrs: {
      "href": "#/#foot-note-882",
      "id": "footnote-882"
    }
  }, [_c('sup', [_vm._v("88")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-4"
    }
  }, [_vm._v("1. Themes and Purpose")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Literary critics differ regarding what the major themes are in Ezra-Nehemiah and what the overall purpose of the book is. Eskenazi, in a ground-breaking monograph, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", concluded that the three major parts of Ezra-Nehemiah were intentionally composed and sequenced to make a point: the extension of holiness in Israel from the establishment of the holy house to the sanctification of the entire city."), _c('a', {
    attrs: {
      "href": "#/#foot-note-881",
      "id": "footnote-881"
    }
  }, [_c('sup', [_vm._v("89")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Sara Japhet, too, urges that chronology in Ezra-Nehemiah is tied to composition. According to Japhet, the author is making a historical statement by sequencing key events in a particular way. In each of the three major events of the book, the restoration begins with Persian authorization and continues with a group of returnees from exile. Japhet states: “By the means of this time-structuring, the author expresses his historical view—which he also expresses in other ways—that change and renewal in the life of Judah were the result of initiative on the part of the Persian kings and the Jews of Babylonia rather than any action in Judah itself, whether political or spiritual. God extended grace to Israel—that is to those who returned from exile—by means of the kings of Persia.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-880",
      "id": "footnote-880"
    }
  }, [_c('sup', [_vm._v("90")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Throntveit suggests a paradigm that focuses on the “crucial theological moments of the restoration as three parallel returns”—those led by Zerubbabel, Ezra, and Nehemiah, each of whom fulfilled his own mission: the reconstruction of the temple, community, and walls. In each case, there was a return from exile prompted by God and authorized by the Persian emperor, opposition from the local authorities, and a meeting of the challenge with God’s help."), _c('a', {
    attrs: {
      "href": "#/#foot-note-879",
      "id": "footnote-879"
    }
  }, [_c('sup', [_vm._v("91")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Shemaryahu Talmon notes that all three narrative blocks display a similar structure and, with one exception, are composed of the same types of subunits: (1) royal documents worded in Aramaic or in Hebrew (Ezra only); (2) letters in a common epistolary style, written in Aramaic by Persian officials to the king and in Hebrew between Nehemiah and Sanballat; (3) diverse lists of returnees, inhabitants, builders, and signatories, as well as inventories."), _c('a', {
    attrs: {
      "href": "#/#foot-note-878",
      "id": "footnote-878"
    }
  }, [_c('sup', [_vm._v("92")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Several scholars have noticed the recollection of the exodus narrative in the language and themes of Ezra-Nehemiah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-877",
      "id": "footnote-877"
    }
  }, [_c('sup', [_vm._v("93")])]), _vm._v(" The writer views the return from exile as a second exodus from slavery. The giving of the law and the journey to the land of promise are central in both stories, and Ezra’s part in these endeavors puts him forward as a sort of second Moses. Blenkinsopp refers to this use of Israelite history in a contemporary way as a trend in the “second historical corpus” (Chronicles-Ezra-Nehemiah) of “gathering up of the past and reshaping it in such a way as to allow for a future.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-876",
      "id": "footnote-876"
    }
  }, [_c('sup', [_vm._v("94")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("There are other suggestions for the text’s main themes and purpose. Some scholars regard Ezra-Nehemiah as establishing a line of continuity with the First Temple period, its royalty, and its priesthood. Others view its purpose as a corrective to overly liberal, inclusive prophets like Isaiah, instead promoting a hardline approach toward outsiders, especially with regard to the cult. According to Lester Grabbe, one of the book’s main messages is that “eternal vigilance is required to keep outsiders from leading the community astray,” and he notes the unsettling ending of the book, which “leaves in the mind of the reader the open-ended danger that remains against which one must be constantly vigilant.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-875",
      "id": "footnote-875"
    }
  }, [_c('sup', [_vm._v("95")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-5"
    }
  }, [_vm._v("2. Literary Techniques")]), _vm._v(" "), _c('p', {
    staticClass: "parab"
  }, [_vm._v("Various literary conventions are employed in Ezra-Nehemiah that delineate the structure of the text. Two of the most obvious of these are concentricity and resumptive repetition. Concentricity, or chiastic structure—sometimes referred to as staircase parallelism or ring composition—refers to the arrangement of a section of the text (whether a verse or a group of chapters) in which “the members of the second half of a bicolon, sentence, or other literary unit echo, repeat, or recall the members of the first half in inverted order, in the form AB … B′A′.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-874",
      "id": "footnote-874"
    }
  }, [_c('sup', [_vm._v("96")])]), _vm._v(" As an example, Ezra 9:3–10:1 records in a chiastic structure Ezra’s intercessory prayer for the community that has intermarried with outsiders:")]), _vm._v(" "), _c('p', {
    staticClass: "chiasm"
  }, [_vm._v("A Narrative action: Ezra’s mourning (9:3–5)")]), _vm._v(" "), _c('p', {
    staticClass: "chiasm1"
  }, [_vm._v("B General confession (9:6–7)")]), _vm._v(" "), _c('p', {
    staticClass: "chiasm2"
  }, [_vm._v("C Present evidence of divine mercy (9:8–9)")]), _vm._v(" "), _c('p', {
    staticClass: "chiasm3"
  }, [_vm._v("X Specific confession (9:10–12)")]), _vm._v(" "), _c('p', {
    staticClass: "chiasm2"
  }, [_vm._v("C′ Questioned continuance of divine mercy (9:13–14)")]), _vm._v(" "), _c('p', {
    staticClass: "chiasm1"
  }, [_vm._v("B′ General confession (9:15)")]), _vm._v(" "), _c('p', {
    staticClass: "chiasm"
  }, [_vm._v("A′ Narrative action: Ezra’s mourning (10:1a)"), _c('a', {
    attrs: {
      "href": "#/#foot-note-873",
      "id": "footnote-873"
    }
  }, [_c('sup', [_vm._v("97")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("The most significant part of the confession, that the community has violated divine laws against intermarriage, appears at the center of the passage (X). This is framed by the supporting parts of Ezra’s prayer: his affirmation of divine mercy both currently (C, C′) and historically (B, B′). Further out (A, A′), the unit begins and ends by setting forth the narrative details of the context: the mourning posture of Ezra and those who gathered around him.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Noss and Thomas focus on the phenomenon of concentricity on a macro scale, which they refer to as “expanded chiasmus” or “mirror image” in which “one set of paragraphs or lines builds up toward a climax from which a second set of paragraphs or lines moves away in opposite fashion (see Neh 3:3–32).”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-872",
      "id": "footnote-872"
    }
  }, [_c('sup', [_vm._v("98")])]), _vm._v(" Often the main point of the passage is highlighted by its placement at the center of one of these chiasms. For Throntveit too, concentricity is the “primary structural device employed in Ezra-Nehemiah, appearing at every level of the text.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-871",
      "id": "footnote-871"
    }
  }, [_c('sup', [_vm._v("99")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The presence of resumptive repetition and summaries in Ezra-Nehemiah was first brought into relief by Shemaryahu Talmon."), _c('a', {
    attrs: {
      "href": "#/#foot-note-870",
      "id": "footnote-870"
    }
  }, [_c('sup', [_vm._v("100")])]), _vm._v(" Resumptive repetition is a technique by which an earlier phrase is repeated or continued after the interruption of other material. A good example is Ezra 2:1b (= Neh 7:6b): "), _c('em', [_vm._v("These are the people of the province who came up from the captivity")]), _vm._v(" … "), _c('em', [_vm._v("[and] returned to Jerusalem and Judah, each to his own city")]), _vm._v(". Here it breaks off for the list of people but summarizes and resumes at Ezra 2:70 (= Neh 7:73) with "), _c('em', [_vm._v("So the priests, Levites, some of the people")]), _vm._v(" … "), _c('em', [_vm._v("settled in their cities.")]), _vm._v("” More pointedly, Ezra 4:6–24a is framed by 4:5b (“until the reign of King Darius of Persia”) and 4:24b (“until the second year of the reign of King Darius of Persia”). This framing of material by a similar phrase at its beginning and end is also called an inclusio or envelope. Thus, rather than insisting that Ezra-Nehemiah is wrong historically in introducing Artaxerxes in the time of Cyrus and Darius, the readers realize that by describing local opposition to the Jews under Darius, the author reminds them of a more recent, similar experience in more recent times under Artaxerxes. The theme of overcoming opposition by local authorities who complain to the emperor, not the chronology, is the organizing factor here."), _c('a', {
    attrs: {
      "href": "#/#foot-note-869",
      "id": "footnote-869"
    }
  }, [_c('sup', [_vm._v("101")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It is possible to identify particular textual units in Ezra-Nehemiah based on summaries. For example, Ezra 4:4–5 summarizes the preceding chapter, relating the troubles of rebuilding the temple."), _c('a', {
    attrs: {
      "href": "#/#foot-note-868",
      "id": "footnote-868"
    }
  }, [_c('sup', [_vm._v("102")])]), _vm._v(" Ezra 6:13–14 summarizes 5:1–6:12, recounting the progress and completion of the temple. Nehemiah 12:26 concludes the list of priests in 12:10–25, and, similarly, Neh 12:47 refers back to 12:44–46. Significantly, Neh 13:29b–31a only summarizes Neh 10–13, marking this section off as a separate source from the rest of the Nehemiah Memoir (see also summaries in Neh 12:26, 47; Ezra 6:13–14)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-867",
      "id": "footnote-867"
    }
  }, [_c('sup', [_vm._v("103")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Prayers too are used to mark off structure. For example, at the end of ch. 5 Nehemiah prays, “Remember for my good, O my God, all that I have done for this people.” This concludes the previous section, and a new episode is begun in ch. 6. Other invocations follow in Neh 6:14; 13:14, 22, 29, and, finally bringing closure to the book, 31."), _c('a', {
    attrs: {
      "href": "#/#foot-note-866",
      "id": "footnote-866"
    }
  }, [_c('sup', [_vm._v("104")])]), _vm._v(" As this discussion demonstrates, the techniques of literary criticism can work in tandem with those of source criticism.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-6"
    }
  }, [_vm._v("3. Literature and History")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Focus on Ezra-Nehemiah as a stylized literary presentation has led some scholars to regard it as fiction, simply motivated by an ideology rather than reality. Derek Kidner argues strongly against the notion that Ezra-Nehemiah is literary fiction or simply a polemic against universalist or messianic parties. He says, rather, that the principal aim of the writer was “to record the rebuilding of Israel’s life and institutions in the first century after the exile in the midst of a gentile world which was part enemy, part protector and governor, part seducer, and to show God’s providence in raising up the leaders who would hold her to her vocation as a people apart, centred on God’s Temple and city and law.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-865",
      "id": "footnote-865"
    }
  }, [_c('sup', [_vm._v("105")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Literary critics caution that indications of time in Ezra-Nehemiah should not be expected to be precise time indicators, as that was not the author’s intent."), _c('a', {
    attrs: {
      "href": "#/#foot-note-864",
      "id": "footnote-864"
    }
  }, [_c('sup', [_vm._v("106")])]), _vm._v(" Still, the large number of dates and times expressions in the book do give the sense of historical concern. The audience of the book, especially its earliest sources, was not far removed from the actual events and would know when they occurred."), _c('a', {
    attrs: {
      "href": "#/#foot-note-863",
      "id": "footnote-863"
    }
  }, [_c('sup', [_vm._v("107")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It appears that both diachronic and literary approaches to the study of Ezra-Nehemiah are valuable but limited in specific ways. Although fraught with unresolved difficulties, historical parallels ascertaining the decidedly Persian context of the drama have been identified, and thus attempts to locate it more precisely are valuable. At the same time, the narrative is not simply an incomplete catalog of data regarding Jews under the Persians but certainly entails a theological agenda that is presented carefully using recognizable literary methods. Thus, a critical reconstruction of the narrative in light of both its historical context and the literary forms imposed on it by later editors is necessary for a balanced viewpoint.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-12"
    }
  }, [_vm._v("D. SOCIAL SCIENCE")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Another modern tool for analyzing Ezra-Nehemiah comes from the Social Sciences. The main value of this approach is that it helps to “bridge the gap between the modern interpreter and the text.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-862",
      "id": "footnote-862"
    }
  }, [_c('sup', [_vm._v("108")])]), _vm._v(" D. Janzen builds upon the work of social anthropologist M. Douglas to explain the insulated communities of Ezra-Nehemiah that erected strong barriers against intermarriage. Douglas’s fieldwork among tribal communities in Africa revealed certain norms that resonate cross-culturally. She produced a system for describing social action within a community with the terms “group,” the barriers the community erects to preserve its identity vis-à-vis the outside world, and “grid,” the level of regulation within the community itself."), _c('a', {
    attrs: {
      "href": "#/#foot-note-861",
      "id": "footnote-861"
    }
  }, [_c('sup', [_vm._v("109")])]), _vm._v(" Janzen characterizes the edicts of Ezra-Nehemiah as coming from a community with strong external boundaries (“high group”) and a fluid internal structure (“weak grid”)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-860",
      "id": "footnote-860"
    }
  }, [_c('sup', [_vm._v("110")])]), _vm._v(" He describes the expulsion of foreign women as the witchhunt of a community that is over-sensitive to foreign elements. D. Moffat, similarly, regards the women as symbolic of non-absorbable impurity, but he does not see them as the target. Rather, the text blames the men for their inclusion and requires reparation rituals of them. In contrast to Janzen, Moffat regards the women as truly outsiders, “resistant to Yahwism and affiliated outside the community,” not as insiders who have been ostracized and labeled as outsiders."), _c('a', {
    attrs: {
      "href": "#/#foot-note-859",
      "id": "footnote-859"
    }
  }, [_c('sup', [_vm._v("111")])]), _vm._v(" Moffat, too, uses Douglas’s group/grid model to explain Ezra’s actions, but he labels the community as a “mid group” (i.e., one with a weakening external boundary because of the infiltration of these foreign women) and a “strong grid” (i.e., one with strict internal conformity, represented by the authoritarian leadership of Ezra)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-858",
      "id": "footnote-858"
    }
  }, [_c('sup', [_vm._v("112")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Douglas’s work on ritual pollution has been most helpful to unlock the social dynamics within Ezra-Nehemiah. She defines ritual pollution behavior as “the reaction which condemns any object or idea likely to confuse or contradict cherished classifications.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-857",
      "id": "footnote-857"
    }
  }, [_c('sup', [_vm._v("113")])]), _vm._v(" Impurity in ancient Israel was a malevolent force that threatened the holiness of the temple, its gifts, and the people. The antonym of impurity is holiness, which is equated with the divine presence, spirit, and power. Purity is the state of being, achieved by requisite purifications, that is necessary for holiness to be active. Profanity is a neutral, or inert, status that is not susceptible to impurity and does not carry any holy power either. Although the categories are somewhat inadequate, and certainly not used in Scripture, the terms “moral” and “ritual” impurity still serve to characterize the two main types of impurity: one due to sin and the other simply to the human condition. The latter can be discussed in three groups: impurity related to (1) death, (2) skin diseases, and (3) sexual processes. There is ample attestation throughout Scripture that these ritual impurities were obstacles to contact with holy things and that an impure person or item had to perform ablutions as prescribed in the Torah. Unlike ritual impurities, moral impurities were voluntary acts of transgression that carried severe penalties—even death, in some cases. In particular, idolatry, murder, and sexual sins were all referred to as moral impurities.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The major innovation in the Second Temple period with regard to impurity is that it is no longer caused just by certain bodily processes or the behavior of human beings, whether sinful acts like murder or biological actions like sexual intercourse. For the first time, it is attached as an adjective to certain types of people. In particular, gentiles, as well as Jews who do not conform to the group’s definition of Israel, are labeled “impure” (see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03c#exc23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 23, “Gentile Impurity,” pp. 469–77")]), _vm._v("). The sinner, not just the sin, is impure."), _c('a', {
    attrs: {
      "href": "#/#foot-note-856",
      "id": "footnote-856"
    }
  }, [_c('sup', [_vm._v("114")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Conversely, ritual purity is a label that communicates and reinforces the boundaries of a particular group; it expresses the group’s desire to preserve its integrity and marks off symbolically where its borders lie."), _c('a', {
    attrs: {
      "href": "#/#foot-note-855",
      "id": "footnote-855"
    }
  }, [_c('sup', [_vm._v("115")])]), _vm._v(" Its goal is to exclude some people from others, creating clear insider and outsider groups. This seems to be exactly the goal of Ezra and Nehemiah. Thus, ritual purity is not the end in itself, but it is a separation tool by which the group condemns and removes itself from contact with its enemy. As Saul Olyan says, “In Ezra-Nehemiah, struggle over defining the boundaries of the people itself leads to the employment of the rhetoric of impurity to justify exclusion from the community of all individuals classed as alien” (Ezra 9:1–2; Neh 13:4–9, 28–30)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-854",
      "id": "footnote-854"
    }
  }, [_c('sup', [_vm._v("116")])]), _vm._v(" Thus, impurity labels are used in Ezra-Nehemiah as accusatory labels that mark a person as an intruder in the group. Nevertheless, the fundamental reason for their undesirability lies elsewhere.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The work of social anthropologist Victor Turner has been influential in the study of Ezra-Nehemiah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-853",
      "id": "footnote-853"
    }
  }, [_c('sup', [_vm._v("117")])]), _vm._v(" Scholars have applied his work on ritual, symbols, and root paradigms to Ezra-Nehemiah in an effort to see how the material logically resonates with social habits in other cultures. Frank Gorman explains that the purpose of ritual is to maintain or restore order in society; it “is a way of construing, actualizing, realizing, and bringing into being a world of meaning and ordered existence. Ritual is, thus, seen as a means of enacting one’s theology.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-852",
      "id": "footnote-852"
    }
  }, [_c('sup', [_vm._v("118")])]), _vm._v(" Ritual action emphasizes the norms and beliefs important to a society and thus keeps them alive for the group. Rituals such as the purification rites required in Ezra-Nehemiah emphasize the bond between the insiders of the group and their shared commitment to holiness as they define it. Ritual can also be a means of restoring order in society after norms have been breached in some way by disruptive actions. A good example of this is the guilt offering, which was brought by those confessing their wrongful intermarriage, as a ritual intended to repair the relationship of the community with its God (Ezra 10:19).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Donald Moffat applies Turner’s work on social drama to Ezra-Nehemiah. Turner illustrates social drama with a model of four phases (breach, mounting crisis, redressive action, and reintegration), and Moffat, with small changes based on the work of more recent social theorists, applies these phases to the crisis of Ezra’s community over intermarriage."), _c('a', {
    attrs: {
      "href": "#/#foot-note-851",
      "id": "footnote-851"
    }
  }, [_c('sup', [_vm._v("119")])]), _vm._v(" The breach was created by the intermarriage of members of the community with outsiders (Ezra 9:2). The breach became a crisis when the matter was handed over to Ezra, who responded publicly, treating the matter as a grave crisis and placing the community in a liminal state requiring ritual actions of mourning and prayer (Ezra 9:3–10a). Redressive action was initiated by the assembly, which evaluated each case of intermarriage, and included a ceremony of recommitment to the covenant. This redress included a sacrifice that also marked the reintegration of the offenders back into the community and the restoration of its relationship with Yahweh.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("To my mind, the success of a sociological approach to Ezra lies with the ability of the interpreter not only to create valid sociological paradigms but also to combine these constructions with sound biblical exegesis. For example, during the intermarriage crisis, the biblical writer uses special terms (e.g., "), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("hošib,")]), _vm._v(" “to settle,” and "), _c('em', [_vm._v("l")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("hô")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ṣ")]), _c('em', [_vm._v("î")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em'), _vm._v(", “to expel,” for marriage and divorce respectively) that carry sociological weight and indicate that there was indeed disagreement over the legitimacy of mixed marriages within this community.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Thus, a sociological perspective can bring into relief aspects of the text that would not be obvious in a traditional historical-critical approach. The cultic nature of the narrative—especially the marriage crisis and its reparation rituals—might be otherwise lost. Admittedly, a common complaint against social science methodology is that it tends to oversimplify and reduce the complexities of the biblical text to generalized social models. Nevertheless, with the caution that one should use social science paradigms as hypothetical models rather than as a substitute for textual data, the social science approach can be fruitful."), _c('a', {
    attrs: {
      "href": "#/#foot-note-850",
      "id": "footnote-850"
    }
  }, [_c('sup', [_vm._v("120")])]), _vm._v(" In many cases, because of the resonance of the text with the social dynamics of other communities, the sociological method challenges the reader to view the Ezra 9–10 crisis as heavily rooted in reality rather than as literary fiction.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-5"
    }
  }, [_vm._v("V. PERSIAN RULE")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The Persians established an extensive empire that was larger than any of its predecessors in the Near East, along with an efficient organization to garnish taxes, draft soldiers, and conscript laborers. They did not consider foreign religions a threat unless they subverted the goals of the regime. Rather, they were a tool that provided a way to secure dominance and trust by supporting local culture.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-13"
    }
  }, [_vm._v("A. HISTORY VS. HISTORICITY")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The Persian period of Jewish history is admittedly one of the most elusive. Scholars often complain that the text of Ezra-Nehemiah has been accepted at face value and that difficulties in the text have too often been ignored."), _c('a', {
    attrs: {
      "href": "#/#foot-note-849",
      "id": "footnote-849"
    }
  }, [_c('sup', [_vm._v("121")])]), _vm._v(" Some doubt the coherence of the story completely."), _c('a', {
    attrs: {
      "href": "#/#foot-note-848",
      "id": "footnote-848"
    }
  }, [_c('sup', [_vm._v("122")])]), _vm._v(" Others recognize some history in the story but discount certain parts, especially its chronological framework."), _c('a', {
    attrs: {
      "href": "#/#foot-note-847",
      "id": "footnote-847"
    }
  }, [_c('sup', [_vm._v("123")])]), _vm._v(" The beneficence of the Persians, too, in financing the rebuilding of the temple and the restoration of its cult is surprising (Ezra 6:3–4), as is the remittance of taxes on the returning Jews."), _c('a', {
    attrs: {
      "href": "#/#foot-note-846",
      "id": "footnote-846"
    }
  }, [_c('sup', [_vm._v("124")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nevertheless, scholars agree that Ezra-Nehemiah is the main source for information regarding Jewish experience in this era. Several texts from the Achaemenid period support its historical value. For example, Near Eastern documents attest to an imperial concern for the continuance of local cults as long as the adherents prayed for the royal family."), _c('a', {
    attrs: {
      "href": "#/#foot-note-845",
      "id": "footnote-845"
    }
  }, [_c('sup', [_vm._v("125")])]), _vm._v(" In addition, the Persepolis Fortification Tablets (PFT) reveal a strong correspondence to Ezra-Nehemiah in terms of support for local religion. Williamson points out PFT 40: “Here we have Persian, Elamite and Babylonian gods all being honoured by their separate devotees within a circumscribed area, and all being supported equally by funds from the imperial treasury. Viewed in this light, the addition of another god to whatever list may have been supported by the treasury of ‘Beyond the River,’ specifying the quantities to be supplied, need have surprised nobody.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-844",
      "id": "footnote-844"
    }
  }, [_c('sup', [_vm._v("126")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Williamson also discusses parallels between the PFT and Ezra-Nehemiah in the area of accounting for and delivery of goods. He notes the minute accounting of goods (sometimes with inaccurate tabulations) to be used in various cults and their method and schedule of delivery to particular religious officials. According to the PFT, a leader of a group of travelers, authorized by a royal official, would draw out daily rations from one of the supply stations along his route. The official at the station would record the debit and send the document to Persepolis for a refund. There, the debit would have been applied to the account of the official who had authorized the travel leader and the account of the waystation would have been resupplied."), _c('a', {
    attrs: {
      "href": "#/#foot-note-843",
      "id": "footnote-843"
    }
  }, [_c('sup', [_vm._v("127")])]), _vm._v(" Williamson concludes that these supply stations must have been a single day’s journey apart along the major imperial highways.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In the discussion of biblical historiography, one does well to remember that the biblical books were written by authors who worked with layers of traditions and, as M. Jaffee notes, they “regarded themselves as mere ‘arrangers’ or ‘retellers’ who organized and recounted what they had received as ancient tradition.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-842",
      "id": "footnote-842"
    }
  }, [_c('sup', [_vm._v("128")])]), _vm._v(" The worldview of the narrative is apparent throughout. The material is not archival but literary, and not only is this evident in the author’s selection of events, but his whole presentation is embedded in a particular ideology. In fact, the sources he uses also reveal particular agendas."), _c('a', {
    attrs: {
      "href": "#/#foot-note-841",
      "id": "footnote-841"
    }
  }, [_c('sup', [_vm._v("129")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Faithful readers of Ezra-Nehemiah do not need to despair over discrepancies that exist in the text—for example, the inaccurate numbers in the lists of Ezra 2 and Neh 7. This work was considered sacred by the Jews not because it was perfect in the sense of lacking any error, however small, but because it was believed to carry divinely inspired traditions that could sustain the faith and life of their community. Indeed, the inclusion of frail human beings, however devoted, in the transmission of material, however sacred, introduces an imperfect element into the process.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-parab"
  }, [_vm._v("Thus, the goal here is to utilize all of the available data to present as complete a picture of Jewish history during this period as possible. To this end, the major sources available are as follows:"), _c('a', {
    attrs: {
      "href": "#/#foot-note-840",
      "id": "footnote-840"
    }
  }, [_c('sup', [_vm._v("130")])])]), _vm._v(" "), _c('table', [_c('tbody', [_c('tr', [_c('td', [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Category")])]), _vm._v(" "), _c('td', [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Sources")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Hebrew Bible")])]), _vm._v(" "), _c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra-Nehemiah, Haggai, Zechariah, Esther, Malachi, parts of Isaiah and Chronicles")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Postbiblical Jewish Literature")])]), _vm._v(" "), _c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("1 Esdras, Judith, Tobit, Susanna, Josephus")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Greek Historians")])]), _vm._v(" "), _c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Herodotus, Xenophon, Pseudo-Scylax, Diodorus Siculus")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ancient Papyri")])]), _vm._v(" "), _c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Elephantine and Wadi Daliyeh papyri")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Epigraphic Documents")])]), _vm._v(" "), _c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Royal inscriptions (e.g., Nabonidus Chronicle, Cyrus Cylinder, Darius I inscriptions), clay tablets (e.g., PFT), ostraca")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Seals")])]), _vm._v(" "), _c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Seals, seal impressions on pottery, and bullae that sealed written papyri")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Numismatic Evidence")])]), _vm._v(" "), _c('td', {
    staticClass: "wid47-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Coins from Judah and Samaria")])])])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para1"
  }, [_vm._v("With a sensitive handling of these sources, a sketch of the Jewish situation in Yehud under Persian rule begins to emerge.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-14"
    }
  }, [_vm._v("B. CYRUS II (559–530 BCE)")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-7"
    }
  }, [_vm._v("1. The Conquests of Cyrus")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The narrative of Ezra 1–6 takes as its starting point the accession of Cyrus to the throne of Persia and his conquest of the Near East. Cyrus came from the Achaemenid family of kings, Achaemenes being his grandfather. In 559 BCE, Cyrus began to reign as king of Anshan, later called Persis, an area in the southern part of Media which can be identified with the plain of Marv Dasht in modern Iran. With the defection of the Median army from the tyrant king Astyages, Cyrus laid claim to the entire Median Empire in 550 BCE."), _c('a', {
    attrs: {
      "href": "#/#foot-note-839",
      "id": "footnote-839"
    }
  }, [_c('sup', [_vm._v("131")])]), _vm._v(" Cyrus moved eastward to conquer Croesus, king of Lydia, and gain control of the Greek cities of western Asia Minor along with various areas of central Asia and the Iranian plateau.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("With the north and west firmly under his control, Cyrus turned south to confront his strongest opponent, Babylonia. According to the Nabonidus Chronicle, a Babylonian text, the people hated their rulers, Nabonidus and his son, Bel-shar-usur (= Belshazzar, Dan 5:1), for their inefficient rule, and they welcomed Cyrus and his army into the city of Babylon. The Chronicle claims that Nabonidus hoarded various cult statues within the city, no doubt to protect the city, and that upon his accession, Cyrus returned these idols to their owners. The explanation for Cyrus’s conquest of Babylonia propagated by the Cyrus Cylinder, a first-person proclamation attributed to Cyrus himself, is even more dramatic. According to this text, the gods appointed Cyrus to take over the nation because of the current rulers’ neglect of the cult of Marduk:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("Marduk … scanned and looked (through) all the countries, searching for a righteous ruler willing to lead him (i.e., Marduk) (in the annual procession). (Then) he pronounced the name of Cyrus, king of Anshan, declared him … to be(come) the ruler of all the world. ("), _c('em', [_vm._v("ANET")]), _vm._v(", 315)")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("According to these texts, both the people of Babylonia and their chief god endorsed Cyrus’s rule over their country.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("According to both the Cyrus Cylinder and the Nabonidus Chronicle, the conquest of Babylon (539 BCE) was peaceful. This is also the impression of the Greek historians (Xenophon, "), _c('em', [_vm._v("Cyropaedia")]), _vm._v(" 1.1.4; see Diodorus Siculus 9.24). On the other hand, these sources may attest to the power of Persian propaganda, since Nabonidus had built up troops to withstand the Persians at the Tigris River, and Cyrus not only conquered but massacred a host of these soldiers."), _c('a', {
    attrs: {
      "href": "#/#foot-note-838",
      "id": "footnote-838"
    }
  }, [_c('sup', [_vm._v("132")])]), _vm._v(" In any case, Cyrus’s triumphant procession into the city sealed the claim of Persian ownership.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Persian conquest of Babylon was undertaken during a Babylonian festival. Daniel describes the sudden death of Belshazzar and defeat of the Babylonian Empire after a banquet for one thousand guests, who observed an unattached hand write a prophecy of doom on the walls of the chamber (Dan 5:1–30). Greek historians confirm this view. Xenophon says the Persians attacked during a night-long festival of drinking and debauchery ("), _c('em', [_vm._v("Cyropaedia")]), _vm._v(" 7.5.15). Herodotus adds:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("The Babylonians themselves say that owing to the great size of the city the outskirts were captured without the people in the centre knowing anything about it; there was a festival going on, and even while the city was falling they continued to dance and enjoy themselves, until hard facts brought them to their senses. ("), _c('em', [_vm._v("Histories")]), _vm._v(" 1.191)")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("With the takeover of Babylon, the entire Transeuphrates region soon surrendered to the Persians. The Cylinder proclaims,")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("All the kings of the entire world from the Upper [Mediterranean] Sea to the Lower Sea [Persian Gulf], those who are seated in throne rooms, all the kings of Amurru living in tents, brought their heavy tribute and kissed my feet in Babylon.")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Cyrus created a huge satrapy that included Babylonia and the Transeuphrates.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-8"
    }
  }, [_vm._v("2. The Jews under Cyrus’s Rule")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Under Cyrus, Jews taken captive from the time of Nebuchadrezzar (= Nebuchadnezzar) King of Babylon in 586/587 BCE were now given the opportunity to return to Yehud and restore their traditional religion and form of local government. According to Ezra-Nehemiah, it was Yahweh himself who “stirred up the heart of Cyrus” to allow the exiled Jews to return to Jerusalem and rebuild their temple (Ezra 1:1).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The recent discovery of fifty-one tablets in a rural area near Nippur confirms that there was a Jewish presence in Babylonia during the sixth century BCE. The name of the town was called Al-Yahadu, “Judahtown,” giving evidence of the people’s identification with their Jewish homeland although on foreign soil. In addition, of the four hundred names on the tablets, over one-third carry Yahwistic features (see "), _c('span', {
    attrs: {
      "href": "#/012_Chapter02a#exc2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 2, “Bloodlines,” pp. 137–41")]), _vm._v(")."), _c('a', {
    attrs: {
      "href": "#/#foot-note-837",
      "id": "footnote-837"
    }
  }, [_c('sup', [_vm._v("133")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Cyrus Cylinder reveals that restoration of captives and their sanctuaries was not restricted to the Jews but applied to other foreign exiles as well:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("I returned to (these) sacred cities on the other side of the Tigris [i.e., to the east], the sanctuaries of which have been ruins for a long time, the images which (used) to live therein and established for them permanent sanctuaries. I (also) gathered all their (former) inhabitants and returned (to them) their habitations. Furthermore, I resettled upon the command of Marduk, the great lord, all the gods of Sumer and Akkad whom Nabonidus has brought into Babylon to the anger of the lord of the gods, unharmed, in their (former) chapels, the places which make them happy. ("), _c('em', [_vm._v("ANET")]), _vm._v(", 315–16)")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Although the Persians were adherents of Ahura Mazda, the god of Zoroastrianism, Cyrus not only allowed foreign captives to return to their homelands, but he even helped to finance the restoration of gods and temples. In return, he asked that the various peoples pray for him: “May all the gods whom I have resettled in their sacred cities ask daily Bel and Nebo for a long life for me” ("), _c('em', [_vm._v("ANET")]), _vm._v(", 316). In the case of the Jews, who had no images, Cyrus restored the valuable temple vessels, stolen by the Babylonians, to the returning exiles (Ezra 1) and subsidized the temple rebuilding along with its cult (Ezra 1:1–4; 6:3–5; cf. Ezra 7:23b). This policy of religious support was certainly unique in the ancient world, where gods boasted of vanquishing other gods in battle."), _c('a', {
    attrs: {
      "href": "#/#foot-note-836",
      "id": "footnote-836"
    }
  }, [_c('sup', [_vm._v("134")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The prophets extol Cyrus as the Jews’ deliverer, even their “messiah” (i.e., “anointed one,” Isa 45:1), an agent of God’s plan to restore Israel to her homeland: “He is my [Yahweh’s] shepherd and shall fulfill my purpose fully” (Isa 44:28a; cf. 45:4). Nowhere in Scripture do the exilic or postexilic prophets pronounce judgment on the Persian Empire."), _c('a', {
    attrs: {
      "href": "#/#foot-note-835",
      "id": "footnote-835"
    }
  }, [_c('sup', [_vm._v("135")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Jews returning from exile were not simply adventurers looking for another chance at life, but, like Cyrus, were stirred by the Spirit, in their case to make the long trek to a neglected homeland (Ezra 1:5). These Jews had maintained a fervent hope for the future that was fueled by their prophets. Just as the fall of Jerusalem was predicted, so was a return to the homeland and a brighter future for the faithful remnant (Jer 3:14–17; 23:3–8; 30:1–33:26; Ezek 34:11–16; 36–37).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Many of the returnees were not poor. They brought servants and animals with them and made sizeable contributions to the religious services (Ezra 2:65–69; 8:26; Neh 7:67–72). At the same time, as Josephus says, “Many remained in Babylon, being unwilling to leave their possessions” ("), _c('em', [_vm._v("Antiquities")]), _vm._v(" 11.8–9, tr. Marcus, LCL). The Murashu tablets, 730 business records from the time of Artaxerxes I and Darius II, tell of Murashu and his sons, who were bankers and brokers. They are said to have “loaned out almost anything for a price.” Approximately sixty Jewish names are listed among their customers in the roles of “contracting parties, agents, witnesses, collectors of taxes, and royal officials.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-834",
      "id": "footnote-834"
    }
  }, [_c('sup', [_vm._v("136")])]), _vm._v(" These cuneiform tablets were found near Nippur, sixty miles southeast of Babylon near the Kebar River, an area that the prophet Ezekiel identified as the settlement of the Jewish exiles (Ezek 1:1).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Upon their arrival in Jerusalem, the returnees, under the leadership of Sheshbazzar, succeeded by Zerubbabel, began to resettle their homes and rebuild the temple. The land had been devastated by the earlier Babylonian conquest, and according to some archaeologists, left half empty by the deportations."), _c('a', {
    attrs: {
      "href": "#/#foot-note-833",
      "id": "footnote-833"
    }
  }, [_c('sup', [_vm._v("137")])]), _vm._v(" Accordingly, the revitalization of the ancient capital took years as the population strove to overcome economic and social challenges.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("At first, the local population, seeing the authority and resources available to the newcomers, asked to help with the building project (Ezra 4:2). However, the returning Jews, who had renounced idolatry during the exile and recognized it as the reason for the captivity in the first place, were wary of any pagan influences. Apparently, they did not regard the religion of their neighbors to be pure. Even though the locals argued that they worshiped the same God as the returnees, Zerubbabel and the other leaders refused to let these "), _c('em', [_vm._v("adversaries")]), _vm._v(" join in the rebuilding project (Ezra 4:1–3), probably due to their religious syncretism. The returning Jews had retained their ethnic and cultural identity throughout the exile, and they were unwilling to place it at risk now by allowing a different “Israel” into their ranks."), _c('a', {
    attrs: {
      "href": "#/#foot-note-832",
      "id": "footnote-832"
    }
  }, [_c('sup', [_vm._v("138")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The local Israelites who had maintained continuous residence in Yehud since preexilic times had mixed with other peoples brought in by the Assyrians from foreign lands, and this created a mixture of both ethnicity and culture (2 Kgs 17:25–33). Their claim to worship the same God as the returnees has some validity, although their mixed identity would undoubtedly have produced a mixture of religious traditions as well. According to Jeremiah, the locals continued to visit the temple site after its destruction by the Babylonians and offer sacrifices there (Jer 41:4–8)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-831",
      "id": "footnote-831"
    }
  }, [_c('sup', [_vm._v("139")])]), _vm._v(" Nevertheless, there is ample evidence of the tendency of ancient Israel to religious syncretism, a criticism of many of the prophets of the past (e.g., 1 Sam 7:3; Jer 44:15–19; Ezek 8:14–16). Archaeological and epigraphical evidence in preexilic Palestine reveal different forms of Yahwisms, from an aniconic monotheism to a Yahwism that included a female consort."), _c('a', {
    attrs: {
      "href": "#/#foot-note-830",
      "id": "footnote-830"
    }
  }, [_c('sup', [_vm._v("140")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Papyri from Wadi Daliyeh and Elephantine evince a diluted Yahwism in these communities during the Persian period. The Wadi Daliyeh papyri, mainly legal documents and letters in Aramaic dating from 375 to 335 BCE, were found in a cave north of Jericho, where they were apparently left by Samaritan refugees fleeing Alexander’s army. The names recorded in these texts reflect some version of Yahwism."), _c('a', {
    attrs: {
      "href": "#/#foot-note-829",
      "id": "footnote-829"
    }
  }, [_c('sup', [_vm._v("141")])]), _vm._v(" They occur with various combinations of pagan gods, as Frank Cross has noted, including Qos (Edomite), Sahar (Aramaic), Kemosh (Moabite), Ba‘al (Phoenician), and Nabu (Baylonian)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-828",
      "id": "footnote-828"
    }
  }, [_c('sup', [_vm._v("142")])]), _vm._v(" The conflict of religion between the returnees and the local Samarians came to a head by the end of the fourth century BCE, when there is clear archaeological evidence of a temple in Samaria on Mt. Gerizim (2 Macc 6:2; Sir 50:25–26; cf. Josephus, "), _c('em', [_vm._v("Antiquities")]), _vm._v(" 11.8; see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03a#exc12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 12, “Samarians and Samaritans,” pp. 294–96")]), _vm._v(")."), _c('a', {
    attrs: {
      "href": "#/#foot-note-827",
      "id": "footnote-827"
    }
  }, [_c('sup', [_vm._v("143")])]), _vm._v(" Also, the names of the Jewish colonists at Elephantine often contained abbreviations of both pagan and Hebrew deities, and there is reference to the worship of both Yahu (Yahweh) and the Egyptian god Khnum."), _c('a', {
    attrs: {
      "href": "#/#foot-note-826",
      "id": "footnote-826"
    }
  }, [_c('sup', [_vm._v("144")])]), _vm._v(" Thus, it is not too surprising that the returning Jewish exiles insisted on separating themselves from the local population and maintaining the purity of their traditions (Ezra 4:3; cf. Neh 2:20).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The economic situation of the local population is hard to ascertain. For sure, the Babylonian conquest of the early sixth century BCE had left a devastated community, which was subject to looting, rape, and starvation (see Lam 5:1–18). The puppet governor, Gedaliah, was soon assassinated, and political chaos reigned for a while. During this period, the province of Samaria to the north probably increased in power over its Judean counterpart."), _c('a', {
    attrs: {
      "href": "#/#foot-note-825",
      "id": "footnote-825"
    }
  }, [_c('sup', [_vm._v("145")])]), _vm._v(" A few hundred Samarian coins, as well as bullae, have come to light from the city of Samaria, and these together with the Wadi Daliyeh papyri reveal a wealthy community in Samaria under Persian rule."), _c('a', {
    attrs: {
      "href": "#/#foot-note-824",
      "id": "footnote-824"
    }
  }, [_c('sup', [_vm._v("146")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The returning Jews experienced opposition in various ways and eventually stopped work on the temple. First, when they were rebuffed from participating in the project, the local Samarians became antagonistic. Second, the heavy taxes of the Persian government drained their finances (see the commentary on Ezra 4:13). Not only the emperor but the governors taxed the people (Neh 5:15; cf. Mal 1:8). This pressure, as well as the difficulty of surviving a drought, which Haggai states was divine punishment for their skewed priorities (Hag 1:2–11), soon brought discouragement to the returnees and eventually a standstill to the temple project until the reign of Darius I.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-15"
    }
  }, [_vm._v("C. DARIUS I (522–485 BCE)")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-9"
    }
  }, [_vm._v("1. The Reign of Darius I")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("After Cyrus and Cambyses, his son, had expanded the empire to include Egypt, the family almost lost control of the empire. Nevertheless, Darius I, who descended from a different branch of the Achaemenids, was able to gain control of the throne and put to death the usurper Gaumata and his followers.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The iron fist of Darius’s rule is legendary. The Behistun Inscription, a tri-lingual account of Darius’s military exploits, is etched into a rock cliff overlooking the main trade route between Mesopotamia and Persia. At the top of the inscription, a representation of the Zoroastrian god Ahura Mazda endorses the titulare of Darius: “I am Darius the Great King, King of Kings, King in Persia, king of countries, the son of Hystaspes, grandson of Arsames, an Achaemenian.” The inscription depicts Darius facing a line of subject kings tied together by a rope behind Gaumata, who is lying on his back. Darius, standing about 50 percent taller than these “liar kings,” rests his foot on Gaumata’s stomach.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Darius I was an accomplished king, and under his rule the Persian Empire reached its zenith. Darius was noted for the efficient organization of his empire, which was divided into twenty satrapies, or large administrative districts."), _c('a', {
    attrs: {
      "href": "#/#foot-note-823",
      "id": "footnote-823"
    }
  }, [_c('sup', [_vm._v("147")])]), _vm._v(" The duties of the satrap, the officer in charge of the satrapy, were to maintain order, extend Persian power, draft an army as needed, and collect tribute and gifts."), _c('a', {
    attrs: {
      "href": "#/#foot-note-822",
      "id": "footnote-822"
    }
  }, [_c('sup', [_vm._v("148")])]), _vm._v(" In his efforts to establish and maintain control of the empire, Darius placed imperial troops under the authority of royal inspectors, who checked on the satraps and reported directly to him. The Greeks called these officers the king’s “Eye” and “Ear” (Herodotus, "), _c('em', [_vm._v("Histories")]), _vm._v(" 1.114; Xenophon, "), _c('em', [_vm._v("Cyropaedia")]), _vm._v(" 8.2.10–12).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Taxes are a difficult issue to analyze. The imperial account is one of moderation, but the Greek historians complain that they were an unbearable burden that reduced the Greeks (and others) to slaves."), _c('a', {
    attrs: {
      "href": "#/#foot-note-821",
      "id": "footnote-821"
    }
  }, [_c('sup', [_vm._v("149")])]), _vm._v(" Subject peoples were obliged to pay imperial taxes as well as providing hospitality for the king and his troops. Herodotus describes it so:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("Apart from the normal tribute, the whole Persian empire is divided into regions for the purpose of furnishing supplies for the king and his army, and for four months out of the twelve the supplies come from the Babylonian territory, the whole of the rest of Asia being responsible for the remaining eight."), _c('a', {
    attrs: {
      "href": "#/#foot-note-820",
      "id": "footnote-820"
    }
  }, [_c('sup', [_vm._v("150")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Evidence of Jews paying taxes in kind comes from a jar from Yehud found in Babylon with the stamp, "), _c('em', [_vm._v("l")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _c('em', [_vm._v("nwnh yhwd")]), _vm._v(" (“belonging to "), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("Ḥ")]), _vm._v("anûnā, Yehûd”), similar to one for tribute from the governor in Byblos."), _c('a', {
    attrs: {
      "href": "#/#foot-note-819",
      "id": "footnote-819"
    }
  }, [_c('sup', [_vm._v("151")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In addition to imperial taxes, the satrap imposed taxes for the upkeep of his own house and entourage. The “Satrap’s table” was the food tax for the satrap. Plutarch says that the reason for a certain satrap’s rebellion was that he did not receive enough for his daily meals ("), _c('em', [_vm._v("Artaxerxes")]), _vm._v(" 4.1). Herodotus states that the Satrap of Babylonia received 800 stallions and 16,000 brood mares and numerous Indian dogs, but the most coveted position was that of the Satrap of Assyria ("), _c('em', [_vm._v("Histories")]), _vm._v(" 1.192)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-818",
      "id": "footnote-818"
    }
  }, [_c('sup', [_vm._v("152")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The satrapies were divided into provinces that could be run by local governors who also demanded tribute."), _c('a', {
    attrs: {
      "href": "#/#foot-note-817",
      "id": "footnote-817"
    }
  }, [_c('sup', [_vm._v("153")])]), _vm._v(" Xenophon says the Persians never sent a Persian governor into Cilicia and Cyprus but were always happy with the local governors (Xenophon, "), _c('em', [_vm._v("Cyropaedia")]), _vm._v(" 7.4.2). This appears to be the case also with Zerubbabel and Nehemiah. It is noteworthy that Nehemiah disclaims his right for this support and instead feeds 150 laborers at his own expense (Neh 5:14–18). Darius also promoted the function of local governments and published a decree that the Egyptians write their laws down."), _c('a', {
    attrs: {
      "href": "#/#foot-note-816",
      "id": "footnote-816"
    }
  }, [_c('sup', [_vm._v("154")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Persians were not so magnanimous as to allow locals to rule without imperial interference, and, of course, they could not decide an issue contrary to the wishes of the satrap. Even temples were not self-governing, but the Persians utilized them as sources of satrapal income. They functioned not only as religious shrines but as locally validated economic centers for the collection of goods and taxes throughout the empire. According to L. Fried, “Except in special cases, priests were available as corvée labor anywhere in the realm.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-815",
      "id": "footnote-815"
    }
  }, [_c('sup', [_vm._v("155")])]), _vm._v(" The satrap, and even a governor, could control the appointment of a high priest. Josephus reports that the priest Yohanan murdered his brother Yeshua because Bagoses (= Bagohi or Bagavahya), the governor of Yehud, promised Yeshua the high priesthood ("), _c('em', [_vm._v("Antiquities")]), _vm._v(" 11.297).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Both satraps and governors commanded garrisons of soldiers. This is clear from data from Babylon, Egypt, and Asia Minor but also from the text of Nehemiah. “King’s men” were sent with Nehemiah to man the citadel of Jerusalem (Neh 2:9)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-814",
      "id": "footnote-814"
    }
  }, [_c('sup', [_vm._v("156")])]), _vm._v(" According to Carter, there were fifteen Persian garrisons and administrative centers in Yehud."), _c('a', {
    attrs: {
      "href": "#/#foot-note-813",
      "id": "footnote-813"
    }
  }, [_c('sup', [_vm._v("157")])]), _vm._v(" Nehemiah placed his brother Hanani over the garrison in Jerusalem (Neh 7:2). Fried claims that, under Nehemiah, the populace at large was not armed but only the Persians whom he brought with him (Neh 4:16–23[10–17])."), _c('a', {
    attrs: {
      "href": "#/#foot-note-812",
      "id": "footnote-812"
    }
  }, [_c('sup', [_vm._v("158")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Darius also standardized the money of the empire and introduced coinage with the royal image."), _c('a', {
    attrs: {
      "href": "#/#foot-note-811",
      "id": "footnote-811"
    }
  }, [_c('sup', [_vm._v("159")])]), _vm._v(" He was interested in exploration and expanded the empire into India in the east and dug a canal from the Red Sea to the Nile to facilitate trade with his southwestern frontier, Egypt. By the fourth century BCE, Yehud had a fair amount of autonomy and even created its own money system. Archaeologists have found a large number of coins and seal impressions inscribed with the word “Yehud.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-810",
      "id": "footnote-810"
    }
  }, [_c('sup', [_vm._v("160")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Another important achievement of the Persians was a, by ancient standards, state-of-the-art transportation and communication system. Royal roads were marked in stages with postal stations, inns, and storehouses."), _c('a', {
    attrs: {
      "href": "#/#foot-note-809",
      "id": "footnote-809"
    }
  }, [_c('sup', [_vm._v("161")])]), _vm._v(" The Via Maris, the main road running north-south along the coast of Palestine, was dotted with forts and granaries for the needs of the Persian administration. A system of aural and visual relays provided additional communication across parts of the empire. The common language of Aramaic was standardized throughout the Persian Empire."), _c('a', {
    attrs: {
      "href": "#/#foot-note-808",
      "id": "footnote-808"
    }
  }, [_c('sup', [_vm._v("162")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("An official east-west road from Susa to Sardis connected 1,677 post offices. According to Herodotus, the royal mail took only one week from anywhere in the empire on swift horses (see the king’s “mounted couriers riding swift horses,” Esth 8:10); commoners were served by a slower system. The inscription on the General Post Office building in New York City (now renamed the James A. Farley Building) “Neither snow nor rain nor heat nor gloom of night stays these couriers from the swift completion of their appointed rounds” is taken from Herodotus’s description of the Persian mail service (Herodotus, "), _c('em', [_vm._v("Histories")]), _vm._v(" 8.98: “These are stopped neither by snow nor rain nor heat nor darkness from accomplishing their appointed course with all speed” [Godley, LCL]). On the royal roads, the king’s men were expected to protect travelers and goods and enforce severe laws against robbers."), _c('a', {
    attrs: {
      "href": "#/#foot-note-807",
      "id": "footnote-807"
    }
  }, [_c('sup', [_vm._v("163")])]), _vm._v(" In the same vein, Xenophon claims that Cyrus the Younger ensured safe travel throughout the realm by punishing robbers with the excision of hands, feet, or eyes (Xenophon, "), _c('em', [_vm._v("Anabasis")]), _vm._v(" 1.9.13).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Persian policy encouraged local populations to codify and practice their own laws and customs as long as they did not interfere with the administration of the Persian government. Once local laws were given the status of royal law, they could be implemented with the authority and muscle of the empire. Udjahorresnet, for example, says he was sent back to Egypt to put its scribal system in order (Inscription of Udjahorresnet 7). About 519 BCE, Darius ordered his satrap in Egypt to assemble Egyptian sages (priests, warriors, and scribes) to collect all the old laws of Egypt from the time of Pharaoh Amasis’s forty-fourth year, 526 BCE, the time of the Achaemenid conquest. This was done in two copies, Aramaic and demotic. The text distinguishes “public,” “temple,” and “private” law."), _c('a', {
    attrs: {
      "href": "#/#foot-note-806",
      "id": "footnote-806"
    }
  }, [_c('sup', [_vm._v("164")])]), _vm._v(" Thus, Darius I funded the codification of a province’s laws, making local customs official rather than micro-managing the provinces by imposing his own traditions.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Darius is also famous as a master builder. He is responsible for the reconstruction of Susa as well as the new capital at Persepolis while continuing to maintain the older capitals of Pasargadae, Babylon, and Ecbatana."), _c('a', {
    attrs: {
      "href": "#/#foot-note-805",
      "id": "footnote-805"
    }
  }, [_c('sup', [_vm._v("165")])]), _vm._v(" Inscriptions and art on his buildings attempt to confirm the overall imperial image of Darius as beneficent, restoring order, building up cities, pacifying quarrelsome peoples, and, in general, maintaining the "), _c('em', [_vm._v("pax persica.")]), _vm._v(" Most importantly, these edifices attest to Darius’s autocratic status as the “king of kings” who commands unquestioning loyalty, service, and tribute from his subjects."), _c('a', {
    attrs: {
      "href": "#/#foot-note-804",
      "id": "footnote-804"
    }
  }, [_c('sup', [_vm._v("166")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Darius was instrumental in preserving and restoring temples throughout his empire. He provided the operating income not only for the Jerusalem temple but for various Egyptian temples which he had rebuilt."), _c('a', {
    attrs: {
      "href": "#/#foot-note-803",
      "id": "footnote-803"
    }
  }, [_c('sup', [_vm._v("167")])]), _vm._v(" According to Udjahorresnet, “His Majesty did this … to preserve the names of all the gods, their temples, the income from their "), _c('em', [_vm._v("wakf")]), _vm._v(" properties, and the observances of their festivals, for all time” (Inscription of Udjahorresnet 7)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-802",
      "id": "footnote-802"
    }
  }, [_c('sup', [_vm._v("168")])]), _vm._v(" The classical sources note Darius’s respect of Egyptian shrines and customs in contrast to the damage done to them by his predecessor, Cambyses (e.g., Diodorus Siculus 1.95.5)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-801",
      "id": "footnote-801"
    }
  }, [_c('sup', [_vm._v("169")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Darius’s many reconstructions of sacred sites served a political end as well. The emperor could be included in the local cult in some way and thereby reinforce his honor and authority."), _c('a', {
    attrs: {
      "href": "#/#foot-note-800",
      "id": "footnote-800"
    }
  }, [_c('sup', [_vm._v("170")])]), _vm._v(" Darius’s image figures prominently among the artistic representations of the Egyptian gods, even as the pharaoh of Egypt, and thus Persian imperial power penetrated into Egyptian religion and was reinforced at every level of the population. The goddess Neith is portrayed as nursing the young Darius; Isis embraces him, and Hathor holds him by the arms. In another place, Mut nurses Darius, conferring upon him divine status.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-10"
    }
  }, [_vm._v("2. The Jews under Darius I")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("During the time of Darius the Great, God called the prophets Haggai and Zechariah to encourage the people to take up the task of rebuilding the temple once again (Ezra 5:1). Cutting through indifference and discouragement, Haggai accuses the people of managing to build their own houses while leaving God’s house in ruins (Hag 1:4). The Jews took up the challenge, and in a matter of weeks the foundations of the new house were laid and some kind of dedication service conducted (Hag 1:14–2:3). The rebuilding effort of the Jewish temple was not without opposition. Tattenai, a local authority, demanded to see a building permit and a list of the names of the workers (Ezra 5:3–4)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-799",
      "id": "footnote-799"
    }
  }, [_c('sup', [_vm._v("171")])]), _vm._v(" After investigating the matter, Darius I found the original edict of Cyrus authorizing the work, and he not only allowed the work to go forward but subsidized it out of the royal treasury.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("According to John Berquist, Darius mandated the construction of the temple and paid for it not only as a religious center but as a “base of operations for the political administration (cf. Neh 13); and it would also serve as storage for grain.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-798",
      "id": "footnote-798"
    }
  }, [_c('sup', [_vm._v("172")])]), _vm._v(" This would meet his need for food and supplies as he was bringing his army through the area on its way to Egypt in 519 BCE. Whatever the imperial reasoning, it was providential for the Jews who lived in Jerusalem. Darius not only supplied authorization for the temple’s continued rebuilding; he also provided operating expenses for the cult. According to Ezra-Nehemiah, he asked only that the Jews pay tribute to him and pray for the protection of his family (Ezra 6:6–12). Work on the temple was begun in 520 BCE and completed by March 515 BCE, and the Jews immediately celebrated the Passover on its completion.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Darius I apparently respected the rights of sacred personnel and did not, as a rule, subject them to taxation. In the Letter of Darius to Gadatas, one of the royal gardeners in charge of a park close to Magnesia on the Maeander (an ancient city on the west coast of modern Turkey), Darius commends Gadatas for his horticulture but accuses him of subjecting the sacred gardeners of Apollo to tribute and to work on profane lands. Apparently, the royal garden of which Gadatas was in charge abutted a Greek sanctuary and Gadatas took advantage of the sacred gardeners. The document is an example of the fiscal immunity of sanctuaries and cultic personnel under the Persians (see also Ezra 7:24; Neh 2:8)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-797",
      "id": "footnote-797"
    }
  }, [_c('sup', [_vm._v("173")])]), _vm._v(" This was probably not just Persian strategy or beneficence at work, for the sanctuary and its priests played a role in the collection of revenue for the treasury of the king."), _c('a', {
    attrs: {
      "href": "#/#foot-note-796",
      "id": "footnote-796"
    }
  }, [_c('sup', [_vm._v("174")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-16"
    }
  }, [_vm._v("D. ARTAXERXES I (465–424 BCE)")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-11"
    }
  }, [_vm._v("1. Artaxerxes Longimanus I")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Artaxerxes I, son of Xerxes, came to power in the mid-fifth century BCE. He was called Longimanus (“Long-handed”) because his right hand was longer than his left. Plutarch describes him as a man of a “gentle and noble spirit” ("), _c('em', [_vm._v("Artaxerxes")]), _vm._v(" 1.1). Artabanus, Xerxes’s vizier, had assassinated the king and tried to take over the government, but the plot failed. Although he was not Xerxes’s eldest son and heir, Artaxerxes managed to take the throne.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Like his predecessors, Artaxerxes had to deal with revolts against his rule in various parts of his large empire. His brother, Hystaspes, for example, instigated a revolt in Bactria; and Inarus, a Libyan aided by a large Athenian fleet, instigated an Egyptian uprising. The Persians persisted for four years in a war against the Athenians (459–454 BCE), and under the leadership of Megabyzus they finally trapped and destroyed the Athenian fleet. Later, however, Megabyzus himself supposedly revolted, but the king granted him pardon."), _c('a', {
    attrs: {
      "href": "#/#foot-note-795",
      "id": "footnote-795"
    }
  }, [_c('sup', [_vm._v("175")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-12"
    }
  }, [_vm._v("2. Artaxerxes and Ezra")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Ezra, a Jewish priest and scribe living in exile, was given permission by Artaxerxes I in 458 BCE to travel to the province of Yehud and teach the inhabitants the law of Yahweh. It may have been due to the difficulty of maintaining Persian control at the western end of the empire, and especially to trouble holding onto Egypt, that Artaxerxes supported Ezra’s journey to Yehud. It is worthy of note that in their report to the king, the enemies of the Jews accuse them of sedition and rebellion (Ezra 4:7–23). By endorsing a Jewish leader in Yehud, the Persians may have hoped to secure a loyal buffer state on the border of Egypt."), _c('a', {
    attrs: {
      "href": "#/#foot-note-794",
      "id": "footnote-794"
    }
  }, [_c('sup', [_vm._v("176")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The biblical sources paint a bleak picture of the local population in Yehud at this time. The region had not recovered from the destruction, depopulation, and loss of the upper echelons of society, including skilled artisans, that occurred during the Babylonian invasions. This negative situation was compounded for the mostly agrarian society by several bad harvests and droughts (Hag 1:6–11; 2:16–17). Economically, the empire was on the decline beginning with the reign of Xerxes. Taxes increased throughout the empire, except on Persians, as focus remained fixed on the Greek wars, and taxes were a burden in Yehud."), _c('a', {
    attrs: {
      "href": "#/#foot-note-793",
      "id": "footnote-793"
    }
  }, [_c('sup', [_vm._v("177")])]), _vm._v(" The Judeans engaged in trade with surrounding areas, selling olives and oil, but were at times forced to borrow at excessive interest rates, to mortgage their farms, and even to sell their children (Neh 5:3–5)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-792",
      "id": "footnote-792"
    }
  }, [_c('sup', [_vm._v("178")])]), _vm._v(" Like the Greek descriptions, Ezra-Nehemiah portrays the people as “slaves” (Neh 9:36–37) of rulers who caused them great distress.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Approximately three thousand to five thousand Jews returned with Ezra to the land of their ancestors in the seventh year of Artaxerxes I. Ezra held the authority of the Persian crown to establish Jewish law in the province, and he was also given provisions for sacrifices at the temple."), _c('a', {
    attrs: {
      "href": "#/#foot-note-791",
      "id": "footnote-791"
    }
  }, [_c('sup', [_vm._v("179")])]), _vm._v(" Similar to Udjahorresnet in Egypt, Ezra is told:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("And as for you, Ezra, according to the wisdom of your God which you possess, appoint judges and magistrates who will judge all the people who live in Across the River, that is, all those who know the law of your God; and teach those who do not know it. And whoever will not obey the law of your God and the law of the king must surely be punished, whether by death, banishment, confiscation of property, or imprisonment. (Ezra 7:25–26)")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("As long as they did not conflict with Persian interests and order in the western region of the empire, the king protected and guaranteed the lawgiver’s orders. Persian interest in local traditions and law, reflected in Ezra-Nehemiah, stems from imperial Persian policy, not from special interest in the Jews."), _c('a', {
    attrs: {
      "href": "#/#foot-note-790",
      "id": "footnote-790"
    }
  }, [_c('sup', [_vm._v("180")])]), _vm._v(" In fact, the Jews in Ezra-Nehemiah express a negative attitude toward the Persians at times (e.g., Neh 9:36–37)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-789",
      "id": "footnote-789"
    }
  }, [_c('sup', [_vm._v("181")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Artaxerxes gave Ezra not only royal authority but also materials and provisions for the temple and its offerings. Following the biblical text, the amounts are astronomical: 100 talents of silver (= 3.75 tons); 100 kors of wheat (= ca. 650 bushels); 100 baths (= 607 gallons) each of oil and wine; and unlimited salt (Ezra 7:22). This support of the sanctuary seems to be a reversal of the policy of Xerxes, who did not fund local temples, but may be a way in which Artaxerxes exerted control on the affairs and revenues of the province of Yehud and thereby strengthened his presence on the Mediterranean frontier. As John Berquist explains, “It strengthened the temple system for funneling taxation revenues through the colony and into the larger imperial system.” It would also increase the loyalty and wealth of the aristocracy in charge of these revenues."), _c('a', {
    attrs: {
      "href": "#/#foot-note-788",
      "id": "footnote-788"
    }
  }, [_c('sup', [_vm._v("182")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra holds the genealogical credentials of a priest (Ezra 7:1), but his expertise rests in studying and teaching Jewish law (Ezra 7:10). Nehemiah 8 describes Ezra reading the law to the whole community, who had gathered to hear it with their families (Neh 8:1–8), and then organizing a further study session with the leaders on the next day (Neh 8:13). He also leads worship (Neh 8:6) and heads one of the two processions celebrating the dedication of the wall (Neh 12:36). His greatest challenge was dealing with the mixed marriages crisis. Over a hundred Jews, including many of the leaders, had married foreign women (Ezra 10:17–43). After much fasting, weeping, and praying, Ezra orders the offenders to divorce their wives—even those with children (Ezra 10:5, 44).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-13"
    }
  }, [_vm._v("3. Artaxerxes and Nehemiah")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Nehemiah, Artaxerxes’s royal cupbearer, came to Jerusalem at the request of his brother, Hanani, after hearing his distressing story of the city’s ruin and lack of security. Artaxerxes granted Nehemiah permission to leave his post and take on the governorship of Judah with the special mission of rebuilding the city’s walls. Unlike Ezra, Nehemiah held an official position as governor of the region. Although he was subordinate to the satrap of the Transeuphrates, he had authority over the five districts of the province of Yehud: Jerusalem, Beth-haccerem, Mizpah, Beth-zur, and Keilah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-787",
      "id": "footnote-787"
    }
  }, [_c('sup', [_vm._v("183")])]), _vm._v(" Like satraps, governors received a special tax (“the governor’s bread”) that provided for everyone at their table, but Nehemiah did not impose it and instead supported 150 men plus guests at his own expense (Neh 5:14–18). Still, he levied taxes and imposed a state of military readiness (Neh 4:17; 5:4)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-786",
      "id": "footnote-786"
    }
  }, [_c('sup', [_vm._v("184")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nehemiah insisted on the strict observance of Jewish law and corrected such abuses as profanation of the Sabbath, lack of tithes for the Levites, and lack of support for those involved in the building project. He organized the collection of various offerings for the temple, including a one-third shekel head tax, firstfruits, tithes, and salaries for the cantors and gatekeepers (Neh 13:12).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Three provincial leaders vigorously opposed Nehemiah and accused him of sedition and rebellion: Sanballat, governor of Samaria; Tobiah, an Ammonite leader; and Geshem (or Gashmu), the Arab."), _c('a', {
    attrs: {
      "href": "#/#foot-note-785",
      "id": "footnote-785"
    }
  }, [_c('sup', [_vm._v("185")])]), _vm._v(" Like Nehemiah in Yehud, Sanballat was subject to the governor of the Transeuphrates; the other two leaders were apparently wealthy landowners. Curiously, they never appeal to the Persian emperor but instead try to neutralize Nehemiah’s purposes with threats and trickery (Neh 6:10–19). Despite their machinations, Nehemiah successfully secures Jerusalem with a wall in fifty-two days (Neh 6:15)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-784",
      "id": "footnote-784"
    }
  }, [_c('sup', [_vm._v("186")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Sanballat is known from the Elephantine papyri as the governor of Samaria, succeeded by his sons Delaiah and Shelemaiah, who were in charge around 407 BCE. At that time, the colonists sent a letter to Bagohi, governor of Judah, requesting support for the rebuilding of their temple, which was destroyed in a riot incited by the Egyptian priests of the god Khnum with the aid of the Persian commander. They complain that their appeals to Johanan, the high priest in Jerusalem, have been ignored (possibly due to the syncretism of their religion) and that they have also written to Delaiah and Shelemaiah, sons of Sanballat, governor of Samaria (see CAP, nos. 30, 108–19). They also appeal to prior authorization from Cambyses, who had apparently endorsed their temple while destroying many others; but, perhaps because they had nothing in writing, their petition was rejected by the local Egyptian governor."), _c('a', {
    attrs: {
      "href": "#/#foot-note-783",
      "id": "footnote-783"
    }
  }, [_c('sup', [_vm._v("187")])]), _vm._v(" The Persian satrap overturned the decision in their favor and granted them permission to rebuild the temple, which they completed around 402 BCE."), _c('a', {
    attrs: {
      "href": "#/#foot-note-782",
      "id": "footnote-782"
    }
  }, [_c('sup', [_vm._v("188")])]), _vm._v(" The Samarian papyri found at Wadi Daliyeh reveal that the family of Sanballat was still in power a half century later.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Not only the Samarians but also the Tobiads gave Nehemiah grief, trying to intimidate him and neutralize his efforts to reconstruct Jerusalem’s walls. Although Nehemiah refers to him as “the Ammonite,” Tobiah considers himself an Israelite. In fact, many of the descendants of Tobiah are listed among the returning exiles; however, they cannot prove their Israelite descent (Neh 7:61–62). Tobiah’s name is Yahwist, and his family had risen to wealth and power during the exilic period, owning estates in the Transjordan."), _c('a', {
    attrs: {
      "href": "#/#foot-note-781",
      "id": "footnote-781"
    }
  }, [_c('sup', [_vm._v("189")])]), _vm._v(" Many Jews of Nehemiah’s community had borrowed money from Tobiah, and they are in his debt. Nehemiah works to remove the debts of the people to the greedy landowners and make the temple the center of the economy through the collection of tithes. Upon his return to Jerusalem, Nehemiah discovers that Tobiah has taken up rooms in the temple court, and he expels him (see the commentary on Neh 13:9; for more discussion on Tobiah, see commentary on Neh 6:17–19; 13:1–9).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Information on Geshem the Arab is negligible. Dedications inscribed on silver vessels found at Tell el-Maskhuta in Egypt may include a reference to him. One vessel is inscribed with the name “Qaynu, son of Gashmu, king of Qedar.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-780",
      "id": "footnote-780"
    }
  }, [_c('sup', [_vm._v("190")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Like Ezra, Nehemiah engaged in social and religious reform by opposing marriages between Jews of his community and outsiders. He regards the genealogical purity of the community as essential to the continuance of Israelite religion according to the law. (See "), _c('span', {
    attrs: {
      "href": "#/012_Chapter02c#exc9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 9, “The Sacrilege of Intermarriage,” pp. 244–54")]), _vm._v(".)")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-17"
    }
  }, [_vm._v("E. INTERNAL JEWISH LEADERSHIP")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Israel’s political and religious institutions, which supported the community during monarchical times, were dealt a shattering blow by the exile. The tripod of king, priest, and prophet as the leadership of Israel had collapsed."), _c('a', {
    attrs: {
      "href": "#/#foot-note-779",
      "id": "footnote-779"
    }
  }, [_c('sup', [_vm._v("191")])]), _vm._v(" Indeed, the king was no longer Israelite but Persian, and Zerubbabel, although a Davidic descendant and local leader, was subject to the Persian crown. After him, Davidic leadership all but vanishes in the Second Temple period, although the genealogical line is still tracked (see Matt 1:1–17).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The authority of the priests became stronger in Second Temple Judaism as they subsumed the vacuum of power left by the absence of a Jewish monarch. Even though the sanctuary was destroyed, they still retained their position among the Jews. According to Ezra-Nehemiah, in the first return there were only three priestly families in charge of the temple service (Neh 10:33–40).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Working alongside the priests, Levites take on a prominent role as one of the three major categories of the community: Priests, Levites, and Israel (see Ezra 7:7; 9:1; 10:5; Neh 11:3; 12:47). According to the Torah, Levites who are not of the Aaronic line are not qualified to officiate at the altar, but they may assist the priests (Num 18:1–6)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-778",
      "id": "footnote-778"
    }
  }, [_c('sup', [_vm._v("192")])]), _vm._v(" In Ezra-Nehemiah, Levites slaughter, but do not offer, the Passover offering for the people (Ezra 6:20), and they assist in purifying the people for the festival (Neh 12:30). Levites also assist the priests in teaching the law, explaining difficult matters to the congregation (Ezra 8:18; Neh 8:7–8). Levites are listed in Ezra-Nehemiah among the officials of the governor (Neh 3:17–18), and they also serve as temple musicians, clerks of the temple storeroom, and gatekeepers of both the temple and the wall of Jerusalem (see Neh 12:44–47). Correcting the neglect of the past, Nehemiah expressly orders the people to contribute tithes for the Levites (Neh 13:10–11).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The authority of the priesthood and the Levites in Ezra-Nehemiah, while a strong force, gave way in part to a new kind of leadership: the scholar."), _c('a', {
    attrs: {
      "href": "#/#foot-note-777",
      "id": "footnote-777"
    }
  }, [_c('sup', [_vm._v("193")])]), _vm._v(" The scribe Ezra is not simply one who records laws and memoranda, but he is a scholar and teacher of the Word of God. According to his memoir, Ezra devoted himself to the study and observance of the Torah. Ezra 7:10 explains that Ezra’s commitment to studying the law was aimed at its practice within the community."), _c('a', {
    attrs: {
      "href": "#/#foot-note-776",
      "id": "footnote-776"
    }
  }, [_c('sup', [_vm._v("194")])]), _vm._v(" In his position as an emissary of the Persians, he had the authority to enforce it. In fact, the importance of the Torah lies in its revelation of God’s wisdom to humanity, including but not limited to law. “The wisdom of God which is in your hand” (Ezra 7:25; cf. v. 14) describes the Torah of Ezra. Ezra equates “wisdom” with the Torah, and in this regard he is the forerunner of Ben Sirach and later Pharisaic Judaism (cf. Sir 24:1–22). New Testament authors take the notion a step farther by associating divine wisdom with Christ (1 Cor 1:30; Col 2:3; cf. John 1:3).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The result of Ezra’s teaching is twofold. On the one hand, there is joy among the congregation just for the fact that they have understood the Word of God (Neh 8). On the other hand, however, there is an evocation of guilt at the recognition that much of the lifestyle of the people does not match the expectations of God. For example, in the matter of mixed marriages, it becomes painfully clear that this is an impediment to holiness, for the lack of which the people incurred the wrath of Yahweh and lost their possession of the promised land (see Lev 26:31–35; Deut 7:1–6; 30:18).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The tension between these leadership roles is evident in Ezra-Nehemiah. For example, Ezra, who is both priest and scribe, is not in the list of current high priests, but he is certainly in charge of the entire community, including the high priesthood. Nehemiah is even more powerful as the authorized Persian governor. Both leaders encounter opposition from the local priesthood, although it is muted and does not lead to outright rebellion. Leaders intermarry, the high priest does not participate in Ezra’s divorce action, and some elders have reservations (Ezra 10:15). The officials of Judah have dealings with the local populations against Nehemiah’s wishes (Neh 6:17); the high priest does not sign Nehemiah’s list of signatories, and when Nehemiah takes a leave of absence, the high priest installs Tobiah the Ammonite in the temple court (Neh 13:5).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The third leg of the preexilic leadership tripod, the prophet, is muted in Ezra-Nehemiah apart from the ministries of Haggai and Zechariah, who supported Zerubbabel and the community of the first return (Ezra 5:1–2). The prophet Malachi later explains that monarchy and prophecy are not dead but that they have to await the coming of Elijah and the Messiah (Malachi 3–4 [3])."), _c('a', {
    attrs: {
      "href": "#/#foot-note-775",
      "id": "footnote-775"
    }
  }, [_c('sup', [_vm._v("195")])]), _vm._v(" Nevertheless, while playing a minor role in Ezra-Nehemiah, the prophetic hope of full restoration under a messiah figure is alive and well during this period, as is clear from the texts of Haggai and Zechariah.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-6"
    }
  }, [_vm._v("VI. SECOND TEMPLE JUDAISM")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Several ideological norms and currents of Second Temple Judaism had their source in the period described in Ezra-Nehemiah. The early Persian period marks a turning point in Jewish faith and practice. Returning exiles proudly linked themselves with the grand past of the Israelite and Judean monarchies through genealogical lines and religious heritage, but their community had changed irreparably as the result of foreign dominance and exile. Their priorities and issues were somewhat different. Nevertheless, certain strengths emerged that continued in the various forms of Judaism of the next several centuries and even to this day. This is the period that spawned the courageous Maccabees, who fought for religious freedom against the hordes of the Greek king Antiochus IV and succeeded with the help of divine intervention (167 BCE). Furthermore, it was during Second Temple times that Jews dedicated to the Torah wrote the Dead Sea Scrolls, preserving the sacred text for posterity.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Second Temple Judaism was also the environment that gave birth to Christianity. Faithful Jews of this period retained their hope in the Messiah, who would relieve oppression and bring the kingdom of God to fruition on earth. It was to expectant and observant Jews serving in the Second Temple that God revealed the birth of his Son (e.g., Simeon, Anna, Zechariah, and Elizabeth). Most importantly, it was faithful Jews who were entrusted with the birth of Jesus and who became his disciples and apostles. The often-expressed evaluation of ancient Judaism as trite legalism or ritualism is not a fair assessment of the community of faith during this period."), _c('a', {
    attrs: {
      "href": "#/#foot-note-774",
      "id": "footnote-774"
    }
  }, [_c('sup', [_vm._v("196")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The experience of exile away from the land of promise led to a definite change in the self-image and thought of Israel. Much has been written on the “axial transformation” or “fundamental change” among the people."), _c('a', {
    attrs: {
      "href": "#/#foot-note-773",
      "id": "footnote-773"
    }
  }, [_c('sup', [_vm._v("197")])]), _vm._v(" In terms of population alone, the difference is staggering (see "), _c('a', {
    attrs: {
      "href": "#/010_Chapter01#sec-4"
    }
  }, [_vm._v("“Setting,” pp. 22–26")]), _vm._v("). Not only were they under foreign domination, but they were a subgroup of the population in Yehud. Having lost political control of the land, their residence in it was tentative.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Many debates have centered on the nature of the development from early Second Temple Judaism to later forms of Judaism. E. P. Sanders suggests that throughout the entire period from Persian hegemony to the rabbinic period, there was a nucleus of religion that did not change, and this became the core of rabbinic Judaism."), _c('a', {
    attrs: {
      "href": "#/#foot-note-772",
      "id": "footnote-772"
    }
  }, [_c('sup', [_vm._v("198")])]), _vm._v(" L. Schiffman rejects this underlying stability of Judaism and argues for a variety of Jewish religious traditions that evolved over the centuries, “each period and approach bequeathed to that which came after”; over time, diverse ideas synthesized into a mainstream tradition."), _c('a', {
    attrs: {
      "href": "#/#foot-note-771",
      "id": "footnote-771"
    }
  }, [_c('sup', [_vm._v("199")])]), _vm._v(" D. Edelman insists that “early Judaism” emerged only from the Hasmoneans onward (ca. 143 BCE)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-770",
      "id": "footnote-770"
    }
  }, [_c('sup', [_vm._v("200")])]), _vm._v(" J. Neusner refuses to accept any core to ancient Judaism. Rather, he sees fragmentation: a number of Judaisms existing in history, each being a competitor to the others."), _c('a', {
    attrs: {
      "href": "#/#foot-note-769",
      "id": "footnote-769"
    }
  }, [_c('sup', [_vm._v("201")])]), _vm._v(" G. Boccaccini rejects this model because it assumes impermeable systems of Judaism and thus ignores the interaction among Jews in this period."), _c('a', {
    attrs: {
      "href": "#/#foot-note-768",
      "id": "footnote-768"
    }
  }, [_c('sup', [_vm._v("202")])]), _vm._v(" Surely we cannot roll up all of the various aspects of Jewish religion in the Second Temple period into one synthesis, but Ezra-Nehemiah is an early treasure trove of religious elements that make a strong cross-sector impact on various groups of early Judaism and, through them, on Christianity, which was planted in Jewish soil. Scholars are responsible to try to discern the antecedents and prehistory of these groups, and Ezra-Nehemiah provides fertile ground.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("It is a goal of this commentary to highlight the seeds of later Judaism in Ezra-Nehemiah. Several notions either come to light for the first time (e.g., the role of the scribe who teaches the Torah) or take on a new emphasis (e.g., genealogical credentials). To be sure, not all later concepts originate here (e.g., covenant). Many find their origins in preexilic traditions, while other ideas (e.g., belief in an end time, ultimate retribution, and a cosmic Torah) come to fruition much later. Nevertheless, seeds of certain later ideas germinate in Ezra-Nehemiah, and they change the future of Judaism in all of its forms in irrevocable ways. Several of the excurses at the end of each chapter are dedicated to these innovative beliefs and practices.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Four “pillars” of Second Temple Judaism are emphasized in Ezra-Nehemiah, and they continued to undergird the faith and practice of Jews across sectors during this period: (1) Yahweh is the only true God of Israel; (2) Yahweh’s law, the Torah, is authoritative; (3) Jerusalem and its sanctuary is “the place of his holiness” (Ezra 9:8); and (4) the community of Israel is holy (Ezra 9:2). In all four of these areas, there are signs in Ezra-Nehemiah of both heritage and innovation.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("All four of these “pillars” are treated as holy in Second Temple Judaism. In the technical texts of the priestly material of Leviticus and Numbers, “holy” ("), _c('em', [_vm._v("qādôš")]), _vm._v(") stands for God, the Holy One, and by extension those who represent him, the priests of Israel. In the holiness traditions of Leviticus and in Deuteronomy, holiness applies to all of Israel. In Leviticus, it is framed as a commandment (Lev 11:44; 19:2), and in Deuteronomy it is considered an "), _c('em', [_vm._v("a priori")]), _vm._v(" fact of the nation of Israel by virtue of election (Deut 7:6)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-767",
      "id": "footnote-767"
    }
  }, [_c('sup', [_vm._v("203")])]), _vm._v(" The temple and its environs are Yahweh’s personal sanctuary and as such are imbued with his holiness. Even those Jews in Second Temple times who rejected the current value of the temple (e.g., authors of some Dead Sea Scrolls and the book of Hebrews) still had to deal with its centrality in Judaism and reinterpret it.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In Ezra-Nehemiah, three of these “pillars” are explicitly labeled “holy”: Yahweh (Ezra 9:8), Jerusalem and its temple (Ezra 9:8; Neh 11:1, 18), and the people of Israel (Ezra 9:2)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-766",
      "id": "footnote-766"
    }
  }, [_c('sup', [_vm._v("204")])]), _vm._v(" According to priestly definitions, being holy signifies that an item is set apart by Yahweh and belongs to him. As such, it benefits from his protection and blessing but is also governed by certain restrictions."), _c('a', {
    attrs: {
      "href": "#/#foot-note-765",
      "id": "footnote-765"
    }
  }, [_c('sup', [_vm._v("205")])]), _vm._v(" In addition to the three “pillars” listed above, several other things are emphasized as holy in Ezra-Nehemiah. The full list of sancta encompasses persons, space, time, and more: Yahweh (Ezra 9:8), priests (Ezra 8:28), Israel as “holy seed” (Ezra 9:2), Jerusalem (Ezra 9:8, "), _c('em', [_vm._v("māqôm")]), _vm._v("; Neh 11:1, 18), the wall of Jerusalem (Neh 3:1), the Sabbath (Neh 9:14; 13:22), festivals (Ezra 3:5; Neh 8:9–11; 10:32), sacrifices (Ezra 2:63; Neh 7:65; 10:34). Also, Levites are “sanctified, set apart” to assist the priests (Neh 12:47).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-18"
    }
  }, [_vm._v("A. MONOTHEISM")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The book of Ezra-Nehemiah records a transformation in the popular religion of Yehud as a result of the returning exiles under the leadership of Zerubbabel, Ezra, and Nehemiah. Although worship of Yahweh is a tenet of Israelite thought from earliest times (see Exod 20:1–3), most scholars agree that in Second Temple times, the Jewish community, across sects, began to affirm monotheism in a stronger way, even to the denial of the existence of any other god.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Yahweh’s distinctive identity, as opposed to pagans’ worship of their deities, lay primarily in his identity as the sole Creator and Ruler of all things."), _c('a', {
    attrs: {
      "href": "#/#foot-note-764",
      "id": "footnote-764"
    }
  }, [_c('sup', [_vm._v("206")])]), _vm._v(" This belief was affirmed by the recitation of the Shema, an ancient practice still observed twice daily in modern Judaism: “Hear ["), _c('em', [_vm._v("š")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("ma")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em'), _vm._v("], O Israel, Yahweh is our God; Yahweh is one” (Deut 6:4; cf. Exod 20:2–6; Deut 5:6–10; Mark 12:29). The distinctive identity of Yahweh as supreme over all realities separates his worship from pagan notions of the divine, but this is coupled in Israel with adoration of Yahweh because of his goodness, especially his election of Israel."), _c('a', {
    attrs: {
      "href": "#/#foot-note-763",
      "id": "footnote-763"
    }
  }, [_c('sup', [_vm._v("207")])]), _vm._v(" This idea is taken up by Jesus, who includes the Shema in his answer to the Pharisees about which is the greatest commandment (Mark 12:29) and explains that he and the Father are “one” (John 10:30).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra-Nehemiah reflects an early stage in the development of this view of monotheism in Second Temple Judaism. The book emphasizes the sole jurisdiction of Yahweh, the God of Judah, who lives in Jerusalem. Even the Persian emperors confess this to be true and support the restoration of his temple and cult there. When the returnees are approached by the local people in Ezra 4, they are confronted with a mixture of Yahwism and paganism. This explains their rejection of help from the locals and the tension between them. Later, those who do not subscribe to the unadulterated religion of the returnees are excluded from observing the Passover (Ezra 6:19–21), and the community is eventually “purged” of foreign mixture and intermarriage (Neh 13:30).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Indeed, religious syncretism was the main reason for the exile in the first place (Jer 7:18; 25:4–14; Hos 11:2; Judg 10:6; Lev 26:31–33). The punishment that was the conquest of Jerusalem and the Kingdom of Judah and the ensuing exile in a foreign land made it all too apparent that Yahweh, the God of the Jews, would not share worship with any other gods. Whether in the form of golden calves, pagan symbols in the temple, or sacrifices to various Baalim, religious syncretism simply was not compatible with the worship of Yahweh, the God of Abraham, Isaac, and Jacob. Although the prophets had repeated this theme throughout the monarchic period, warning the people of impending destruction, it was now a past reality. In the wake of these events, the worship of Yahweh to the exclusion of all other gods became a central tenet of the returning exiles."), _c('a', {
    attrs: {
      "href": "#/#foot-note-762",
      "id": "footnote-762"
    }
  }, [_c('sup', [_vm._v("208")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Archaeological evidence confirms a radical reduction in the number of figurines in Persian Yehud compared with those in Judah of the earlier period, as well as reduction of images between Yehud and other parts of Palestine. In particular, the Judahite pillar figurine disappears in Yehud in this period."), _c('a', {
    attrs: {
      "href": "#/#foot-note-761",
      "id": "footnote-761"
    }
  }, [_c('sup', [_vm._v("209")])]), _vm._v(" E. Stern remarks that there is “not a single piece of evidence” of any pagan cults in Judah and Samaria in this period, including sanctuaries, figurines, and other pagan cult objects."), _c('a', {
    attrs: {
      "href": "#/#foot-note-760",
      "id": "footnote-760"
    }
  }, [_c('sup', [_vm._v("210")])]), _vm._v(" This is in sharp contrast to other areas of Palestine as well as the earlier, Assyrian and Babylonian, periods of Israel’s history for which pagan cult objects are easily documented. The only exceptions to this rule are apotropaeic cults (e.g., rituals to ward off demons) and some pagan designs on coins, which may simply be borrowed motifs rather than rituals of cultic significance. Apparently, Judeans and Samarians went through a reversal in attitude toward pagan cults in this period.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Furthermore, Ezra-Nehemiah regards the jurisdiction of Yahweh as not just Jerusalem and Judah; Yahweh is not just the Jewish god within the ranks of polytheism but the God who is the eternal Creator and Sustainer of everything. The Levites’ prayer of Neh 9 begins with this invocation:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("“Arise, bless Yahweh, your God who is from everlasting to everlasting. May they bless your glorious name, which is exalted above all blessing and praise. You alone are Yahweh. You made the heavens, the highest heavens, and their entire army; the earth, and all that is on it; the seas, and all that is in them. You give life to everything, and the army of heaven bows down to you.” (Neh 9:5b–6)")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Typical of Jewish worship practice, the prayer then moves on to a recital of Yahweh’s election, nurture, and care of Israel throughout her past (Neh 9:7–35).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("By the Hellenistic period, Judaism’s most defining feature was its fiercely monotheistic stance."), _c('a', {
    attrs: {
      "href": "#/#foot-note-759",
      "id": "footnote-759"
    }
  }, [_c('sup', [_vm._v("211")])]), _vm._v(" One main factor was Antiochus Epiphanes’s coercion with its violent backlash by the Maccabees. In Second Temple Judaism, much thought centered on the heavenly abode of Yahweh and the celestial beings that resided there (e.g., angels), but all of these supernatural beings (even those in the realm of evil, such as fallen angels, demons, and Satan) were under his power. Yahweh’s retinue served to accentuate his greatness like one of the emperors and their courts or even like the myths surrounding pagan deities. However, only the invisible, aniconic Yahweh was worthy of worship. The Roman historian Tacitus reports, “the Jews conceive of one god only, and that with the mind alone” ("), _c('em', [_vm._v("Histories")]), _vm._v(" 5.5, tr. Moore, LCL). Monotheism continues to be a foundational pillar of Jewish identity even after the fracture into various sects in late Second Temple Judaism. Josephus emphasizes the unity both of Israel and her God: “For God is one, and the Hebrew race is one” ("), _c('em', [_vm._v("Antiquities")]), _vm._v(" 4.201, tr. Marcus, LCL).")]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-19"
    }
  }, [_vm._v("B. TORAH")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("“Torah” ("), _c('em', [_vm._v("tôrâ")]), _vm._v("; lit. “instruction, teaching”) refers to the Pentateuch, or the Five Books of Moses, and became an important identifying factor for all forms of Judaism in the Second Temple period. The centrality of the written Torah for the people of Israel in the Second Temple period cannot be overestimated."), _c('a', {
    attrs: {
      "href": "#/#foot-note-758",
      "id": "footnote-758"
    }
  }, [_c('sup', [_vm._v("212")])]), _vm._v(" In fact, when the Maccabees rallied the Jews to fight off their Hellenistic overlords, the battle slogan was, “For the Temple and the Torah!”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-757",
      "id": "footnote-757"
    }
  }, [_c('sup', [_vm._v("213")])]), _vm._v(" The law is central in both Ezra and Nehemiah. Ezra’s teaching becomes pivotal for the community because he taught “the law of Moses, which Yahweh, the God of Israel, had given” (Ezra 7:6; cf. 3:2; Neh 9:3; 13:1). The written word is powerful, and the people realize that they must behave in accordance with it to maintain their relationship with God (e.g., “in accordance with what is written,” Ezra 3:2–4; 6:18; Neh 8:14–15)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-756",
      "id": "footnote-756"
    }
  }, [_c('sup', [_vm._v("214")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The book of Ezra-Nehemiah reflects a shift from earlier traditions that emphasized “seeking Yahweh” ("), _c('em', [_vm._v("lidrôš et-Yahweh")]), _vm._v("; e.g., Gen 25:22). Ancient Israelites would seek Yahweh directly in prayer or through the prophet, his representative. The process of seeking and finding in Ezra-Nehemiah, however, refers at least ten times to the searching out of information in authoritative written records."), _c('a', {
    attrs: {
      "href": "#/#foot-note-755",
      "id": "footnote-755"
    }
  }, [_c('sup', [_vm._v("215")])]), _vm._v(" This emphasis on the written word starts already in Ezra 1 with the edicts of the Persian emperors. Also, priestly validity is established only by supplying written genealogical registers (Ezra 2). Darius orders a search for the actual text of the earlier Persian decree (Ezra 6). This focus on written texts sets the reader up for introducing the Torah, the most important document since it is the Word of God.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Persians seem obsessed with law. The term "), _c('em', [_vm._v("dāt")]), _vm._v(" (“law”) used in late Biblical Hebrew is a Persian loanword that refers to right and order—to everything being in its rightful place (i.e., the natural law). According to Fried, “Laws were collected for scientific and antiquarian purposes. They were also collected and published as abstract instances of justice to reassure the populace that the new king was concerned to install right order in the land.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-754",
      "id": "footnote-754"
    }
  }, [_c('sup', [_vm._v("216")])]), _vm._v(" The main point to the public was that justice and law stemmed from the god Ahura Mazda through the agency of the Persian king.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-14"
    }
  }, [_vm._v("1. Torah for Everyone")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Ezra became a new role model in Judaism of a teacher and scribe of the law. Ezra 7 provides the first record of the development of written Scripture and its interpreters in Second Temple times. Ezra is an “expert scribe” ("), _c('em', [_vm._v("sôpēr māhîr")]), _vm._v(", Ezra 7:6). Other biblical scribes tend to be educated officials and copyists in the royal service (2 Kgs 12:10–12; 18:18, 37; 22:3, 12; Jer 36). Ezra, on the other hand, combines skill, divine wisdom, and knowledge of Jewish law with devotion to the God of Israel. He strives to understand the text in order to observe it and provide guidance to the community. Ezra is no prophet, yet he is able to guide the people, even under a foreign monarch, by his expertise in interpreting the Torah and thus connecting his people to Yahweh through understanding of his word. Divine revelation and guidance in the time of the monarchy tended to come through a prophet to the king on behalf of the nation (see Jeremiah, Isaiah), but in Ezra-Nehemiah, there is a strong emphasis on the democratization of this process. Ezra teaches the people by reading them the Torah (Neh 8:8) with great success. They are excited because they understand what God is saying to them. The elders return for further study on the next day and, a chapter later, laypersons are reading the Torah for themselves (Neh 9:3)!"), _c('a', {
    attrs: {
      "href": "#/#foot-note-753",
      "id": "footnote-753"
    }
  }, [_c('sup', [_vm._v("217")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The importance of studying the Torah with a teacher continued in Second Temple times and became a hallmark of Judaism. Ezra served as the role model for later scribes of this period. Ben Sirach ran a school for boys, teaching them wisdom through the Torah (Sir 38–39). The Dead Sea Scrolls describe students searching for divine wisdom by studying texts (4Q415–418, 423). Members of the Qumran sect organized themselves into shifts for continuous study and copying of the Torah, always in search of further divine revelation (1QS vi 6–8). The early rabbinic sages stated that the Torah was the first pillar of three upon which the world rests: Torah, temple, and deeds of loving-kindness (m. "), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ʾ")]), _vm._v("Abot 1:1). Torah learning was handed down from teacher to student in a sort of academic genealogy that was honored and recorded. A student’s words were often evaluated based on his teacher. Today, Jews are still sometimes referred to as “the people of the book.”")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Early Christianity, beginning as a Jewish sect, continued this reverence for Scripture (see 2 Tim 3:16) but also began to describe the person of Jesus Christ as “the word becoming flesh” (John 1:14). The idea, according to John, is that God’s word becomes incarnate in the life of Jesus, who fulfills its expectations perfectly among his people and ultimately connects them to the Father by his atoning death and resurrection.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-15"
    }
  }, [_vm._v("2. Heritage: What Was Ezra’s Torah?")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Some form of the Pentateuch was available to Ezra. Blenkinsopp has traced a number of traditions from the books of the Pentateuch that are evident in Ezra-Nehemiah, and I have added to this list in the table on "), _c('a', {
    attrs: {
      "href": "#/#tab1"
    }
  }, [_vm._v("pp. 70–71")]), _vm._v(". What was Ezra’s book of the law? The whole Pentateuch? Random legal material? Some form of Deuteronomy? Blenkinsopp opts for a Deuteronomic legal core supplemented by priestly traditions."), _c('a', {
    attrs: {
      "href": "#/#foot-note-752",
      "id": "footnote-752"
    }
  }, [_c('sup', [_vm._v("218")])]), _vm._v(" Others claim that the Pentateuch was in the process of compilation during the time of Ezra and Nehemiah and that some of it was written in response to their exclusionary attitude."), _c('a', {
    attrs: {
      "href": "#/#foot-note-751",
      "id": "footnote-751"
    }
  }, [_c('sup', [_vm._v("219")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In my view, the Pentateuch was known to the community in some form. First, the people accepted the authority of the law immediately; no convincing seems to have been necessary. And as Derek Kidner points out, Malachi (usually dated between 450 and 425 BCE) places the trouble with a priesthood who knew the law but did not teach or observe it faithfully (Mal 2:6–9): “Ezra’s law book … owed its power not to its being new, but precisely to its being old and rediscovered, brought and expounded to the whole people, and treated as an authority which judged the very priests themselves (cf. e.g., Ezra 9:1ff.; 10:1ff.; Neh 8:1ff.).”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-750",
      "id": "footnote-750"
    }
  }, [_c('sup', [_vm._v("220")])]), _vm._v(" Indeed, the law was known and practiced before the fall of Jerusalem because the prophets held the people responsible for it and explained that it was disobedience to the law that had landed them in the unfortunate state of exile.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The authors are familiar with all parts of the Pentateuch."), _c('a', {
    attrs: {
      "href": "#/#foot-note-749",
      "id": "footnote-749"
    }
  }, [_c('sup', [_vm._v("221")])]), _vm._v(" Scholars have demonstrated that Ezra-Nehemiah is working with an established legal tradition that reaches back into the Pentateuch. The author refers several times to practices that are “written in the law” (Ezra 3:2; Neh 8:15; 10:34, 36; cf. Neh 13:1, the "), _c('em', [_vm._v("Book of Moses")]), _vm._v("). Prescriptions for the offerings are understood in detail: for example, the guilt offering, which is brought for sacrilege (Ezra 10:19) and the various needs of the temple cult (see Neh 10:31–39)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-748",
      "id": "footnote-748"
    }
  }, [_c('sup', [_vm._v("222")])]), _vm._v(" The writer understands the defiling threat of illicit sexual relations and describes it as an abomination and pollution using language from Lev 18 (Ezra 9:10–11; cf. Lev 18:24–27). Regarding the laws of Sukkot, Ezra-Nehemiah “facilitates” the text, to use Clines’s term, by specifying what materials and fruit are to be included in the observance of the festival (Neh 8:13–18; cf. Lev 23:40)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-747",
      "id": "footnote-747"
    }
  }, [_c('sup', [_vm._v("223")])]), _vm._v(" Nehemiah 5 may reflect knowledge of the Jubilee laws of Lev 25, according to which property must be returned to its original clan (see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03a#exc15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 15, “The Jubilee and Sabbatical Years,” pp. 328–33")]), _vm._v("). Thus, although scholars argue over the date of Leviticus, it appears that the writers of Ezra-Nehemiah were familiar with some form of the work.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The author of Ezra-Nehemiah also cites several passages from Deuteronomy. The intermarriage prohibition is prominent in this regard (Deut 7:3//Ezra 9:2; Neh 10:31[32]; 13:25; Deut 23:4–5[3–4]//Neh 13:1–2). Several cultic traditions in Ezra-Nehemiah are influenced by Deuteronomy, including sanctifying the Sabbath and Sabbatical Year, setting aside firstlings and firstfruits, refusing interest on loans, and observing sacred forfeiture (the table on "), _c('a', {
    attrs: {
      "href": "#/#tab1"
    }
  }, [_vm._v("pp. 70–71")]), _vm._v("). Even the divine promise to return a repentant Israel to her land even though she may be scattered among the nations (Deut 30:1–5) resonates in Nehemiah’s prayer (Neh 1:8–9). Titus Reinmuth demonstrates an especially close connection between Ezra-Nehemiah and Deuteronomy 31:9–13, which treats several of the topics found in Nehemiah, including debt relief, Torah reading, and the Festival of Sukkot."), _c('a', {
    attrs: {
      "href": "#/#foot-note-746",
      "id": "footnote-746"
    }
  }, [_c('sup', [_vm._v("224")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Not only is the redactor of Ezra-Nehemiah familiar with the general laws and practices of the Pentateuch, but he uses specific terminology from both priestly and Deuteronomic traditions."), _c('a', {
    attrs: {
      "href": "#/#foot-note-745",
      "id": "footnote-745"
    }
  }, [_c('sup', [_vm._v("225")])]), _vm._v(" Ezra 9:10–11, for example, refers to impure "), _c('em', [_vm._v("abominable practices")]), _vm._v(" ("), _c('em', [_vm._v("tô")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("ēbôt")]), _vm._v(") of the local population that are denounced in earlier traditions. These probably refer to a catalog of sexual sins found in Lev 18 that defile sinners irrevocably and are punishable by expulsion and extirpation (Lev 18:24–30). The author of Ezra-Nehemiah uses the root "), _c('em', [_vm._v("bādal")]), _vm._v(" to emphasize the need to “separate” from impure outsiders (Ezra 9:1; 10:11; Neh 9:2; 10:28). The original priestly mandate to distinguish between “pure and impure” and “holy and profane” comes from Leviticus (see Lev 10:10; 11:44–47; 20:25–26). According to Leviticus, the very purpose of Israel’s purity system is to reflect and reinforce her separation from other people:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("I, Yahweh, am your God who has set you apart ["), _c('em', [_vm._v("bādal")]), _vm._v("] from other peoples. So you shall set apart ["), _c('em', [_vm._v("bādal")]), _vm._v("] the clean beast from the unclean, the unclean bird from the clean…. You shall be holy to me, for I, Yahweh, am holy, and I have set you apart ["), _c('em', [_vm._v("bādal")]), _vm._v("] from other peoples to be mine. (Lev 20:24–26)")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Thus, Israel is to eat by a unique standard of food laws so that she will not share meals with her pagan neighbors. In this way, the law limits social contact between Israel and her neighbors and reduces the possibility that she will be entrapped by intermarriage with idolaters (see Exod 34:15–16; Num 16:21).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The notion that Leviticus in some form stands behind Ezra-Nehemiah is evident in other terms as well. While "), _c('em', [_vm._v("niddâ")]), _vm._v(" refers only to menstrual impurity in Leviticus with one exception for incest (Lev 20:21), Ezra-Nehemiah uses the term more broadly in the phrases “impure land” and “impurity of the peoples of the land” (Ezra 9:11). Ezra-Nehemiah follows exilic and postexilic usage in using "), _c('em', [_vm._v("niddâ")]), _vm._v(" as a metaphor for sin (Ezek 7:19–20; Lam 1:17; 2 Chr 29:5). Additionally, the root "), _c('em', [_vm._v("gā")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("al")]), _vm._v(" (with "), _c('em', [_vm._v("aleph")]), _vm._v(": “nauseating, revolting, defiling”) is introduced to describe the defilement of the priesthood by intermarriage (Neh 13:29). This root is a later form of "), _c('em', [_vm._v("gā")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("al")]), _vm._v(" (with "), _c('em', [_vm._v("ayin")]), _vm._v("), a pollution term found in Leviticus (e.g., Lev 26:11, 43)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-744",
      "id": "footnote-744"
    }
  }, [_c('sup', [_vm._v("226")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("So who is borrowing from whom? Mary Douglas argues that Leviticus is a response to the harsh decrees of Ezra-Nehemiah toward outsiders."), _c('a', {
    attrs: {
      "href": "#/#foot-note-743",
      "id": "footnote-743"
    }
  }, [_c('sup', [_vm._v("227")])]), _vm._v(" I argue the opposite. The answer lies in the language itself, with many technical cultic terms in Ezra-Nehemiah assumed to be common knowledge and the concepts expanded in new ways. The term "), _c('em', [_vm._v("ma")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("al")]), _vm._v(" has moved from the “sacrilege” of holy items to the desecration of all Jewish persons. The label of "), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ṭ")]), _c('em', [_vm._v("um")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("â")]), _vm._v(" (“impurity”) refers not only to a status (usually temporary) antithetic to the sacred but also permanently labels those outside of the community of Israel. The scope of separation indicated by "), _c('em', [_vm._v("bādal")]), _vm._v(" extends to these ethnically unauthorized persons. A term from earlier traditions, "), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("am hā")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("ārets")]), _vm._v(" or "), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("ammê hā")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("ărātsôt")]), _vm._v(" (“the people of the land[s]”), which referred to the population of Israel who supported the traditions and laws of the cult (Lev 4:27; 20:2, 4), now refers derogatorily to outsiders (Ezra 4:4; 9:1, 11). Ezra-Nehemiah employs the term "), _c('em', [_vm._v("niddâ")]), _vm._v(", which in Leviticus refers to menstrual impurity, as a metaphor to illustrate moral impurity. As noted above, the term "), _c('em', [_vm._v("gā")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("al")]), _vm._v(" (“nauseating, revolting, defiling”) is employed to increase the disgust for the genealogical pollution of the priesthood.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Mary Douglas argues that the progressive notions of Leviticus about impurity follow Ezra-Nehemiah rather than precede them. However, if this were the case, why is there no overt polemic in Leviticus and instead a neutral sense of teaching the particulars of the laws of purity and pollution (see Lev 11:1–2; 12:1–2; 14:1–2; 15:1–2)? Also, it makes more sense to see the generous attitude in Leviticus to the "), _c('em', [_vm._v("gēr")]), _vm._v(" as having roots in an Israel from preexilic times that is in charge of its own destiny and land, no matter when one dates its final redaction. Ezra-Nehemiah, on the other hand, reveals the constraints of invasion, deportation, and return as a threatened minority group.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Although when the final form of the Pentateuch was redacted remains a matter of debate—whether during, before, or after the period of Ezra-Nehemiah—it is clear that Ezra-Nehemiah draws on earlier Pentateuchal traditions. Indeed, the law was a force throughout the history of ancient Israel from earliest times, but it was often neglected."), _c('a', {
    attrs: {
      "href": "#/#foot-note-742",
      "id": "footnote-742"
    }
  }, [_c('sup', [_vm._v("228")])]), _vm._v(" At times, new laws and interpretations are put forward in Ezra-Nehemiah. For example, the tithing laws (see Num 18:21) assume a 1-10 ratio of priests to Levites, which is contradicted by the lack of Levites in Ezra-Nehemiah, the ratio being closer to 12-1. Thus, the priestly tithing regulations must precede the time of Ezra-Nehemiah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-741",
      "id": "footnote-741"
    }
  }, [_c('sup', [_vm._v("229")])]), _vm._v(" Intermarriage is allowed in the earlier Pentateuchal period. Moses marries a Midianite and allows his soldiers to do so as well (Exod 2:1–21; Num 31:18; Deut 21:10–14). The Deuteronomic laws are clearly known to the author of Ezra-Nehemiah (see Ezra 9:1–2; Deut 7:1–6; 23:1–9), but they are applied in more stringent terms.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Nevertheless, some scholars challenge the undergirding of the Pentateuch in Ezra-Nehemiah based on a “paucity of evidence that Ezra’s law bears close resemblance to the law now in Ex, Lev and Deut.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-740",
      "id": "footnote-740"
    }
  }, [_c('sup', [_vm._v("230")])]), _vm._v(" One major contention is that the fast of Yom Kippur is not mentioned, although there is a strong emphasis on the other sacred assemblies of the seventh month.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("To be sure, the process of the formation of the Pentateuchal text was ongoing to some degree during Ezra’s time, as is apparent in the differences in text types (i.e., LXX, the Samaritan Pentateuch, and the proto-MT found among the Dead Sea Scrolls). For example, early Greek versions of antediluvian chronology (Gen 5) and the tabernacle narratives in LXX differ markedly from those in MT."), _c('a', {
    attrs: {
      "href": "#/#foot-note-739",
      "id": "footnote-739"
    }
  }, [_c('sup', [_vm._v("231")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Even if the new code of Ezra is the entire Pentateuch, this does not mean the end of interpretation. Quite the contrary, the system of laws provided in the Pentateuch cannot be understood let alone implemented without considerable interpretation and oral tradition."), _c('a', {
    attrs: {
      "href": "#/#foot-note-738",
      "id": "footnote-738"
    }
  }, [_c('sup', [_vm._v("232")])]), _vm._v(" Furthermore, there were competing notions of how to observe the law: Sabbath restrictions that Nehemiah implemented were stricter than the prevailing interpretation, and Nehemiah’s covenant renewal ceremony probably created some new laws (see below)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-737",
      "id": "footnote-737"
    }
  }, [_c('sup', [_vm._v("233")])]), _vm._v(" Also, Ezra’s new definition of intermarriage, which required the exclusion of all foreign wives, qualifies as a new law.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Another point to consider is that while laws may have ancient origins, their codification is often much later. Thus, the book of the Torah in the form we have it may have been compiled in the Persian period, but its laws represent an ongoing sacred tradition of interpretation and practice reaching further back into antiquity. It is probably correct to say with Albertz and Becking that the end of the monarchy and temple, as well as the subsequent return and restoration, “started a process of reflection and reformulation of Israel’s religious tradition ending in the re-edition—and in some cases first edition—of religious texts.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-736",
      "id": "footnote-736"
    }
  }, [_c('sup', [_vm._v("234")])]), _vm._v(" As Jaffee puts it, “The literary strands of the Hebrew Bible were contributed in large measure by ancient Israel, but the weaving was done on a Persian loom.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-735",
      "id": "footnote-735"
    }
  }, [_c('sup', [_vm._v("235")])])]), _vm._v(" "), _c('p', {
    staticClass: "table_title",
    attrs: {
      "id": "tab1"
    }
  }, [_vm._v("Pentateuchal Traditions Reflected in Ezra-Nehemiah"), _c('a', {
    attrs: {
      "href": "#/#foot-note-734",
      "id": "footnote-734"
    }
  }, [_c('sup', [_vm._v("236")])])]), _vm._v(" "), _c('table', [_c('tbody', [_c('tr', [_c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Tradition")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Pentateuch")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Ezra-Nehemiah")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Holy people; no intermarriage with local Canaanites")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Deut 7:3")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 9:2; Neh 10:30(31); 13:25")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Prohibition of Ammonites/Moabites in the assembly")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Deut 23:4–5(3–4)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 13:1–2")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Divine promise to return penitent Israelites from diaspora")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Deut 30:1–5 paraphrase")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 1:8–9")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Constructing booths for the Festival of Sukkot")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 23:42–43")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 8:14–15")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Solemn assembly on Sukkot, day 8")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 23:36, 39")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 8:14–15")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Festival sacrifices")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Num 29:12–38")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 3:4")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Passover and Feast of Unleavened Bread; participation regulations regarding strangers and ritual purification")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Exod 12:43–49; Lev 23:4–5; Num 9:1–14")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 6:19–22; Neh 12:30")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Annual temple tax")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Exod 30:11–16 (half shekel)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 10:32(33)")]), _vm._v(" "), _c('p', {
    staticClass: "t-left"
  }, [_vm._v("(one-third shekel)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Purification of rooms")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 14:34–45; Num 19:18")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 13:8–9")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Daily burnt offering")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Exod 29:38–42; Num 28:3–8")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 3:3, 5")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Sanctifying the Sabbath")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Exod 16:23; 20:8–11; 31:14; Deut 5:12–15")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 13:15–22")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Tithes for priests and Levites")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Num 18:25–32")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 10:37–38(38–39)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Firstlings and firstfruits")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 23:14; Num 18:15–16; Deut 15:19–20; 26:1–4")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 10:35–37(36–38)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("No lending at interest")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 25:36–37; Deut 23:19–20")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 5:7–13")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Sabbatical Year brings debt release and fallow land")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Deut 15:1–3; Lev 25:1–7, 18–24")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 10:31b(32b)")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Confessing sins ("), _c('em', [_vm._v("hitwaddâ")]), _vm._v("), as a ritual act in the atonement process")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 5:5; 16:20–22; 26:40; Num 5:7")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 10:1; Neh 1:6; 9:2–3")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Separating ("), _c('em', [_vm._v("bādal")]), _vm._v(") from impurity")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 10:10; 11:44–47; 20:25–26")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 6:21; Neh 9:2; 13:3")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Sacrilege ("), _c('em', [_vm._v("ma")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("al")]), _vm._v(")")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 5:15–26; 26:40; Num 5:6")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 9:2, 4; 10:2, 10; Neh 13:27")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Guilt offering ("), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("āšām")]), _vm._v(")")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 5:15–26; 7:1; 19:20–22")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 10:10")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Sacred forfeiture ("), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _c('em', [_vm._v("erem")]), _vm._v(")")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 27:28; Deut 13:16–17; 20:16–17")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 10:8")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Abominable practices ("), _c('em', [_vm._v("tô")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("ēbôt")]), _vm._v(")")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Lev 18:24–30")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 9:11, 14")])])])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec3",
    attrs: {
      "id": "sec3-16"
    }
  }, [_vm._v("3. Legacy")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Ezra-Nehemiah not only reflects a heritage from the Torah, but it represents an ongoing, innovative tradition of interpretation. The people not only come to appreciate the Torah, but they observe it in new ways as they face new challenges. As Eskenazi points out, Ezra-Nehemiah transforms most of its inherited material and traditions. In fact, that is often a main reason for producing any new text; observing Torah is both a receptive and a creative process."), _c('a', {
    attrs: {
      "href": "#/#foot-note-733",
      "id": "footnote-733"
    }
  }, [_c('sup', [_vm._v("237")])]), _vm._v(" For example, Ezra-Nehemiah’s attitude to outsiders is more stringent than Torah law, but it is in keeping with a new social context—one over which the Jews have little control. The threat of idolatry and syncretism, which are strong Pentateuchal concerns and had landed the Jews in exile, must be avoided at all costs, and thus greater stricture in the areas of marriage and outsiders was enforced. The law does not remain static but is engaged to meet challenges unprecedented in Israelite history.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("There are strong links between the legal innovations of Ezra-Nehemiah and the development of Jewish law in later Judaism. Schiffman speaks of “extrapolating forward” the laws of Ezra-Nehemiah and other biblical Second Temple texts as well as attempting to “extrapolate back” traditions enmeshed in later Second Temple texts and the Mishnah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-732",
      "id": "footnote-732"
    }
  }, [_c('sup', [_vm._v("238")])]), _vm._v(" Complicating the process is the fact that the law was not a simple chronological development in this period but was interpreted differently by competing groups of Jewish sages and priests. To be sure, the Torah was not the sole influence in the development of these new ideas: some simply reflect ancient custom (one-third shekel), others are new applications of the Torah in response to current issues (intermarriage, genealogical purity), and some refer to the treatment and method of interpretation of the Torah itself (midrash, synagogue practice). Moreover, some innovations are simply a matter of emphasis rather than new practices (Sabbath, correct priesthood). The following are nine areas of Jewish religion and law apparent in Ezra-Nehemiah that represent new directions in Second Temple Judaism."), _c('a', {
    attrs: {
      "href": "#/#foot-note-731",
      "id": "footnote-731"
    }
  }, [_c('sup', [_vm._v("239")])]), _vm._v(" Several points of ideology go on to become standard practice among all Jews, while others represent pivotal issues separating Jews from each other. The advent of Hellenism often exacerbated this tension.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("1. "), _c('em', [_vm._v("Intermarriage")]), _vm._v(". Through a stringent interpretation of Torah law, Ezra and Nehemiah both forbid intermarriage with non-Jews and even consider the latter impure (Ezra 9:1–2; Neh 10:31[32]). Deuteronomy forbids Israel to intermarry with the seven Canaanite nations and even requires their complete annihilation because of their pagan influence on Israel (Deut 7:1–6; cf. Exod 34:15–16). However, the Torah also allows for inclusion of the resident alien (see Deut 16:14; 29:11) and marriage with a foreign captive bride through a process of inculturation (Deut 21:10–14; cf. the marriage of Moses to a foreigner, Num 12:1). These texts were undoubtedly known to Ezra and Nehemiah, but they chose to ignore this flexibility. Instead, they ban all marriages with non-Jews on both religious and genealogical grounds, giving no recourse to a gentile who wishes to take up residence with the community and commit to its religious practice. By sending away foreign wives along with their children, Ezra-Nehemiah also supply the earliest source for the Jewish law that the ethnicity of a child is based on the race of its mother."), _c('a', {
    attrs: {
      "href": "#/#foot-note-730",
      "id": "footnote-730"
    }
  }, [_c('sup', [_vm._v("240")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Several other Jewish texts in Second Temple times follow Ezra-Nehemiah by banning intermarriage. Aramaic Levi (ca. 300 BCE), Jubilees (ca. 150 BCE), and several other texts found among the Dead Sea Scrolls (e.g., 4QMMT) refer to intermarriage as "), _c('em', [_vm._v("z")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("nût")]), _vm._v(", sexual impurity."), _c('a', {
    attrs: {
      "href": "#/#foot-note-729",
      "id": "footnote-729"
    }
  }, [_c('sup', [_vm._v("241")])]), _vm._v(" Paul warns against intermarriage based on religious but not ethnic concerns. He exhorts the Corinthian church not to be “unequally yoked with unbelievers” (2 Cor 6:14 ESV).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("2. "), _c('em', [_vm._v("Temple practice")]), _vm._v(". Several practices connected to the temple cult known to be critical to later Judaism are found in Ezra-Nehemiah. Some of these are not mentioned in the Pentateuch, but Ezra-Nehemiah confirms their early antiquity. For example, the rabbis consider a donation of one-third shekel annually to the temple to be legally binding, and they also give evidence of a collection of wood for the altar. Furthermore, the rotation of shifts of priests and Levites is not spelled out in the Pentateuch, but it is in effect in Ezra-Nehemiah (see the commentary on Ezra 6:18; Neh 12:9). Several cultic practices that are described in the Torah and present in Ezra-Nehemiah continue to be obligatory even after the demise of the temple. These include firstfruits offerings, the redemption of the firstborn, the temple tax, eating of firstborn male animals by priests, an agricultural offering for the priests ("), _c('em', [_vm._v("t")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("rûmâ")]), _vm._v("), the dough offering ("), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _c('em', [_vm._v("allâ")]), _vm._v("), and tithes for priests and Levites (see Neh 10:35–40)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-728",
      "id": "footnote-728"
    }
  }, [_c('sup', [_vm._v("242")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In addition, the pilgrimage festivals, which hold a prominent role in Ezra-Nehemiah (e.g., Passover), continue in Judaism to this day. While these celebrations stem from the Torah, they take on new details of interpretation and customary practice throughout Second Temple Judaism. This process is evident in Nehemiah 8:13–15 (see the commentary there) regarding the Festival of Sukkot, which provides a biblical example of interpretation of Lev 23:40–42.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("3. "), _c('em', [_vm._v("Sabbath")]), _vm._v(". The effort to stop others from doing business on the Sabbath becomes a key issue in Second Temple Judaism. Nehemiah raises the bar on Sabbath observance when he bans foreigners from trading in a market on the outskirts of Jerusalem and even locks the door of the holy city (Neh 13:15–22). This assumes the addition of Isa 58:13 and Jer 17:21–22, 24, 27 to the Torah Sabbath prohibitions. This attitude is also reflected in the Damascus Document from Qumran, but rabbinic law considers this interpretation a non-biblical injunction (see CD x 14–xii 6)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-727",
      "id": "footnote-727"
    }
  }, [_c('sup', [_vm._v("243")])]), _vm._v(" Sabbath law becomes a marker in Second Temple Judaism dividing observant Jews from those who would compromise due to Hellenistic pressure.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("4. "), _c('em', [_vm._v("Torah reading and interpretation")]), _vm._v(". Nehemiah 8–10, with its public Torah reading and covenant renewal ceremony, provides the model for later Torah study and ritual. Ezra reads the Torah to the assembly supported by other leaders of the community. Then the Levites help him explain the text to the people so that they can understand its meaning (Neh 8:7–8).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra-Nehemiah records the earliest mention of the root "), _c('em', [_vm._v("dāraš")]), _vm._v(" in the sense of studying and interpreting the Torah (Ezra 7:10). According to later usages, the noun "), _c('em', [_vm._v("midrash")]), _vm._v(" refers to interpreting a passage of the Torah in light of another passage. In this way, the rabbis were able to harmonize Torah passages that seem to be disparate using linguistic connections to other biblical passages, and this became the dominant method of rabbinic exegesis.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Although Ezra-Nehemiah does not refer to any specific interpretation of the Torah as midrash, it gives some evidence of this process in embryo. For example, J. Milgrom argues that Ezra has “spun a midrash” between Deuteronomy’s description of Israel as “a holy people” and Jer 2:3, which uses the priestly doctrine of sacrilege metaphorically to invoke destruction on Israel’s enemies (i.e., by their invasion, they have desecrated a sanctum, Israel, and must be punished)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-726",
      "id": "footnote-726"
    }
  }, [_c('sup', [_vm._v("244")])]), _vm._v(" Ezra-Nehemiah redirects the metaphor against Israel, who has desecrated and endangered herself by intermarriage with unholy outsiders. Israel’s holy status was conferred on her by Yahweh at Sinai, but, according to Ezra 9:1–2, this status was now breached and the “holy seed” profaned by marriage with “unholy” gentiles.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Although this style of studying the Bible would become the dominant form of exegesis in rabbinic literature, it is already evident at Qumran with less sophistication and without reference to the formal types of techniques known later."), _c('a', {
    attrs: {
      "href": "#/#foot-note-725",
      "id": "footnote-725"
    }
  }, [_c('sup', [_vm._v("245")])]), _vm._v(" Among the Dead Sea Scrolls, the term "), _c('em', [_vm._v("midrash")]), _vm._v(" can carry the general meaning of inquiry (1QS vi 24); studying and interpreting the Torah (1QS viii 15, 26; 4Q270 7 ii 15; cf. 4QpNah i 3–4); and even the more particular method of interpreting one passage of Scripture in light of another (4Q174 i 14).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("5. "), _c('em', [_vm._v("Ransom of captives")]), _vm._v(". The Jewish duty to buy back other Jews who have been sold to foreigners or otherwise been forcibly detained by them is called "), _c('em', [_vm._v("pidyôn š")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("bûyîm")]), _vm._v(" (“the ransom of the captives,” m. Ketub. 4:8–9; m. Gi"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ṭ")]), _vm._v(". 4:6, 9; b. B. Bat. 8ab). The requirement begins in Leviticus, in which family members are obliged to redeem those of the clan who have fallen into foreign hands (Lev 25:47–55), but Nehemiah interprets the requirement as a duty of not only the clan but the entire Jewish community (Neh 5:5–12; see the commentary there). Further evidence of this cultural norm in Second Temple times comes from King Herod, who considered selling Jewish criminals to foreigners but was told that this action would only increase the Jews’ hatred of him (Josephus, "), _c('em', [_vm._v("Antiquities")]), _vm._v(" 16.1–4).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("6. "), _c('em', [_vm._v("Synagogue practice")]), _vm._v(". The synagogue practice of later Judaism is reflected to some degree as early as Ezra-Nehemiah. In fact, several aspects of synagogue practice known in the modern world are reflected here for the first time together: (1) the assembly of the people; (2) the request for reading the Torah; (3) the opening of the scroll while the people stand; (4) Ezra’s blessing; (5) the congregation’s response with “Amen”; (6) sermon instruction; (7) Torah reading; (8) oral explanation and exhortation; and a (9) fellowship meal (Neh 8:4–7)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-724",
      "id": "footnote-724"
    }
  }, [_c('sup', [_vm._v("246")])]), _vm._v(" It is hard to know when public Torah reading became fully institutionalized, but by the first century CE, it was a common practice among Jews to hold weekly sessions to read and study the Torah."), _c('a', {
    attrs: {
      "href": "#/#foot-note-723",
      "id": "footnote-723"
    }
  }, [_c('sup', [_vm._v("247")])]), _vm._v(" It was unique in the ancient world for an entire community to devote itself to reading a sacred text as an act of worship. Also, lifting one’s hands in prayer is an ancient Jewish custom that expresses worship to God and demonstrates need (see Pss 28:2; 134:2; 1 Tim 2:8), and one could add the use of a raised platform or lectern for the Torah reader."), _c('a', {
    attrs: {
      "href": "#/#foot-note-722",
      "id": "footnote-722"
    }
  }, [_c('sup', [_vm._v("248")])]), _vm._v(" Even the practice of assembling the people at the “gate,” a location outside the temple precincts, for Torah study (see Neh 8:1) may be a precursor to synagogue practice."), _c('a', {
    attrs: {
      "href": "#/#foot-note-721",
      "id": "footnote-721"
    }
  }, [_c('sup', [_vm._v("249")])]), _vm._v(" Many of these elements have found their way in various forms into Christian ecclesiastical practice as well. This is unsurprising, since the earliest church assemblies consisted of Jewish Christians.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("7. "), _c('em', [_vm._v("Priesthood")]), _vm._v(". The tension between lay authorities (scribe, governor) and priests is prominent in Ezra-Nehemiah. Ezra, who is not the high priest, wields authority over all the priests in the matter of intermarriage, and he is challenged by some (Ezra 10:15). Nehemiah takes upon himself to purge the priesthood from foreign mixture as well, albeit in opposition to the measures of the ruling high priest (Neh 13:4–5, 28, 30). The emphasis on the correct genealogy of the priesthood with supporting credentials becomes an issue in Ezra-Nehemiah (Ezra 2:62–63; Neh 7:64–65) that continues in Second Temple Judaism. One text fragment from Qumran records the case of a priest who had been taken captive into a gentile area; upon his return, he was no longer accepted to serve as a priest (4Q266 5 ii 5–7).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The corruption of the priesthood noted by Nehemiah had a legacy. The high priest’s office was often granted or sold to the highest bidder in the Second Temple period. According to Josephus ("), _c('em', [_vm._v("Antiquities")]), _vm._v(", book 11), the governor Bagohi (= Bagoses), perhaps Nehemiah’s successor, offered the high priesthood to a friend, Jeshua, who was the brother of the reigning high priest, Johanan. This scandal ended in the murder of Jeshua within the temple precincts. Another story is told of Manasseh, brother of the high priest Jaddua, whose marriage to the daughter of Sanballat, governor of Samaria, caused a scandal. The elders of Jerusalem forced him to choose between his priesthood and his wife. Manasseh rejected the elders and joined Sanballat, who offered to build him a temple on Mt. Gerizim, where he could be the priest in charge. Whether or not these stories are true, the notion that the priesthood’s genealogy must be protected from intermarriage became more pronounced as the Jews moved from the time of Ezra into the Hellenistic period."), _c('a', {
    attrs: {
      "href": "#/#foot-note-720",
      "id": "footnote-720"
    }
  }, [_c('sup', [_vm._v("250")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("8. "), _c('em', [_vm._v("Holiness")]), _vm._v(". The expansion of the label of holiness in physical ways beyond the priests and the temple is evident in Ezra-Nehemiah. This is most apparent with regard to the holy people (see "), _c('span', {
    attrs: {
      "href": "#/012_Chapter02c#exc9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 9, “The Sacrilege of Intermarriage,” pp. 244–54")]), _vm._v(") and the city of Jerusalem (see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03b#exc21",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 21, “Jerusalem, the Holy City,” pp. 428–38")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("9. "), _c('em', [_vm._v("Purity")]), _vm._v(". Although not a major theme in Ezra-Nehemiah, ritual purification does surface in a few places. Jews must purify themselves before participating in the sacred festivals (Ezra 6:21; Neh 13:9). Even the wall and gates are ritually purified as part of its dedication (Neh 12:30).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The "), _c('em', [_vm._v("gēr")]), _vm._v(" (a gentile who wishes to live among the community and subscribe to its regulations) and even some Jews are excluded and referred to as impure. Nowhere in the Pentateuch are gentiles viewed as a source of pollution specifically because they are gentiles."), _c('a', {
    attrs: {
      "href": "#/#foot-note-719",
      "id": "footnote-719"
    }
  }, [_c('sup', [_vm._v("251")])]), _vm._v(" Like Leviticus, Ezra-Nehemiah uses purity rhetoric for social ends, but unlike Leviticus, Ezra-Nehemiah does not allow for permeability in this boundary."), _c('a', {
    attrs: {
      "href": "#/#foot-note-718",
      "id": "footnote-718"
    }
  }, [_c('sup', [_vm._v("252")])]), _vm._v(" Building on the Levitical concept that Yahweh wants a distinction between his people and others (see Lev 20:24–26), Ezra-Nehemiah moves to labeling those others as impure and separating them."), _c('a', {
    attrs: {
      "href": "#/#foot-note-717",
      "id": "footnote-717"
    }
  }, [_c('sup', [_vm._v("253")])]), _vm._v(" In contrast to earlier law, Nehemiah finds it imperative to purify rooms in the temple occupied by a man of mixed ancestry, Tobiah the Ammonite (cf. Neh 4:3; 13:9). While this man is a Yahwist, he is not accepted into the group, and his residence in the temple precincts, although apparently sanctioned by the reigning high priest, is considered a pollution by Nehemiah.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Despite the universalist vision of the prophets for the end times (e.g., Isa 49:6), the application of ritual impurity to outsiders, which is attested as early as Ezra-Nehemiah, continues in many Second Temple texts (e.g., 2 Macc 5:27; Mal 2:11–12)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-716",
      "id": "footnote-716"
    }
  }, [_c('sup', [_vm._v("254")])]), _vm._v(" In the same vein, the New Testament too indicates that many Jews considered it necessary to wash themselves after contact with gentiles (Acts 10:28; cf. Mark 7:3–4). Josephus in fact points out that accusations regarding impure food or violating the Sabbath were a prominent way of ostracizing fellow Jews from the community ("), _c('em', [_vm._v("Antiquities")]), _vm._v(" 11.346–347).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The idea that even Jews can be labeled with ritual impurity because they do not follow a community’s interpretation of the Torah is brought to its most extreme expression in the Dead Sea Scrolls, where not only certain bodily conditions but also moral violations bring about ritual impurity."), _c('a', {
    attrs: {
      "href": "#/#foot-note-715",
      "id": "footnote-715"
    }
  }, [_c('sup', [_vm._v("255")])]), _vm._v(" The Community Rule states that new members are purified by their humble repentance as well as the cleansing waters (1QS iii 6–9). According to the Damascus Document, the word of a transgressor is not believed until the person has been ritually purified (CD x 2). Violators of any of the sect’s laws become impure and are excluded from handling or eating the “purity” ("), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ṭ")]), _c('em', [_vm._v("ohŏrâ")]), _vm._v("; i.e., ritually pure items, especially food), lest they pollute it (1QS v 14).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("According to several scrolls, gentile impurity is ritually contagious to persons and food. Initiates to the sect must undergo a two-year trial period before being fully accepted to the communal table (1QS vi 17–21; cf. “three years” in Josephus, "), _c('em', [_vm._v("War")]), _vm._v(" 2.138). The phrase “impurity of the gentiles,” reminiscent of Ezra-Nehemiah, is applied in 4Q266 to priests who have returned from captivity among gentiles and so may not enter the inner sanctuary because they have been defiled (4Q266 5 ii 7)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-714",
      "id": "footnote-714"
    }
  }, [_c('sup', [_vm._v("256")])]), _vm._v(" The Temple Scroll author forbids a woman taken as a captive bride (Deut 21:10–14) to eat her Jewish husband’s pure food for seven years (11Q19 lxiii 14–15). (For further discussion, see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03c#exc23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 23, “Gentile Impurity,” pp. 469–77.")]), _vm._v(")")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The table below outlines traditions that are in Ezra-Nehemiah but not evident in the Pentateuch and gives references to the continuity of these notions in later Jewish literature. Further discussion and references are included in several of the notes and excurses throughout the commentary.")]), _vm._v(" "), _c('p', {
    staticClass: "table_title"
  }, [_vm._v("Traditions Reflected in Ezra-Nehemiah and in Other Second Temple Literature but Not in the Pentateuch")]), _vm._v(" "), _c('table', [_c('tbody', [_c('tr', [_c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Tradition")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Ezra-Nehemiah")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30"
  }, [_c('p', {
    staticClass: "t-header"
  }, [_vm._v("Second Temple Literature")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Wood offering for the temple")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 10:34(35); 13:31")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("4Q365 23; Josephus, "), _c('em', [_vm._v("War")]), _vm._v(" 2.425; b. Ta"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ʿ")]), _vm._v("an"), _c('em', [_vm._v(".")]), _vm._v(" 28a")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Expansion of “holy house” to “holy city”")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 3:1; 11:1, 18")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Isa 48:2; 52:1; Dan 9:24; 11Q19 xlvi 9–12; 4QMMT"), _c('sup', [_vm._v("b")]), _vm._v(" 59–62; 2 Macc 1:7; Josephus, "), _c('em', [_vm._v("Antiquities")]), _vm._v(" 9.10; Wis 12:3; 2 Bar 71:1; 84:8")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Stricter Sabbath law than Pentateuch imposed on business and trade")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 13:16–22; cf. Jer 17:21–27; Amos 8:5")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("CD xi 16–17; Jub 2:17–33; 50:6–13")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Fasting during a crisis (not the Day of Atonement)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 8:21–23; 9:5–10:6; Neh 1:4; 9:1")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Zech 7:3, 5; 8:19; Esth 4:3, 16; Dan 9:3; 10:2–3; Tob 12:8; Jdt 8:6; Matt 17:21; Acts 14:23; cf. m. Ta"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ʿ")]), _vm._v("an. 4:6")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Minimum age for Levites")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 3:8, fixed at 20 (cf. age 25 in Num 8:24)")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("1 Chr 23:24, 27")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("All intermarriage with non-Jews forbidden based on ethnicity")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 9:1; 10:11–12; Neh 13:23–25")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("4QMMT"), _c('sup', [_vm._v("b")]), _vm._v(" 75–76, 81–82; Aramaic Levi Document 6.4; T. Levi 9:9–10; 14:6; Tg. Ps.-J. on Lev 18:21; Jub. 30:13–15; m. Meg. 4:9")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Midrash Torah")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 7:10; cf. Neh 8:13; 9:3")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Sir 3:20–22; 24:33; 1 QS viii 15, 26; 4Q270 7 ii 15; cf. 4QpNah i 3–4; 4Q174 i 14")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ransom of captives ("), _c('em', [_vm._v("pidyôn š")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("bûyîm")]), _vm._v(")")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 5:5–12; cf. Lev 25:47–55")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Josephus, "), _c('em', [_vm._v("Antiquities")]), _vm._v(" 16.1–4; m. Ket. 4:8–9; m. Gi"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ṭ")]), _c('em', [_vm._v(".")]), _vm._v(" 4:6, 9; cf. b. B. Bat. 8ab")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Gentile impurity")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 6:21; 9:11; Neh 13:9")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Jub. 22:16–17; 3 Macc. 33:3–7; 1QS v 14–20; m. Pesa"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ḥ")]), _vm._v(". 8:8; t. Zavim 2:1; Acts 10:28")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Rotating shifts of priests and Levites")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 12:24–25; 13:30")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("1 Chr 24:3; 25:1; Josephus, "), _c('em', [_vm._v("Antiquities")]), _vm._v(" 7.365; Luke 1:8")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Genealogical records for priesthood")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Ezra 2:62–63; 7:1–5; Neh 7:64–65; 12")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("1 Chr 24:7; Josephus, "), _c('em', [_vm._v("Apion")]), _vm._v(" 1.35–36; 1 Macc 2:1")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Standing before public Torah reading, using a platform/lectern, and other elements of synagogue/early church practice")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("Neh 8:4–7")])]), _vm._v(" "), _c('td', {
    staticClass: "wid30-bort"
  }, [_c('p', {
    staticClass: "t-left"
  }, [_vm._v("1 Macc 3:48; Philo, "), _c('em', [_vm._v("Every Good Person Is Free")]), _vm._v(" 81–82; Luke 4:16–30; Acts 13:15–16; Theodotus; m. Ta"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ʿ")]), _vm._v("an. 2:2; b. Meg"), _c('em', [_vm._v(".")]), _vm._v(" 21a")])])])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-20"
    }
  }, [_vm._v("C. SANCTUARY: THE SECOND TEMPLE")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("J. Blenkinsopp has aptly referred to the Jerusalem temple as the “Center of Gravity” for the ancient Jewish community."), _c('a', {
    attrs: {
      "href": "#/#foot-note-713",
      "id": "footnote-713"
    }
  }, [_c('sup', [_vm._v("257")])]), _vm._v(" It was the nexus of heaven and earth connecting Israel with her God. This was no mere theological principle but a reality graphically demonstrated on occasion by a cloud of God’s glory within the temple (1 Kgs 8:10–11; 2 Chr 5:13–14; Isa 6:4; Ezek 10:4). The prophets report visions of Yahweh’s presence with his angels hovering over the sanctuary (Ezek 9:3; 10:3–4) and speaking to them from within its walls (Isa 6:1–4). These moments were not sterile either but accompanied by earthquakes or other miraculous signs (Isa 6:4–7). Several priests witnessed the glory of God in the temple (1 Kgs 8:11; 2 Chr 5:14; 26:19–20) and received angelic visitors there (Luke 1:11). (See the image of the Second Temple on "), _c('a', {
    attrs: {
      "href": "#/#fig1"
    }
  }, [_vm._v("p. 97")]), _vm._v(".)")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("According to Ezra-Nehemiah, rebuilding the temple was the principal mandate initiated by Yahweh and handed down by Cyrus the Great. The temple became the focal point of Judaism, giving religious identity to the postexilic Jewish community."), _c('a', {
    attrs: {
      "href": "#/#foot-note-712",
      "id": "footnote-712"
    }
  }, [_c('sup', [_vm._v("258")])]), _vm._v(" The author wants to emphasize that the Second Temple cult is a secure home for the unadulterated worship of Yahweh and the only legitimate continuation of Solomon’s temple cult. To this end, the author emphasizes not only the rebuilding effort but also cultic personnel, vessels, and religious festivals. Unlike Solomon’s temple cult, which according to 1 Kgs 8:41–43 welcomed foreigners to come and pray toward the temple, Ezra-Nehemiah makes no provision for them to join in Israelite worship.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The construction and dedication of the First Temple is instructive for understanding the significance of the Second Temple. Yahweh commends David for his desire to build him a house “to his name”—that is, for his honor (1 Kgs 8:19). Furthermore, by placing his name on his house, Yahweh affirms both his ownership of the house and his residence there. This in turn emphasizes the access God’s people can have to him at his house. This point is made especially on the occasion of the dedication of the First Temple, when Yahweh promises Solomon, “I have sanctified this house which you have built, to put my name there forever, and my eyes and my heart will be there perpetually” (1 Kgs 9:3). Solomon reminds Yahweh of these words and his promise that “My name shall be there, that you may listen unto the prayer which your servant will make toward this place” (1 Kgs 8:29; cf. 2 Sam 7:13). Indeed, worshipers are invited to entreat Yahweh for help during crises by repenting and confessing his name at his house (1 Kgs 8:33). Placing the divine name on the temple thus not only indicates that Yahweh is great because he has a house of worship but also proclaims that people have access to him at his house. This biblical concept still supports the assembly of believers at the church, which stands as a beacon of hope to those who would enter, turn to God, and confess his name (see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03a#exc14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 14, “The Name Yahweh,” pp. 314–17")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Second Temple is presented as the legitimate successor to Solomon’s Temple in several ways. Parallels between the accounts of both temple-building processes present the new temple as a restoration of the old one, not a completely new one (see the commentary on Ezra 3:7). The original temple vessels are restored completely, counted out, and brought back."), _c('a', {
    attrs: {
      "href": "#/#foot-note-711",
      "id": "footnote-711"
    }
  }, [_c('sup', [_vm._v("259")])]), _vm._v(" Also, the genealogies of the builders go back to First Temple times. Some of those who attended the First Temple are present at the dedication of the new altar (Ezra 3:12), which is built right on the original spot (Ezra 3:3). The building process is comparable to that of Solomon’s Temple (see Ezra 3:7 with 1 Chr 22); similar categories of builders are hired, including Sidonians and Tyrians who are commissioned to bring cedars from Lebanon through Joppa (2 Chr 2:16). Also, Levites are mustered for both occasions (1 Chr 23:4; Ezra 3:9).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Using ancient sources, Schiffman provides a description of Zerubbabel’s temple:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("It had two courtyards. One report suggests dimensions of 500 by 100 cubits (about 750 by 150 feet) for the inner courtyard. There were at least four gates in the wall of the outer courtyard, and at least one of them faced a street. There were at least two gates to the inner courtyard. Various chambers surrounded the Temple in both courtyards. Most of these were in the outer courtyard, and were used for the storage of tithes, equipment, and vessels. Certain high officials apparently merited private chambers within the Temple precincts. The returnees constructed their altar on the site of the altar of the First Temple. The Temple building was of hewn stone, with wooden beams reinforcing the walls from within. The Temple itself was 60 cubits (approximately 90 feet) high. The Holy of Holies was empty, as there was no ark and no cherubim."), _c('a', {
    attrs: {
      "href": "#/#foot-note-710",
      "id": "footnote-710"
    }
  }, [_c('sup', [_vm._v("260")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Although it was refurbished by Simon the Just (ca. 200 BCE) and to a greater extent by Herod the Great (20/19 BCE), the Second Temple was to stand at the center of Judaism for the entire period from Zerubbabel to its destruction by the Romans in 70 CE. Even the sectarian community reflected in Qumran texts never freed itself from the temple entirely but expected to one day renovate its cult."), _c('a', {
    attrs: {
      "href": "#/#foot-note-709",
      "id": "footnote-709"
    }
  }, [_c('sup', [_vm._v("261")])]), _vm._v(" Jewish Christians, and the author of Hebrews in particular, who regarded the work of Jesus Christ as supererogatory to the temple, still felt compelled to reinterpret it.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The daily sacrifice ("), _c('em', [_vm._v("tāmîd")]), _vm._v("), offered every morning and afternoon, was essential to the temple cult. At these times, a lamb was offered in order to keep the holy fire on the altar burning and thus maintain the means of atonement in Israel (see Lev 6:2–6, 8–13). The sacrifices were accompanied by meal and drink offerings (Exod 29:38–43; Num 28:1–8). Incense was offered (Exod 30:7–8), and apparently psalms were sung (1 Chr 23:30–31). These two daily worship services marked the beginning and end of the day’s cultic activities, and together they validated the rest of the sacrifices which might be offered as well as worship in general. P. Trudinger describes it: “As breathing is a sign of a living body, so the performance of the Tamid service was a sign of a living cult, without which no other cultic practice had meaning.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-708",
      "id": "footnote-708"
    }
  }, [_c('sup', [_vm._v("262")])]), _vm._v(" Nehemiah orders the people to pay one-third of a shekel as a tax for the maintenance of the "), _c('em', [_vm._v("tāmîd")]), _vm._v(" and other temple sacrifices (Neh 10:32–33[33–34]; cf. Ezek 46:13–15, who places this obligation on the ruler).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The origins of the Second Temple were not, as some might suppose, the beginnings of ritualism and legalism. Rather, Ezra-Nehemiah emphasizes the vitality of worship throughout. The first act the community undertakes upon the first return under Zerubbabel is to immediately rebuild the altar of the temple on the same spot as the old altar and begin worshiping Yahweh (Ezra 3:2–3). Temple Servants, gatekeepers, and musicians returned from exile ready to enhance the worship of the soon-to-be-restored temple, along with over four thousand priests and hundreds of Levites (Ezra 2:36–61).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("This focus on the cult is not limited to the restored operations of the temple but emphasizes the worship of the entire community offering collective praise to Yahweh. Kidner describes it so:")]), _vm._v(" "), _c('p', {
    staticClass: "quotes_bq"
  }, [_vm._v("colour and movement of the great occasions of worship, with instrumentalists and antiphonal singers, with the congregation’s shouts of joy or lamentation … with morning and evening sacrifices and with choirs which had their named leaders (cf. Neh 11:17, 23; 12:8f., 24). All this was intensified at festivals, which added the dimension of a vivid commemoration of God’s saving acts—whether with the passover lamb and the unleavened bread (Ezra 6:20ff.) or with the home-made arbours that turned all Jerusalem into an Exodus encampment (Neh 8:14ff.)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-707",
      "id": "footnote-707"
    }
  }, [_c('sup', [_vm._v("263")])])]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("The author of Ezra-Nehemiah makes repeated references to priests, Levites, singers and celebrations (Ezra 3:10–13; 6:16–22; Neh 12:27–43), emphasizing the timeless conviction that "), _c('em', [_vm._v("the joy of Yahweh is your strength")]), _vm._v(" (Neh 8:10).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Jewish tradition of the afternoon prayer ("), _c('em', [_vm._v("min")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _c('em', [_vm._v("â")]), _vm._v("; see m. Ber. 4:1; m. Pesa"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ḥ")]), _vm._v(". 10:1; b. Ta"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ʿ")]), _vm._v("an. 12b; m. Ma"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ʿ")]), _vm._v("aś. Š. 5:10) dates back at least to the time of Ezra, who offered his penitential prayer at the time of the perpetual evening sacrifices (Ezra 9:5). The term "), _c('em', [_vm._v("min")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _c('em', [_vm._v("â")]), _vm._v(" technically refers to the fragrant grain offering that often accompanied an animal sacrifice, but it came to be associated especially with the evening incense offering (see Ps 141:2). This was a time of heightened spiritual sensitivity. Several revelations of the angel Gabriel occurred at the time of the evening "), _c('em', [_vm._v("min")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _c('em', [_vm._v("â")]), _vm._v(", including the revelations to Daniel (Dan 9:21), Zechariah (Luke 1:9f), and the high priest, John Hyrcanus (Josephus, "), _c('em', [_vm._v("Antiquities")]), _vm._v(" 13.282–283). The custom was that the priest entered the temple at this time and offered incense to God while the people prayed in the outer court. No one was allowed to enter the inner court (between the porch and the altar) during this time (m. Kelim 1:9).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("There is an expanded sense of holy space within Ezra-Nehemiah. Like the democratization of the Torah, so, on a certain level, there is a democratization of holiness. According to T. C. Eskenazi, one of the three main themes in Ezra-Nehemiah is “the expansion of the house of God to encompass the city.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-706",
      "id": "footnote-706"
    }
  }, [_c('sup', [_vm._v("264")])]), _vm._v(" She sees holiness confined to the altar and temple in Ezra 1–6, transferred to all Israel in Ezra 7–10 (the leadership in Ezra 8:28; the laity in Ezra 9:2), and finally to the city of Jerusalem in Nehemiah (Neh 3:1–32; 11:1, 18; 12:27–47). In the summary of Cyrus’s decree (Ezra 6:3), the temple is larger than Solomon’s Temple, and no dimensions are given for the altar area and courts. Eskenazi thinks this is because Ezra 6 is the beginning of the project, which is not completed until Nehemiah finishes and consecrates it (Neh 3:1), conducts a cultic celebration (Neh 7:1; 13:22), and extends the sacred area all the way to the city walls."), _c('a', {
    attrs: {
      "href": "#/#foot-note-705",
      "id": "footnote-705"
    }
  }, [_c('sup', [_vm._v("265")])]), _vm._v(" In the processions to the temple, the laity find themselves “in the house of God” (Neh 12:40), although they are merely within the walls of the city, not the sanctuary itself."), _c('a', {
    attrs: {
      "href": "#/#foot-note-704",
      "id": "footnote-704"
    }
  }, [_c('sup', [_vm._v("266")])]), _vm._v(" Indeed, some level of purity and holiness has been extended beyond the area of the temple to the entire city.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra-Nehemiah builds upon the expansion of holy space already present in some psalms and prophets. The psalmist refers to the city of Jerusalem as the “holy mountain” (Ps 48:1–2). Jeremiah foresees a time when the city will include valleys and fields, all of which will be holy (Jer 31:38–40; see also Isa 52:1; Ezek 48:35; Joel 3:17; Zech 2:11; 14:20). This idea gains further momentum in later Second Temple Judaism. Josephus states that under Antiochus III, only purified Jews could enter the temple complex, and unclean animals, including skins and carcasses, were not allowed into Jerusalem ("), _c('em', [_vm._v("Antiquities")]), _vm._v(" 12.145–146). Some texts from Qumran expand the limits of sacred space to include the entire city of Jerusalem and require greater purity than the Torah on all who enter. 4QMMT refers to all of Jerusalem as the “camp of holiness” (4Q394 iii 10–13; see 11Q19 xlvii 4). In the same vein, the Damascus Document bans sexual relations within the city limits (CD xii 1–2)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-703",
      "id": "footnote-703"
    }
  }, [_c('sup', [_vm._v("267")])])]), _vm._v(" "), _c('h1', {
    staticClass: "sec2",
    attrs: {
      "id": "sec2-21"
    }
  }, [_vm._v("D. HOLY PEOPLE")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("Ezra 9:2 refers to Israel as “holy seed.” The idea that Israel is supposed to be a holy nation is not new (Exod 19:6; Deut 7:6; Lev 19:2), but the particular phrase “holy seed,” with its emphasis on biological transmission of holiness, is an innovation."), _c('a', {
    attrs: {
      "href": "#/#foot-note-702",
      "id": "footnote-702"
    }
  }, [_c('sup', [_vm._v("268")])]), _vm._v(" The notion of holy people began much earlier than Ezra-Nehemiah. God’s name is on his people, even before the restoration of the temple. The name theology referring to the temple discussed above can also apply to the people. The people belong to God and are given access to him, even without the building. Indeed, the ancient priestly benediction affirms, “I will place my name on the Israelites, and I will bless them” (Num 6:27). Thus, God’s people belong to him, and he takes responsibility for their welfare. At the divine revelation to all Israel at Mt. Sinai, Yahweh promises to establish Israel as a “royal priesthood and a holy nation ("), _c('em', [_vm._v("gôy qādôš")]), _vm._v(")” if she is obedient to him (Exod 19:6). Deuteronomy states that the whole of Israel is a “holy people” ("), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("am qādôš")]), _vm._v(") and is required to destroy seven Canaanite nations lest they intermarry with Israel and influence them to sin (Deut 7:1–6; Ezra 9:1).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Leviticus sets forth what is involved in being Yahweh’s “holy people.” The command to Israel “Be holy as the Lord your God is holy” is found in two contexts: one is ritual, after a catalog of laws regarding impure foods (11:44; cf. 20:26), and the other is moral, coming after a list of sexual impurities and preceding various ethical commands (19:2). Jewish rituals (e.g., circumcision, kosher food laws and ritual purification after contact with impurity) physically enforced Israel’s separation from other nations who, as a rule, would not engage in social intercourse or marriage with such a different culture. This seems to be the intent of the laws in the first place (Lev 20). Notions of purity and impurity surface in Ezra-Nehemiah as a way of reinforcing group identity—the distinction of the holy people vis-à-vis all outsiders."), _c('a', {
    attrs: {
      "href": "#/#foot-note-701",
      "id": "footnote-701"
    }
  }, [_c('sup', [_vm._v("269")])]), _vm._v(" The second aspect of Israel’s holiness is her reflection of Yahweh’s ethical goodness. The second command to be holy in Leviticus comes between a list of laws regulating sexual behavior and a catalog of ethical laws that instill moral integrity. By keeping these laws, Israel reflects the character of Yahweh and begins to fulfill the law “Be holy as I am holy” by imitating him ("), _c('em', [_vm._v("imitatio Dei")]), _vm._v(")."), _c('a', {
    attrs: {
      "href": "#/#foot-note-700",
      "id": "footnote-700"
    }
  }, [_c('sup', [_vm._v("270")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Torah’s “holy people” ("), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _c('em', [_vm._v("am qādôš")]), _vm._v(", Deut 7:6) becomes Ezra-Nehemiah’s “holy seed” ("), _c('em', [_vm._v("zera")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʿ")]), _vm._v(" "), _c('em', [_vm._v("haqqōdeš")]), _vm._v(", Ezra 9:2). The identity of “seed” becomes an issue for some of the priests who were unable to verify their descent, “their seed” (Neh 7:61; Ezra 2:59). Unlike the Pentateuch, which grants some latitude with regard to neighboring Moabites, Ammonites, Edomites, and Egyptians (Deut 23), the author adds these groups to the list of Canaanites in Deut 7."), _c('a', {
    attrs: {
      "href": "#/#foot-note-699",
      "id": "footnote-699"
    }
  }, [_c('sup', [_vm._v("271")])]), _vm._v(" Indeed, the Pentateuch’s flexibility on the subject of intermarriage stands in stark contrast to Ezra-Nehemiah. Deuteronomy 21:10–14 outlines a procedure whereby a foreign captive can be taken as a wife by an Israelite. According to Numbers, even Moses married someone who was not of the people of Israel (Num 12:1). By contrast, Ezra expelled 113 foreign women, along with their children, who were married to Jewish men. As Kaufman points out, there is no indication in the text that these women were outright idolaters, but apparently absorption into Israel was not seen as an option for them."), _c('a', {
    attrs: {
      "href": "#/#foot-note-698",
      "id": "footnote-698"
    }
  }, [_c('sup', [_vm._v("272")])]), _vm._v(" The reality behind this new emphasis on holy seed, however it was derived, is that people of other races are simply not eligible for Israel’s holy status even if they renounce their own religion and adopt the worship of Yahweh. The measure seems drastic, but apparently, for this community, a minority group under Persian control, purity of religion could only be entrusted to those who shared its genealogical heritage.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Eskenazi charts the emphasis on holiness in Ezra-Nehemiah from the temple, its altar, and its priests (Ezra 1–6) to the leader and priest Ezra (8:28) to Israel as a community (Ezra 9–10) and finally to the entire city (Neh 3:1–32; 11:2; 12:27–47)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-697",
      "id": "footnote-697"
    }
  }, [_c('sup', [_vm._v("273")])]), _vm._v(" She regards the central matter in Ezra-Nehemiah to be the community, with purity and separation as by-products."), _c('a', {
    attrs: {
      "href": "#/#foot-note-696",
      "id": "footnote-696"
    }
  }, [_c('sup', [_vm._v("274")])]), _vm._v(" In fact, the emphasis on the cult comes to the foreground as an attempt to unify the people into a new community. Still, the parameters of the community were understood to be fixed by the Torah. The cult is subservient to whatever is dictated by what is written as law (Ezra 3:2, 3; 6:14). In Ezra 7–10, separation follows the dictates of the Torah; the goal is reached in Neh 8–10 (or 8–12). At this point, there are clear guidelines as to who is “in” and who is “out” both physically and symbolically. Ezra-Nehemiah turns Jerusalem into a Jewish city, not just a city with some Jewish residents."), _c('a', {
    attrs: {
      "href": "#/#foot-note-695",
      "id": "footnote-695"
    }
  }, [_c('sup', [_vm._v("275")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Thus, the label of holiness in Ezra-Nehemiah is expanded beyond that of earlier texts. It includes an inherent holiness of Israel and her descendants by race, which can be impaired by intermarriage. The exclusion of non-Israelites (no matter which race) is clear from the writer’s emphasis on “holy seed.” Holiness becomes biological, thus disallowing any conversion opportunity. In fact, any mixture of holy seed with nonholy seed results in impairment (see below); only the unadulterated remnant (i.e., the returnees) is “holy seed.” Within Israel, holiness is maintained not only by keeping away from the “abominable practices” of the local population but also by ritual purification before entering sacred time (Ezra 6:19–21) and space (Neh 12:30; 13:22).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The emphasis on holiness in Ezra-Nehemiah extends beyond people and space to other items as well. For example, one of the reforms Nehemiah is especially proud of is the reassignment of the holy portions of crops to the Levites and priests (Neh 12.47; cf. 13:10–14). Apparently, Jews were not “setting aside as holy” agricultural gifts for the Levites. Conversely, priests who were not qualified to receive these gifts were barred from the sacred perquisites (Ezra 2:63; Neh 7:65). Also, Nehemiah puts into place firm measures to guard the holiness of the Sabbath (Neh 10:31[32]; 13:22). In another expansion of laws regarding sancta, several Qumran texts, as well as Josephus’s Essenes, extend the requirement of purity for eating sacred offerings to ordinary food. Thus, an expanded concept of holiness, similar to what is known from later sources, is already in embryo in Ezra-Nehemiah.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The expansion of holiness restricted by purity rituals from the arena of the priests to the laity is a trend among Jewish groups of the Second Temple period. Jubilees claims priestly status for all Israel in line with the Exod 19:6 mandate of the nation to be a royal priesthood. Like Ezra-Nehemiah, this priestly status is based on Israel maintaining sexual purity. Sexual impurity is defined in Jubilees’ retelling of the rape of Dinah as marriage to a gentile. In fact, Jubilees decrees that any Jewish woman who marries a gentile should be burned and the father or brother who arranged the marriage should be stoned (Jub. 30:5–26). This represents a more stringent position than even Ezra-Nehemiah envisioned. 4QFlorilegium describes a “human sanctuary,” a reference to the holiness of its community into which no foreigner is allowed (4Q174 i 4).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The idea develops in Second Temple times that God’s name is on his people even as it is on his house, as is evident in many texts of the Dead Sea Scrolls and the New Testament, whose writers functioned sometimes without a temple building. The writer of 4QFlorilegium regards the community as a human temple that offers up praise to the Creator (4Q174 i 4; cf. “Sanctify yourselves like the holy place,” Aramaic Levi Document 6.4)."), _c('a', {
    attrs: {
      "href": "#/#foot-note-694",
      "id": "footnote-694"
    }
  }, [_c('sup', [_vm._v("276")])]), _vm._v(" Apparently, while the Second Temple stood, the notion that the bodies of Jews were containers of God’s presence was already in place (see "), _c('span', {
    attrs: {
      "href": "#/012_Chapter02c#exc9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 9, “The Sacrilege of Intermarriage,” pp. 244–54")]), _vm._v("). Paul, in fact, in an argument for sexual purity, considers this common knowledge: “Don’t you know that you are a temple of the Holy Spirit?” (1 Cor 6:19). Thus, the bodies of the people of God, selected by him and labeled with his name, become repositories for the Holy Spirit.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-7"
    }
  }, [_vm._v("VII. CHRISTIAN APPLICATION")]), _vm._v(" "), _c('p', {
    staticClass: "para"
  }, [_vm._v("As the exiles return to a changed reality in Yehud, new issues emerge that turn out to be significant in the further course of the history of the Jewish people but also continue to be relevant for Christian believers today. Two basic problems emerge: How does the community adapt to the changed reality described above and still maintain its sense of heritage and continuity with the past? And what should be the relationship of God’s people to the outside world, whether foreign authorities, Jews with different religious practices, or outsiders seeking membership in the community?")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("In short, how do God’s people maintain a holy community within a society that applies pressure according to a different value system? On the one hand, Jews found themselves in a completely different social matrix than in the preexilic monarchy: a people without national independence, sharing its homeland with antagonists, with little control over its destiny, paying harsh taxes to a foreign emperor who could as easily destroy as support them, at the whim of local officials too who vilify when they are rebuffed. At the same time, the attraction of the local population is not to be denied, and this comes as an even more difficult issue. How are God’s people supposed to identify and reject the harmful practices and idolatry of the mainstream culture while still seeking to advance themselves in society? In a pluralistic culture, how is one supposed to find a suitable spouse and sustain relations among kin of diverse points of view? The threat of deteriorating faith through assimilation was real, and extinction was a strong possibility. Moreover, internal dissension within the community is frequently a greater threat than external forces.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra and Nehemiah deal with the problem on three levels: education in the Torah as the written Word of God, restoration of temple worship, and maintenance of the daily lives of the community by separation from foreigners with pagan influences. This focus on the Word of God, assembly and worship at his house, and the definition and protection of the community proved to be providential for Jewish survival. The political world would break apart more than once as Jews exchanged their Persian overlords for Greek conquerors and Roman emperors, but their “religious and spiritual cohesion remained firmly established on the foundations laid down during the Persian age by Ezra and Nehemiah, King Darius and King Artaxerxes.”"), _c('a', {
    attrs: {
      "href": "#/#foot-note-693",
      "id": "footnote-693"
    }
  }, [_c('sup', [_vm._v("277")])])]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Like Zerubbabel and Joshua, Christians in the modern world face the temptation of adopting a pluralistic or relativistic attitude toward religion. Christian faith, with its emphasis on faith in Christ’s death and resurrection as the key to acceptance by God, is often considered a primitive alternative to more intelligent ways of thinking. Rationalism is viewed as contrary to biblical faith rather than supporting it."), _c('a', {
    attrs: {
      "href": "#/#foot-note-692",
      "id": "footnote-692"
    }
  }, [_c('sup', [_vm._v("278")])]), _vm._v(" And it is fashionable to accept all forms of belief as valid practice rather than to evaluate them according to the message of Scripture. Tolerance, while a cardinal virtue in any community, must not be confused with affirmation and adoption of religious practice that is contrary to the Word of God.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The Torah was the standard Ezra taught and, together with Nehemiah, enforced among the community. The uncompromising stance these leaders adopted against what they viewed as a threat to the continuance of God’s people has drawn much fire from those who view them as unnecessarily harsh. To be sure, their interpretation of the Torah on the matter of intermarriage represents a stringent approach heretofore unknown in Jewish texts. Nevertheless, the community’s status as a minority group without political autonomy makes the measures of Ezra and Nehemiah understandable as the survival of Jewish ethno-religious identity into the next generations took precedence over all other concerns.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Worship at God’s house, an overriding first mission of the returnees, should inspire the Christian as well. The desire to return and rebuild the temple, fueled by the Holy Spirit, was the first goal of the returning exiles. In fact, they could not wait for the completion of the building but began to worship on the quickly built altar set up on its original site (Ezra 3:2–3). The accompaniment of music enhanced the worship on more than one occasion in the narrative, and the writer emphasizes the joy with which the people celebrated the goodness of Yahweh (see the translation note on Ezra 3:12–13; see also "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03c#exc22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 22, “Music and Worship,” pp. 452–55")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("The writer reports that, even in times of sorrow and distress, the people worship Yahweh as the Creator and Sustainer of life. In fact, it is in these times that the faithful petitioner confidently challenges Yahweh to act on his behalf. This affirmation is expressed in the prayers of Ezra, Nehemiah, and the Levites. God exists, creates and sustains life, intervenes in human affairs, and cares for his people. As such, he not only is worthy of praise but is the only source of blessing. Thus, the righteous make their petitions to him because he is able to help them, and he has promised to be committed to those who love him (Neh 1:5).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Finally, the community of faith must be protected in every way possible: spiritually, financially, and physically. The leaders must be sensitive to threats to the welfare of the people of God. In Ezra-Nehemiah, prophets encouraged the people to begin anew their efforts to build the temple so as to complete the vision the Spirit had inspired (Ezra 5:1). In an unprecedented and much-criticized decree, both clergy and laity are ordered to divorce foreign wives in order to maintain community solidarity (Ezra 10:5). Nehemiah supervises the building of a new wall to protect the community and organizes its defense under physical attack.")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("Ezra-Nehemiah is a testimony to God’s willingness to assist leaders in their efforts on behalf of his people. Sheshbazzar plays a passive role in collecting the returnees for the journey to the homeland. Rather, it is the Spirit of God that inspires both the foreign king to support and the Babylonian Jews to make the journey to Jerusalem (Ezra 1:1, 5). Ezra is not significantly challenged despite his harsh decree for men to divorce their foreign wives. Rather, the suggestion to enforce such an edict comes from a member of the community following Ezra’s prayer to God (Ezra 10:1–2). Later, the people rally around him to learn more of the message of God’s Word. After Nehemiah’s fast and prayer for God’s help, he receives divine favor that causes the king to accede to his requests for a furlough and imperial aid (Neh 2:8). Nehemiah is then able to protect the community by organizing the building of a wall around Jerusalem. Authorized by the king as governor of Yehud, he later insists on and receives without question further financial assistance for the beleaguered workers who have become prey to creditors (Neh 5:12). In fact, all of the leaders of the return are granted favor and finances from the Persian authorities (Ezra 1:7; 6:8–9; 7:15; Neh 2:8–9).")]), _vm._v(" "), _c('p', {
    staticClass: "indent-para"
  }, [_vm._v("God’s people are holy—that is, they carry his name and are agents of his will. It is through them that the divine will is accomplished in the world. They can take courage from the fact they are in his hand no matter the circumstances. His favor will support every task he inspires. Christians are not on their own in this endeavor. They are linked to one another in the present community of faith, but also to the “great cloud of witnesses” of the past (Heb 12:1)—the generations of the faithful extending all the way back into biblical times. With such a heritage, today’s Christians can find the strength to stand as faithful role models and teachers of the next generation, and the next after that.")]), _vm._v(" "), _c('h1', {
    staticClass: "sec1",
    attrs: {
      "id": "sec-8"
    }
  }, [_vm._v("VIII. OUTLINE")]), _vm._v(" "), _c('p', {
    staticClass: "outline_1"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec-9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("I. From Babylonian exiles to temple community (Ezra 1:1–6:22)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec2-22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("A. Return to Jerusalem (1:1–2:70)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec3-17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Preparation for return (1:1–11)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Imperial authorization (1:1–4)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Enthusiastic response and financial support (1:5–6)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("c. Return of the sacred vessels (1:7–11)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec3-18",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Genealogical record of returnees (2:1–70)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Returnees by descent (2:1–20)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Returnees by city (2:21–35)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("c. Temple and related personnel (2:36–58)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("d. Undocumented Israelites/priests (2:59–63)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("e. Total returnees and animals (2:64–67)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("f. Financial donations and conclusion (2:68–70)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec2-23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("B. Rebuilding the temple (3:1–6:22)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec3-19",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Rebuilding the foundations (3:1–13)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Rebuilding and dedicating the altar (3:1–6)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Laying the temple foundations (3:7–13)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec5-1",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("i. Requisition of materials (3:7)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec5-2",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("ii. Commencement of the work (3:8–9)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec5-3",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("iii. Laying the foundations amid worship (3:10–11)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec5-4",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("iv. Mixed emotional reactions (3:12–13)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec3-20",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Opposition to the building effort (4:1–6:22)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. The local population rejected (4:1–3)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Opposition begins (4:4–6)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec4-14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("c. Aramaic letters of opposition and suspension during the time of Artaxerxes I (4:7–24)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec5-5",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("i. The letter of Bishlam, Mithredath, and Tabeel (4:7)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec5-6",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("ii. The letter of Rehum and Shimshai (4:8–16)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02a#sec5-7",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("iii. Artaxerxes’s response (4:17–24)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("d. Encouragement of the prophets (5:1–2)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-16",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("e. Tattenai’s opposition (5:3–17)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec5-8",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("i. Interrogation of the builders (5:3–5)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec5-9",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("ii. Letter to Darius requesting an investigation (5:6–17)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-17",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("f. Darius’s decrees (6:1–12)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec5-10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("i. Cyrus’s edict is found in the archives (6:1–5)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec5-11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("ii. Order to complete the temple with imperial aid (6:6–10)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec5-12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("iii. Threats for neglecting or changing the decree (6:11–12)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-18",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("g. Completion and dedication of the temple (6:13–18)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-19",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("h. Celebration of the Passover and the Festival of Unleavened Bread (6:19–22)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_1"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec-10",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("II. The ministry of Ezra (Ezra 7:1–10:44)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec2-24",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("A. Ezra’s authorization (7:1–28)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-21",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Introduction to Ezra (7:1–10)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-20",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Ezra’s genealogy (7:1–5)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-21",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Ezra’s mission (7:6–10)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Commission of Ezra by Artaxerxes I (7:11–26)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Ezra’s praise to Yahweh (7:27–28)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec2-25",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("B. Ezra’s journey to Jerusalem (8:1–36)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-24",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. List of returning families (8:1–14)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-25",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Assembly of the people and search for Levites (8:15–20)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-26",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Fasting and prayer (8:21–23)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-27",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Commissioning the priests and Levites with sacred valuables (8:24–30)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-28",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5. The journey from the river Ahava to Jerusalem (8:31–34)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-29",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6. Worship at the temple and delivery of the imperial orders (8:35–36)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec2-26",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("C. The intermarriage crisis (9:1–10:44)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec3-30",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Ezra’s trauma (9:1–15)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-22",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Elders inform Ezra of the sacrilege of “holy seed” (9:1–2)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02b#sec4-23",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Ezra’s horrified reaction (9:3–5)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec4-24",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("c. Ezra’s prayer (9:6–15)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec3-31",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Ezra’s action (10:1–44)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec4-25",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Confession (10:1–4)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec4-26",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Oath (10:5–8)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec4-27",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("c. Assembly in the rain (10:9–15)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec4-28",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("d. Investigation of intermarriage cases and expulsion (10:16–17)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec4-29",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("e. List of offenders and expulsion of wives (10:18–44)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec5-13",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("i. Temple personnel (10:18–24)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/012_Chapter02c#sec5-14",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("ii. Laity (10:25–44)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_1"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec-11",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("III. Nehemiah rebuilds the wall of Jerusalem (Neh 1:1–6:19)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-27",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("A. Report of Jerusalem in distress (1:1–3)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-28",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("B. Nehemiah’s reaction (1:4–11)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-32",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Mourning (1:4)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-33",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Prayer (1:5–11)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-29",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("C. Artaxerxes commissions Nehemiah (2:1–10)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-34",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Nehemiah blurts out his problem to the king (2:1–3)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-35",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. The king approves and supports Nehemiah’s journey to Jerusalem (2:4–9)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-36",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Opposition from Sanballat and Tobiah (2:10)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-30",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("D. Nehemiah surveys the wall (2:11–20)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-37",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Private inspection by night (2:11–15)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-38",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Meeting with local Jewish leaders (2:16–18)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-39",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Dealing with opposition (2:19–20)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-31",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("E. The leaders and their crews rebuild the wall in sections (3:1–32)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-40",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Priests rebuild the northern end (3:1)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-41",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Reconstruction of the northern and western sections (3:2–15)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-42",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Reconstruction of the eastern section (3:16–27)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-43",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Further repairs to the eastern section (3:28–32)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-32",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("F. External opposition to the building project (4:1–23)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-44",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Scoffing from Sanballat and Tobiah (4:1–3)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-45",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Nehemiah’s prayer and enclosure of the wall (4:4–6)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-46",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Threats and discouragement (4:7–12)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-47",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Nehemiah provides military protection, and the builders bear arms (4:13–23)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-33",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("G. Internal oppression (5:1–19)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-48",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Complaint of oppression by fellow Jews (5:1–5)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-49",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Nehemiah accuses creditors of abusing kinship bonds and orders restoration (5:6–11)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-50",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Creditors swear to obey Nehemiah’s order in a public ceremony (5:12–13)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-51",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Nehemiah’s selfless devotion to the project and the people (5:14–19)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-34",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("H. Sanballat, Tobiah, and Geshem attempt to destroy Nehemiah (6:1–19)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-52",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. A plot to lure Nehemiah away from the jobsite (6:1–9)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-53",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. An attempt to lock Nehemiah in the temple by engaging false prophecy (6:10–14)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-54",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Completion of the wall despite opposition (6:15–16)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-55",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Intimidation through Tobiah’s family network (6:17–19)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_1"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec-12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("IV. Restoring the holy community (Nehemiah 7:1–13:31)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-35",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("A. Setting a guard for Jerusalem (7:1–3)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec2-36",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("B. Registering the people (7:4–72a)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-56",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Planning for the repopulation of Jerusalem (7:4–5)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-57",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Returnees by descent (7:6–25)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-58",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Returnees by city (7:26–38)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-59",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Temple and related personnel (7:39–60)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-60",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5. Undocumented Israelites/priests (7:61–65)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-61",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6. Total returnees and animals (7:66–69)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03a#sec3-62",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("7. Financial donations and conclusion (7:70–72a)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec2-37",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("C. Teaching, celebrating, and observing the law (7:72b–8:18)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-63",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Ezra reads the law (7:72b–8:4)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-64",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. People worship (8:5–6)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-65",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Levites teach the law (8:7–8)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-66",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. People celebrate (8:9–12)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-67",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5. Festival of Booths (8:13–18)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec2-38",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("D. Covenant renewal (9:1–10:39)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-68",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Confession (9:1–3)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-69",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. The Levites’ penitential prayer on behalf of the community (9:4–37)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-70",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Solemn oath (9:38–10:39)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec4-30",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Introduction (9:38)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec4-31",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Signatories (10:1–27)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec4-32",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("c. Stipulations (10:28–39)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec2-39",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("E. Resettlement of Jerusalem (11:1–36)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-71",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Casting lots to repopulate the city (11:1–2)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec3-72",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. List of residents (11:3–36)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec4-33",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("a. Lay families (11:3–9)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec4-34",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("b. Priestly families (11:10–14)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec4-35",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("c. Levites and gatekeepers (11:15–24)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_4"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec4-36",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("d. Residents outside of Jerusalem (11:25–36)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec5-15",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("i. Judah (11:25–30)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_5"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03b#sec5-16",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("ii. Benjamin (11:31–36)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec2-40",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("F. Temple personnel (12:1–26)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-73",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Priests and Levites during Zerubbabel’s time (12:1–9)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-74",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Line of high priests (12:10–11)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-75",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Priests in Joiakim’s day (12:12–21)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-76",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Leaders of families of Levites (12:22–26)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec2-41",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("G. Dedication of the wall of Jerusalem (12:27–47)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-77",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Preparation (12:27–30)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-78",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Processions (12:31–43)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-79",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Worship (12:44–47)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_2"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec2-42",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("H. Nehemiah’s reforms (13:1–31)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-80",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("1. Excluding foreigners (13:1–3)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-81",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("2. Expelling Tobiah from the temple (13:4–9)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-82",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("3. Reinstating tithes for Levites (13:10–14)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-83",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("4. Sanctifying the Sabbath (13:15–22)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-84",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("5. Dealing with intermarriage (13:23–29)")])]), _vm._v(" "), _c('p', {
    staticClass: "outline_3"
  }, [_c('span', {
    attrs: {
      "href": "#/014_Chapter03c#sec3-85",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("6. Nehemiah’s summary and prayer (13:30–31)")])]), _vm._v(" "), _c('p', {
    staticClass: "image-f"
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/foot.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-969",
      "id": "foot-note-969"
    }
  }, [_vm._v("1")]), _vm._v(". E. M. Yamauchi, “Ezra-Nehemiah,” in "), _c('em', [_vm._v("The Expositor’s Bible Commentary")]), _vm._v(", ed. F. E. Gaebelein (Grand Rapids: Zondervan, 1984), 4:586.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-968",
      "id": "foot-note-968"
    }
  }, [_vm._v("2")]), _vm._v(". But see S. Talmon and J. Ben-Dov, “Calendrical Documents and Mišmarot,” in "), _c('em', [_vm._v("Calendrical Texts")]), _vm._v(", vol. 16 of "), _c('em', [_vm._v("Qumran Cave 4")]), _vm._v(", ed. S. Talmon, J. Ben-Dov, and U. Glessmer, DJD 21 (Oxford: Clarendon, 2001), 120–21, who suggest that the founders may have been remnants of the Assyrian conquest of Israel, finding support in Isa 11:1; 19:18–22 for a possible eighth-century Israelite settlement in Egypt.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-967",
      "id": "foot-note-967"
    }
  }, [_vm._v("3")]), _vm._v(". M. Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", NAC 10 (Nashville: Broadman & Holman, 1993), 30.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-966",
      "id": "foot-note-966"
    }
  }, [_vm._v("4")]), _vm._v(". J. Blenkinsopp, “Ezra-Nehemiah: Unity or Disunity?,” in "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah: Redaction, Rhetoric and Reader")]), _vm._v(", ed. M. Boda and P. Redditt (Sheffield: Sheffield Phoenix, 2008), 310.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-965",
      "id": "foot-note-965"
    }
  }, [_vm._v("5")]), _vm._v(". C. L. V. Hensley, “The Official Persian Documents in the Book of Ezra” (PhD diss., University of Liverpool, 1977).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-964",
      "id": "foot-note-964"
    }
  }, [_vm._v("6")]), _vm._v(". K. Hoglund, "), _c('em', [_vm._v("Achaemenid Imperial Administration in Syria-Palestine and the Missions of Ezra and Nehemiah")]), _vm._v(" (Atlanta: Scholars Press, 1992), 47.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-963",
      "id": "foot-note-963"
    }
  }, [_vm._v("7")]), _vm._v(". Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 36.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-962",
      "id": "foot-note-962"
    }
  }, [_vm._v("8")]), _vm._v(". S. Talmon, “Ezra and Nehemiah (Books and Men),” in "), _c('em', [_vm._v("Interpreter’s Dictionary of the Bible: Supplementary Volume")]), _vm._v(", ed. Keith Crim (Nashville: Abingdon, 1976), 361.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-961",
      "id": "foot-note-961"
    }
  }, [_vm._v("9")]), _vm._v(". S. Japhet, “Composition and Chronology in the Book of Ezra-Nehemiah,” in "), _c('em', [_vm._v("Temple and Community in the Persian Period")]), _vm._v(", vol. 2 of "), _c('em', [_vm._v("Second Temple Studies")]), _vm._v(", ed. T. C. Eskenazi and K. H. Richards (Sheffield: Sheffield Academic, 1994), 196.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-960",
      "id": "foot-note-960"
    }
  }, [_vm._v("10")]), _vm._v(". P. A. Noss and K. J. Thomas, "), _c('em', [_vm._v("A Handbook on Ezra and Nehemiah")]), _vm._v(", UBS Handbook Series (New York: United Bible Societies, 2005), 3.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-959",
      "id": "foot-note-959"
    }
  }, [_vm._v("11")]), _vm._v(". E. Bickerman, "), _c('em', [_vm._v("From Ezra to the Last of the Maccabees: Foundations of Post-Biblical Judaism")]), _vm._v(" (New York: Schocken, 1962), 28–29. In the Egyptian account of the Victory of Rameses II at Kadesh, a third-person section that “objectively” praises the king for his heroic acts follows the king’s first-person account of his victory.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-958",
      "id": "foot-note-958"
    }
  }, [_vm._v("12")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", WBC 16 (Waco: Word, 1985), xxxi.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-957",
      "id": "foot-note-957"
    }
  }, [_vm._v("13")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxiv.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-956",
      "id": "foot-note-956"
    }
  }, [_vm._v("14")]), _vm._v(". J. Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", OTL (Philadelphia: Westminster, 1988), 46.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-955",
      "id": "foot-note-955"
    }
  }, [_vm._v("15")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxviii.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-954",
      "id": "foot-note-954"
    }
  }, [_vm._v("16")]), _vm._v(". Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 47.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-953",
      "id": "foot-note-953"
    }
  }, [_vm._v("17")]), _vm._v(". D. Janzen, “The Cries of Jerusalem: Ethnic, Cultic, Legal, and Geographic Boundaries in Ezra-Nehemiah,” in "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah: Redaction, Rhetoric and Reader")]), _vm._v(", ed. M. Boda and P. Redditt (Sheffield: Sheffield Phoenix, 2008), 129–30.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-952",
      "id": "foot-note-952"
    }
  }, [_vm._v("18")]), _vm._v(". Cf. T. C. Eskenazi, “Unity and Disunity in Ezra-Nehemiah: Responses and Reflections,” in Boda and Redditt, "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah")]), _vm._v(", 323.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-951",
      "id": "foot-note-951"
    }
  }, [_vm._v("19")]), _vm._v(". H. G. M. Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", FAT (Tübingen: Mohr Siebeck, 2004), 4–5, 19; G. R. Driver, "), _c('em', [_vm._v("Aramaic Documents of the Fifth Century BC")]), _vm._v(" (Oxford: Clarendon, 1965). Williamson also notes that Elisahib’s activities in Nehemiah’s absence may suggest that he was not expecting him to return.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-950",
      "id": "foot-note-950"
    }
  }, [_vm._v("20")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 20; E. Yamauchi, “Two Reformers Compared: Solon of Athens and Nehemiah of Jerusalem,” in "), _c('em', [_vm._v("The Bible World: Essays in Honor of Cyrus H. Gordon")]), _vm._v(", ed. G. Rendsburg et al. (New York, 1980), 269–92.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-949",
      "id": "foot-note-949"
    }
  }, [_vm._v("21")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 20.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-948",
      "id": "foot-note-948"
    }
  }, [_vm._v("22")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxxiv.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-947",
      "id": "foot-note-947"
    }
  }, [_vm._v("23")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxxiv–xxxv.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-946",
      "id": "foot-note-946"
    }
  }, [_vm._v("24")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxxv. Williamson is supported by M. A. Throntveit, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", Interpretation (Louisville: Westminster John Knox, 1992), 10.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-945",
      "id": "foot-note-945"
    }
  }, [_vm._v("25")]), _vm._v(". T. C. Eskenazi, “Unity and Disunity in Ezra-Nehemiah,” 317. For a good example of the incremental approach, cf. J. Wright, "), _c('em', [_vm._v("Rebuilding Identity: The Nehemiah-Memoir and Its Earliest Readers")]), _vm._v(", BZAW 348 (Berlin: de Gruyter, 2004).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-944",
      "id": "foot-note-944"
    }
  }, [_vm._v("26")]), _vm._v(". Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 70.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-943",
      "id": "foot-note-943"
    }
  }, [_vm._v("27")]), _vm._v(". T. C. Eskenazi, "), _c('em', [_vm._v("In an Age of Prose: A Literary Approach to Ezra-Nehemiah")]), _vm._v(", SBLMS 36 (Atlanta: Scholars Press, 1988), 155–74.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-942",
      "id": "foot-note-942"
    }
  }, [_vm._v("28")]), _vm._v(". E. Ulrich, “Ezra and Qohelet Manuscripts from Qumran (4QEzra and 4QQoh"), _c('sup', [_vm._v("a, b")]), _vm._v("),” in "), _c('em', [_vm._v("Priests, Prophets, and Scribes: Essays on the Formation and Heritage of Second Temple Judaism in Honour of Joseph Blenkinsopp")]), _vm._v(", ed. E. Ulrich et al., JSOTSup 149 (Sheffield: Sheffield Academic, 1992), 153.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-941",
      "id": "foot-note-941"
    }
  }, [_vm._v("29")]), _vm._v(". J. H. Charlesworth, “Announcing a Dead Sea Scrolls Fragment of Nehemiah,” Institute for Judaism and Christian Origins (webpage archived on "), _c('a', {
    attrs: {
      "href": "http://Archive.org",
      "target": "_blank"
    }
  }, [_vm._v("Archive.org")]), _vm._v(", captured October 26, 2008, "), _c('a', {
    attrs: {
      "href": "https://www.web.archive.org/web/20081026024708/",
      "target": "_blank"
    }
  }, [_vm._v("https://web.archive.org/web/20081026024708/")]), _vm._v(" "), _c('a', {
    attrs: {
      "href": "https://http://www.ijco.org/?categoryId=28681",
      "target": "_blank"
    }
  }, [_vm._v("http://www.ijco.org/?categoryId=28681")]), _vm._v("). It should be noted that the authenticity of the Nehemiah fragment is presently under dispute due to provenance and scribal issues. K. Davis et al., “Nine Dubious ‘Dead Sea Scrolls’ Fragments from the Twenty-First Century,” "), _c('em', [_vm._v("DSD")]), _vm._v(" 24 (2017): 189–228; K. Davis, “Caves of Dispute,” "), _c('em', [_vm._v("DSD")]), _vm._v(" 24 (2017): 229–70.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-940",
      "id": "foot-note-940"
    }
  }, [_vm._v("30")]), _vm._v(". Cf. H. K. Harrington, “Intermarriage in Qumran Texts: The Legacy of Ezra-Nehemiah,” in "), _c('em', [_vm._v("Mixed Marriages: Intermarriage and Group Identity in the Second Temple Period")]), _vm._v(", ed. Christian Frevel, LHBOTS 547 (New York: T&T Clark, 2011), 251–79; Carolyn Sharp, “Phinehan Zeal and Rhetorical Strategy in 4QMMT,” "), _c('em', [_vm._v("RevQ")]), _vm._v(" 18 (1997): 211.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-939",
      "id": "foot-note-939"
    }
  }, [_vm._v("31")]), _vm._v(". P. Sacchi, "), _c('em', [_vm._v("The History of the Second Temple Period")]), _vm._v(" (Sheffield: Sheffield Academic, 2000), 135.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-938",
      "id": "foot-note-938"
    }
  }, [_vm._v("32")]), _vm._v(". J. Myers gives various alternatives for the literary and historical order of Ezra and Nehemiah and adopts the view that Nehemiah precedes Ezra ("), _c('em', [_vm._v("Ezra, Nehemiah: A New Translation with Introduction and Commentary")]), _vm._v(", AB 14 [Garden City, NY: Doubleday, 1965], xxxviii–xlviii). From a different angle, L. Fried, argues that if Ezra actually released the priests from corvée labor, it would have been impossible for Nehemiah to rescind this decree and reinstate a priestly corvée later; thus Ezra’s action must have post-dated Nehemiah’s administration ("), _c('em', [_vm._v("The Priest and the Great King: Temple-Palace Relations in the Persian Empire")]), _vm._v(", BJSUCSD 10 [Winona Lake, IN: Eisenbrauns, 2004], 223). U. Kellerman has written one of the best refutations of Ezra preceding Nehemiah (“Erwägungen zum Problem der Ezradatierung,” "), _c('em', [_vm._v("ZAW")]), _vm._v(" 80 [1968]: 55–87).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-937",
      "id": "foot-note-937"
    }
  }, [_vm._v("33")]), _vm._v(". Dates follow the suggestions of A. E. Steinmann, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", Concordia Commentary (St. Louis: Concordia, 2010), 55–58.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-936",
      "id": "foot-note-936"
    }
  }, [_vm._v("34")]), _vm._v(". These governorships are based on the following attestations: Sheshbazzar (Ezra 1:8, 11; 5:14); Zerubbabel (Ezra 3:8; Hag 1:1, 14; Zech 4:6–10); Elnathan (bulla and seal); Yehoezer (jar impression); Ahzai (jar impression); Nehemiah (Neh 5:14; 12:26); Bagohi/Bagoas (Aramaic Papyri); Hezekiah (coins).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-935",
      "id": "foot-note-935"
    }
  }, [_vm._v("35")]), _vm._v(". “Jo-” and “Jeho-” represent short and long forms of the Yahweh prefix. Names differing only in these forms refer to Emperors over Persia.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-934",
      "id": "foot-note-934"
    }
  }, [_vm._v("36")]), _vm._v(". L. Fried, “A Silver Coin of Yo"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ḥ")]), _vm._v("anan Hakkôhēn,” "), _c('em', [_vm._v("Transeuphratène")]), _vm._v(" 25 (2003): 65–66.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-933",
      "id": "foot-note-933"
    }
  }, [_vm._v("37")]), _vm._v(". Proponents of a late Ezra sometimes translate “grandson of Eliashib.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-932",
      "id": "foot-note-932"
    }
  }, [_vm._v("38")]), _vm._v(". Papponymy is attested among the Sanballats (cf. the Wadi Daliyeh Papyri), the Oniads, and the Tobiads; F. M. Cross, “Aspects of Samaritan and Jewish History in Late Persian and Hellenistic Times,” in "), _c('em', [_vm._v("Emerging Judaism")]), _vm._v(", ed. Michael Stone and David Satran (Minneapolis: Fortress, 1989), 49–59; B. Mazar, “The Tobiads,” "), _c('em', [_vm._v("IEJ")]), _vm._v(" 7 (1957): 235, 235n73.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-931",
      "id": "foot-note-931"
    }
  }, [_vm._v("39")]), _vm._v(". But cf. Fried, "), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 227–31, who sees no need for another Johanan-Jaddua pair, arguing that the Johanan of the Elephantine papyri (ca. 410 BCE) was beginning his career of forty years (evidenced by a coin minted in 370 BCE) and that his son Jaddua continued with an equally long career and met Alexander in 333 BCE as Josephus states ("), _c('em', [_vm._v("Jewish Antiquities")]), _vm._v(" 11.326).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-930",
      "id": "foot-note-930"
    }
  }, [_vm._v("40")]), _vm._v(". J. VanderKam, “Jewish High Priests of the Persian Period: Is the List Complete?,” in "), _c('em', [_vm._v("From Joshua to Caiaphas: High Priests after the Exile")]), _vm._v(" (Minneapolis: Fortress, 2004), 43–111.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-929",
      "id": "foot-note-929"
    }
  }, [_vm._v("41")]), _vm._v(". See Yamauchi, “Ezra-Nehemiah,” 584; Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 44; D. Kidner, "), _c('em', [_vm._v("Ezra and Nehemiah: An Introduction and Commentary")]), _vm._v(", TOTC (Leicester: Inter-Varsity, 1979), 152; cf. B. Childs"), _c('em', [_vm._v(", Introduction to the Old Testament as Scripture")]), _vm._v(" (Philadelphia: Fortress, 1979), 636.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-928",
      "id": "foot-note-928"
    }
  }, [_vm._v("42")]), _vm._v(". R. K. Harrison, "), _c('em', [_vm._v("Introduction to the Old Testament")]), _vm._v(" (Grand Rapids: Eerdmans, 1969), 148–49.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-927",
      "id": "foot-note-927"
    }
  }, [_vm._v("43")]), _vm._v(". Some scholars have kept the contemporaneity of Ezra and Nehemiah but emended Artaxerxes’s “seventh year” to “thirty-seventh,” keeping Ezra after Nehemiah. Both numbers begin with the Hebrew letter "), _c('em', [_vm._v("shin")]), _vm._v(". See J. Bright, "), _c('em', [_vm._v("History of Israel")]), _vm._v(" (London: SCM, 1977), 391–402; W. F. Albright, “The Biblical Period,” in "), _c('em', [_vm._v("The Jews: Their History, Culture, and Religion")]), _vm._v(", ed. L. Finkelstein (New York: Harper, 1949), 3–69; cf. E. Yamauchi, “Ezra-Nehemiah,” 585–86.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-926",
      "id": "foot-note-926"
    }
  }, [_vm._v("44")]), _vm._v(". Hoglund ("), _c('em', [_vm._v("Achaemenid Imperial Administration")]), _vm._v(", 236) supports this conclusion by explaining that it is in the best interest of the Persian emperors to secure a law-abiding Jewish community.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-925",
      "id": "foot-note-925"
    }
  }, [_vm._v("45")]), _vm._v(". Talmon, “Ezra and Nehemiah (Books and Men),” 317–28, esp. 324–28.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-924",
      "id": "foot-note-924"
    }
  }, [_vm._v("46")]), _vm._v(". Yamauchi (“Ezra-Nehemiah,” 575–80) suggests that the Chronicler was an editor of the separate sources of Ezra-Nehemiah and Chronicles, a later follower of Ezra’s circle, or even Ezra himself. He places Ezra-Nehemiah first at 440 BCE and then Chronicles at ca. 400 BCE. Williamson places Chronicles at ca. 350 BCE and the final version of Ezra-Nehemiah at 300 BCE. Because of Greek words for coins in Ezra, Torrey thinks it could be as late as 250 BCE (see Yamauchi, “Ezra-Nehemiah,” 577).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-923",
      "id": "foot-note-923"
    }
  }, [_vm._v("47")]), _vm._v(". S. Japhet, “The Relationship between Chronicles and Ezra-Nehemiah,” in "), _c('em', [_vm._v("Congress Volume: Leuven 1989")]), _vm._v(", ed. J. A. Emerton, VTSup 43 (1991), 301–4; J. C. VanderKam, following S. R. Driver, organizes parallel word lists (“Ezra-Nehemiah or Ezra and Nehemiah?,” in Ulrich et al., "), _c('em', [_vm._v("Priests, Prophets and Scribes")]), _vm._v(", 55–75); T. C. Eskenazi ("), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(") focuses on themes.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-922",
      "id": "foot-note-922"
    }
  }, [_vm._v("48")]), _vm._v(". See a full discussion in Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxii. Since the genealogical data places the complete work of Chronicles no earlier than 400 BCE, after Ezra’s death (on the traditional dating), some suggest that a close disciple of Ezra wrote Chronicles (Yamauchi, “Ezra-Nehemiah,” 577–78).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-921",
      "id": "foot-note-921"
    }
  }, [_vm._v("49")]), _vm._v(". Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 48; see his forceful arguments, 47–54.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-920",
      "id": "foot-note-920"
    }
  }, [_vm._v("50")]), _vm._v(". Myers, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", li–lii.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-919",
      "id": "foot-note-919"
    }
  }, [_vm._v("51")]), _vm._v(". See Yamauchi, “Ezra-Nehemiah,” 577.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-918",
      "id": "foot-note-918"
    }
  }, [_vm._v("52")]), _vm._v(". Cited in Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 49. For example, Chronicles favors "), _c('em', [_vm._v("qdš")]), _vm._v(" while Ezra-Nehemiah uses "), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ṭ")]), _c('em', [_vm._v("hr")]), _vm._v(" for purification.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-917",
      "id": "foot-note-917"
    }
  }, [_vm._v("53")]), _vm._v(". J. D. Newsome, as cited in Yamauchi, “Ezra-Nehemiah,” 578.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-916",
      "id": "foot-note-916"
    }
  }, [_vm._v("54")]), _vm._v(". Throntveit, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 9.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-915",
      "id": "foot-note-915"
    }
  }, [_vm._v("55")]), _vm._v(". Steinmann, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", 132–33. Steinmann cites Marcus, the editor of "), _c('em', [_vm._v("BHQ")]), _vm._v(" for Ezra and Nehemiah, who says that the Chronicles version explicates the Ezran verses. S. Japhet dates Chronicles after Ezra-Nehemiah, well into the fourth century BCE ("), _c('em', [_vm._v("1 and 2 Chronicles: A Commentary")]), _vm._v(" [Louisville: Westminster, 1993), 27; so also I. Kalimi, "), _c('em', [_vm._v("The Reshaping of Ancient Israelite History in Chronicles")]), _vm._v(" (Winona Lake, IN: Eisenbrauns, 2005), 9.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-914",
      "id": "foot-note-914"
    }
  }, [_vm._v("56")]), _vm._v(". Cf. VanderKam, “Ezra-Nehemiah or Ezra and Nehemiah?,” 55–75; D. Kraemer, “On the Relationship of the Books of Ezra and Nehemiah,” "), _c('em', [_vm._v("JSOT")]), _vm._v(" 59 (1993): 73–92.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-913",
      "id": "foot-note-913"
    }
  }, [_vm._v("57")]), _vm._v(". Kraemer, “Relationship of the Books of Ezra and Nehemiah,” 77.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-912",
      "id": "foot-note-912"
    }
  }, [_vm._v("58")]), _vm._v(". Kraemer, “Relationship of the Books of Ezra and Nehemiah,” 84–89.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-911",
      "id": "foot-note-911"
    }
  }, [_vm._v("59")]), _vm._v(". See Talmon, “Ezra and Nehemiah (Books and Men),” 318.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-910",
      "id": "foot-note-910"
    }
  }, [_vm._v("60")]), _vm._v(". S. Talmon, “Ezra and Nehemiah,” in "), _c('em', [_vm._v("Literary Guide to the Bible")]), _vm._v(", ed. R. Alter and F. Kermode (London: Collins: 1987), 357–58.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-909",
      "id": "foot-note-909"
    }
  }, [_vm._v("61")]), _vm._v(". L. S. Fried, “Who Wrote Ezra-Nehemiah—and Why Did They?,” in Boda and Redditt, "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah")]), _vm._v(", 75.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-908",
      "id": "foot-note-908"
    }
  }, [_vm._v("62")]), _vm._v(". K. Min, "), _c('em', [_vm._v("The Levitical Authorship of Ezra-Nehemiah")]), _vm._v(", JSOTSup 409 (London: T&T Clark, 2004), 27. See also T. C. Eskenazi, “The Structure of Ezra-Nehemiah and the Integrity of the Book,” "), _c('em', [_vm._v("JBL")]), _vm._v(" 107 (1988): 641–56, who considers the repetition of the people lists as integral to the structure of the book. She explains that the material between Ezra 1:5 and Neh 7:72 is intentionally bracketed by the lists to emphasize the people, not just the leaders, who actualized Cyrus’s decree to rebuild the temple; see 647–50.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-907",
      "id": "foot-note-907"
    }
  }, [_vm._v("63")]), _vm._v(". The term "), _c('em', [_vm._v("n")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ə")]), _c('em', [_vm._v("tînîm")]), _vm._v(" is found outside of Ezra-Nehemiah only in 1 Chr 9:2. For the collocation, “the hand of Yahweh was upon me,” see Ezek 37:1.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-906",
      "id": "foot-note-906"
    }
  }, [_vm._v("64")]), _vm._v(". Min ("), _c('em', [_vm._v("Levitical Authorship of Ezra-Nehemiah")]), _vm._v(", 2–30) surveys the scholarly literature on this, including the work of L. Batten and H. G. M. Williamson. See Eskenazi, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", 37–126; and Mark A. Throntveit, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", Interpretation (Louisville: John Knox, 1992), 4–8.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-905",
      "id": "foot-note-905"
    }
  }, [_vm._v("65")]), _vm._v(". H. K. Harrington, “Holiness and Purity in Ezra-Nehemiah,” in Boda and Redditt, "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah")]), _vm._v(", 98–116.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-904",
      "id": "foot-note-904"
    }
  }, [_vm._v("66")]), _vm._v(". Yamauchi, “Ezra-Nehemiah,” 579.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-903",
      "id": "foot-note-903"
    }
  }, [_vm._v("67")]), _vm._v(". A. Siedlecki suggests a compilation date of 300 BCE (“Contextualizations of Ezra-Nehemiah,” in Boda and Redditt, "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah")]), _vm._v(", 263–76), following H. G. M. Williamson.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-902",
      "id": "foot-note-902"
    }
  }, [_vm._v("68")]), _vm._v(". For more on issues of dating and sequence, see Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 139–44.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-901",
      "id": "foot-note-901"
    }
  }, [_vm._v("69")]), _vm._v(". Eskenazi (“Unity and Disunity in Ezra-Nehemiah,” 316–17) describes these two trends in scholarship: (1) “free standing documents were combined at some specific, definable junctures” (e.g., H. G. M. Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(" [1985]); or (2) there was an “incremental process in which scribal activities responded in a continuous fashion to issues internal to the text and those external, historical circumstances that unfolded” (e.g., J. Wright [2004]). For a full discussion of recent theories regarding redactional layers in Ezra-Nehemiah, see the collected essays in Boda and Redditt, "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-900",
      "id": "foot-note-900"
    }
  }, [_vm._v("70")]), _vm._v(". T. C. Eskenazi, “Reflections on Oded Lipschits: The Fall and Rise of Jerusalem,” "), _c('em', [_vm._v("JHS")]), _vm._v(" 7 (2006).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-899",
      "id": "foot-note-899"
    }
  }, [_vm._v("71")]), _vm._v(". O. Lipschits, "), _c('em', [_vm._v("The Fall and Rise of Jerusalem: Judah under Babylonian Rule")]), _vm._v(" (Winona Lake, IN: Eisenbrauns, 2005). For other estimates, see C. E. Carter, "), _c('em', [_vm._v("The Emergence of Yehud in the Persian Period: A Social and Demographic Study")]), _vm._v(", JSOTSup 294 (Sheffield: Sheffield Academic, 1999), 200–205; a more generous estimate is provided by M. Broshi, “La population de l’ancienne Jérusalem,” "), _c('em', [_vm._v("RB")]), _vm._v(" 82 (1975): 5–14.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-898",
      "id": "foot-note-898"
    }
  }, [_vm._v("72")]), _vm._v(". Sanballat, governor of Samaria, and his dynasty are recorded in several sources, including a bulla inscribed with “belonging to Isaiah, son of Sanballat, governor of Samaria”; the Wadi Daliyeh papyri; and some coins. See E. Stern, "), _c('em', [_vm._v("The Assyrian, Babylonian, and Persian Periods (732–332 B.C.E.)")]), _vm._v(", vol. 2 of "), _c('em', [_vm._v("Archaeology of the Land of the Bible")]), _vm._v(" (New York: Doubleday, 2001), 552.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-897",
      "id": "foot-note-897"
    }
  }, [_vm._v("73")]), _vm._v(". So also Bickerman, "), _c('em', [_vm._v("From Ezra to the Last of the Maccabees")]), _vm._v(", 11; E. Stern, "), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 430–31, explains the differences between the biblical rosters according to the purpose of each list. For a view which excludes Lod and Ono from Yehud, see D. Moffat, "), _c('em', [_vm._v("Ezra’s Social Drama")]), _vm._v(", 31; Moffat refers to the funerary inscription of the Sidonian King Eshmunezer II, who claimed to receive this area from the Persians ("), _c('em', [_vm._v("ANET")]), _vm._v(", 662) and also notes that Neh 6:2 seems to depict Ono as neutral territory.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-896",
      "id": "foot-note-896"
    }
  }, [_vm._v("74")]), _vm._v(". E.g., see Map 6 in Lipschits, "), _c('em', [_vm._v("Fall and Rise of Jerusalem")]), _vm._v(", 183; cf. C. E. Carter, "), _c('em', [_vm._v("Emergence of Yehud")]), _vm._v(", 82–90, for a slightly smaller estimate of Yehud’s area. Moffat ("), _c('em', [_vm._v("Ezra’s Social Drama")]), _vm._v(", 40) surveys the views of Carter and Lipschits and concludes, “Territorial boundaries are better regarded as vague and permeable frontiers rather than hard lines of demarcation.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-895",
      "id": "foot-note-895"
    }
  }, [_vm._v("75")]), _vm._v(". As Moffat ("), _c('em', [_vm._v("Ezra’s Social Drama")]), _vm._v(", 37) points out, a Yehud seal impression has been found at a Persian-era fortress in Kadesh-Barnea, an area far south of Judean control.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-894",
      "id": "foot-note-894"
    }
  }, [_vm._v("76")]), _vm._v(". A. Alt, “Die Rolle Samarias bei der Entstehung des Judentums,” in "), _c('em', [_vm._v("Kleine Schriften zur Geschichte Volkes Israels")]), _vm._v(", vol. 2 (Munich: Beck, 1953), 316–37.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-893",
      "id": "foot-note-893"
    }
  }, [_vm._v("77")]), _vm._v(". P. Briant, "), _c('em', [_vm._v("From Cyrus to Alexander: A History of the Persian Empire")]), _vm._v(", trans. P. T. Daniels (Winona Lake, IN: Eisenbrauns, 2002), 488; translation of "), _c('em', [_vm._v("Histoire de l’Empire perse")]), _vm._v(" (Paris: Librairie Arthème Fayard, 1996).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-892",
      "id": "foot-note-892"
    }
  }, [_vm._v("78")]), _vm._v(". N. Avigad identifies this Shelomith with the daughter of Zerubbabel of 1 Chr 3:19 and dates the seals to the sixth century BCE ("), _c('em', [_vm._v("Bullae and Seals from a Postexilic Judean Archive")]), _vm._v(" [Jerusalem: Institute of Archaeology of the Hebrew University, 1976], 35). See also the discussion in S. Japhet, “Sheshbazzar and Zerubbabel: Against the Background of the Historical and Religious Tendencies of Ezra-Nehemiah, Part 1,” "), _c('em', [_vm._v("ZAW")]), _vm._v(" 94 (1982): 80. For a complete list of the governors over Yehud in the Persian period, see Fried, "), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 183–88, who notes continuous rule. She notes that even though most of the governors had Yahwist names, they were Persian officials ruling at the discretion of the Persian satrap and king.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-891",
      "id": "foot-note-891"
    }
  }, [_vm._v("79")]), _vm._v(". Steinmann, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", 573; cf. Yamauchi, “Ezra-Nehemiah,” 745.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-890",
      "id": "foot-note-890"
    }
  }, [_vm._v("80")]), _vm._v(". Broshi, “La population de l’ancienne Jérusalem,” 5–14; cf. Levine (“Jerusalem,” "), _c('em', [_vm._v("DEJ")]), _vm._v(", 797), who estimates about 5,000 people in the city. For lower estimates of Jerusalem’s population in the Persian period, see Lipschits ("), _c('em', [_vm._v("Fall and Rise of Jerusalem")]), _vm._v(") at 1,000 to 1,250 persons and I. Finkelstein with an even lower number (“Jerusalem in the Persian (and Early Hellenistic) Period and the Wall of Nehemiah,” JSOT 32 [2008]).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-889",
      "id": "foot-note-889"
    }
  }, [_vm._v("81")]), _vm._v(". The main proponent of the Hellenistic context is Finkelstein, “Jerusalem in the Persian (and Early Hellenistic) Period,” 504–7. See counterarguments in D. Ussishkin, “Nehemiah’s City Wall and the Size of Jerusalem,” in "), _c('em', [_vm._v("New Perspectives on Ezra-Nehemiah: History and Historiography, Text, Literature, and Interpretation")]), _vm._v(", ed. I. Kalimi (Winona Lake, IN: Eisenbrauns, 2012), 118–19.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-888",
      "id": "foot-note-888"
    }
  }, [_vm._v("82")]), _vm._v(". Proponents of this view include Avi-Yonah, Tsafrir, Avigad, Williamson, Eshel, Stern, Ritmeyer and Ritmeyer, Geva, Steiner, and Na’aman; see Ussishkin, “Nehemiah’s City Wall and the Size of Jerusalem”; H. Geva, “Small City, Few People,” "), _c('em', [_vm._v("BAR")]), _vm._v(" 32 (2006): 66–68; H. G. M. Williamson, “Nehemiah’s Wall Revisited,” "), _c('em', [_vm._v("PEQ")]), _vm._v(" 116 (1984): 81–88; Y. Tzafrir, “The Walls of Jerusalem in the Period of Nehemiah,” "), _c('em', [_vm._v("Cathedra")]), _vm._v(" 4 (1977): 31–42.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-887",
      "id": "foot-note-887"
    }
  }, [_vm._v("83")]), _vm._v(". Stern, "), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 468.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-886",
      "id": "foot-note-886"
    }
  }, [_vm._v("84")]), _vm._v(". Steinmann, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", 414.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-885",
      "id": "foot-note-885"
    }
  }, [_vm._v("85")]), _vm._v(". J. Simons, "), _c('em', [_vm._v("Jerusalem in the Old Testament: Researches and Theories")]), _vm._v(" (Leiden: Brill, 1952); H. Vincent and A. Steve, "), _c('em', [_vm._v("Jerusalem de l’Ancien Testament: Recherches d’archéologie et d’histoire")]), _vm._v(" (Paris: Gabalda, 1954). This view has been championed in recent times by Ussishkin, “Nehemiah’s City Wall and the Size of Jerusalem,” 101–30.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-884",
      "id": "foot-note-884"
    }
  }, [_vm._v("86")]), _vm._v(". Ussishkin, “Nehemiah’s City Wall and the Size of Jerusalem,” 109, 122.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-883",
      "id": "foot-note-883"
    }
  }, [_vm._v("87")]), _vm._v(". See Lipschits, “Persian Period Finds from Jerusalem,” who estimates the population of Jerusalem at this time to be about 1,000–1,250 persons.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-882",
      "id": "foot-note-882"
    }
  }, [_vm._v("88")]), _vm._v(". Japhet, “Composition and Chronology,” 215.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-881",
      "id": "foot-note-881"
    }
  }, [_vm._v("89")]), _vm._v(". Eskenazi, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(". A similar conclusion was reached by R. J. Coggins, "), _c('em', [_vm._v("The Books of Ezra and Nehemiah")]), _vm._v(" (Cambridge: Cambridge University Press, 1976), 107, who stated that the author of Ezra-Nehemiah used theological rather than historical criteria: The most important matter is first the rebuilding of the temple, and then the purifying of the people, and then the walls of the city, the “grand climax” being the reading of the law.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-880",
      "id": "foot-note-880"
    }
  }, [_vm._v("90")]), _vm._v(". Japhet, “Composition and Chronology,” 210.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-879",
      "id": "foot-note-879"
    }
  }, [_vm._v("91")]), _vm._v(". Throntveit ("), _c('em', [_vm._v("Ezra-Nehmiah")]), _vm._v(", 4) divides the book into two large sections (Ezra 1:1–Neh 7:3; Neh 7:4–12:43; and a “coda,” 12:44–13:31). The first large section links the community with the Israelite past, and the second seeks “renewal and reform.” The coda simply illustrates the “tension between reform and subsequent relapse” and “invites the reader to contemplate the continuing need for present commitment in the life of faith.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-878",
      "id": "foot-note-878"
    }
  }, [_vm._v("92")]), _vm._v(". S. Talmon, “Ezra and Nehemiah,” in Alter and Kermode, "), _c('em', [_vm._v("Literary Guide to the Bible")]), _vm._v(", 358.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-877",
      "id": "foot-note-877"
    }
  }, [_vm._v("93")]), _vm._v(". L. Fried (“Who Wrote Ezra-Nehemiah?,” 94–95) notes that Ezra seems like a second Moses not only as a lawgiver but also as the central character in a certain sequence of events; cf. T. Reinmuth, “Nehemiah 8 and the Authority of Torah in Ezra-Nehemiah,” in Boda and Redditt, "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah")]), _vm._v(", 247, who identifies parallels between Exodus and Nehemiah: the people believed the word of God to Moses and fell down on their faces and worshiped (Exod 4:30–31 // Neh 8:6; cf. Exod 24:7 // Neh 8:3).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-876",
      "id": "foot-note-876"
    }
  }, [_vm._v("94")]), _vm._v(". Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 37. Also, the language of the early prophets is used to highlight the return as a fulfillment of prophecy (cf. Jer 31 with Ezra 7–9 for their use of similar themes); see J. G. McConville, “Ezra-Nehemiah and the Fulfillment of Prophecy,” "), _c('em', [_vm._v("VT")]), _vm._v(" 36 (1986): 216, 220; cited in Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 57, who thinks the word “remnant” in Ezra 9:14–15 is influenced by Isaiah and Jeremiah.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-875",
      "id": "foot-note-875"
    }
  }, [_vm._v("95")]), _vm._v(". L. Grabbe, “‘Mind the Gaps’: Ezra, Nehemiah and the Judean Restoration,” in "), _c('em', [_vm._v("Restoration: Old Testament, Jewish, and Christian Perspectives")]), _vm._v(", ed. J. M. Scott (Leiden: Brill, 2001), 84.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-874",
      "id": "foot-note-874"
    }
  }, [_vm._v("96")]), _vm._v(". Throntveit, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 4.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-873",
      "id": "foot-note-873"
    }
  }, [_vm._v("97")]), _vm._v(". This analysis comes from Throntveit, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 52.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-872",
      "id": "foot-note-872"
    }
  }, [_vm._v("98")]), _vm._v(". Noss and Thomas, "), _c('em', [_vm._v("Handbook on Ezra and Nehemiah")]), _vm._v(", 14.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-871",
      "id": "foot-note-871"
    }
  }, [_vm._v("99")]), _vm._v(". Throntveit, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 6. Throntveit claims that, in Ezra, both the three major units (Ezra 1–6; 7–8; 9–10) and the majority of their subunits (1:7–11; 4:8–23; 5:1–6:22; 7:1b–5; 7:11–26; 9:1–10:1a; 10:1b–44) are structured chiastically.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-870",
      "id": "foot-note-870"
    }
  }, [_vm._v("100")]), _vm._v(". S. Talmon, “Presentation of Synchroneity and Simultaneity in Biblical Narrative,” in "), _c('em', [_vm._v("Studies in Hebrew Narrative Art Throughout the Age")]), _vm._v("s, ed. J. Heinemann and S. Werses, ScrHier 27 (Jerusalem: Magnes, 1978), 9–26, esp. 17.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-869",
      "id": "foot-note-869"
    }
  }, [_vm._v("101")]), _vm._v(". Breneman ("), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 38) claims that the reappearance of Ezra in Neh 8 and the way the author inserts Neh 8–10 between Neh 7:73 and 11:1 in a parenthetical manner are typical of the overall style.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-868",
      "id": "foot-note-868"
    }
  }, [_vm._v("102")]), _vm._v(". Talmon, “Ezra and Nehemiah (Books and Men),” 322.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-867",
      "id": "foot-note-867"
    }
  }, [_vm._v("103")]), _vm._v(". Talmon, “Ezra and Nehemiah,” 359–60.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-866",
      "id": "foot-note-866"
    }
  }, [_vm._v("104")]), _vm._v(". Noss and Thomas, "), _c('em', [_vm._v("Handbook on Ezra and Nehemiah")]), _vm._v(", 14.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-865",
      "id": "foot-note-865"
    }
  }, [_vm._v("105")]), _vm._v(". Kidner, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", 169–70.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-864",
      "id": "foot-note-864"
    }
  }, [_vm._v("106")]), _vm._v(". Talmon (“Ezra and Nehemiah,” 363) suggests that many of these phrases are not dates or even sequences but formulaic literary devices creating an impression rather than an accurate time. Phrases of this nature include, “Now after these things” (Ezra 7:1), “Now when these things were done” (Ezra 9:1), and “And before this” (Neh 13:4).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-863",
      "id": "foot-note-863"
    }
  }, [_vm._v("107")]), _vm._v(". Talmon (“Ezra and Nehemiah,” 364) posits compilation in 400 BCE. Another criticism against this literary method is registered by those who feel that literary critics have sidestepped the responsibility of attempting to reconstruct biblical history. As Blenkinsopp ("), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 42) says, it has “a tendency to circumvent the tension between confessional and critically reconstructed history. This tension seems to be inescapable, and it is difficult to see how theology can maintain its integrity, or avoid degenerating into ideology in the pejorative sense, if it is avoided.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-862",
      "id": "foot-note-862"
    }
  }, [_vm._v("108")]), _vm._v(". D. P. Moffat, "), _c('em', [_vm._v("Ezra’s Social Drama: Identity Formation, Marriage and Social Conflict in Ezra 9 and 10")]), _vm._v(" (New York: Bloomsbury, 2013), 7.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-861",
      "id": "foot-note-861"
    }
  }, [_vm._v("109")]), _vm._v(". M. Douglas, "), _c('em', [_vm._v("Natural Symbols: Explorations in Cosmology")]), _vm._v(" (London: Routledge, 1996), 54–68.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-860",
      "id": "foot-note-860"
    }
  }, [_vm._v("110")]), _vm._v(". D. Janzen, “Scholars, Witches, Ideologues, and What the Text Said: Ezra 9–10 and Its Interpretation,” in "), _c('em', [_vm._v("Approaching Yehud: New Approaches to the Study of the Persian Period")]), _vm._v(", ed. J. L. Berquist, Semeia Studies 50 (Atlanta: SBL Press; Leiden: Brill, 2007), 64–66; D. Janzen, "), _c('em', [_vm._v("Witch-hunts, Purity and Social Boundaries: The Expulsion of the Foreign Women in Ezra 9–10")]), _vm._v(" (Sheffield: Sheffield Academic, 2002), 57–62.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-859",
      "id": "foot-note-859"
    }
  }, [_vm._v("111")]), _vm._v(". Moffat, "), _c('em', [_vm._v("Ezra’s Social Drama")]), _vm._v(", 186.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-858",
      "id": "foot-note-858"
    }
  }, [_vm._v("112")]), _vm._v(". Moffat, "), _c('em', [_vm._v("Ezra’s Social Drama")]), _vm._v(", 186.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-857",
      "id": "foot-note-857"
    }
  }, [_vm._v("113")]), _vm._v(". M. Douglas, "), _c('em', [_vm._v("Purity and Danger")]), _vm._v(" (London: Routledge, 1966), 124–25.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-856",
      "id": "foot-note-856"
    }
  }, [_vm._v("114")]), _vm._v(". P. Sacchi ("), _c('em', [_vm._v("History of the Second Temple Period")]), _vm._v(", 453–54) sees the foreigner as “manifestly impure” already with Ezekiel, based on Ezek 44:6–9: “No foreigner, uncircumcised in heart and flesh, of all the foreigners who are among the people of Israel, shall enter my sanctuary.” But other scholars consider this text an allusion to the practice of using foreign slaves to serve the priesthood (cf. Ezra 8:20). Sacchi argues that the universalistic trend in the time before Ezra and Nehemiah that allowed gentiles to offer sacrifices and be a part of Israelite society (Isa 56:3–6; Lev 17:8; Num 15:14) was truncated due to Ezra and Nehemiah’s reforms but resurfaced later. Sacchi claims that the impoverishment of the priests in the sixth century BCE (cf. Mal 1:8) led to their linkage with the powerful families of Samaria and Ammon and thus prepared the way for Trito-Isaiah (late sixth century BCE) to claim that all people can worship Yahweh (119–21). Ruth and Jonah are to be set against the backdrop of the segregationist-integrationist discussion as well.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-855",
      "id": "foot-note-855"
    }
  }, [_vm._v("115")]), _vm._v(". D. L. Smith, “The Politics of Ezra: Sociological Indicators of Postexilic Judaean Society,” in "), _c('em', [_vm._v("Persian Period")]), _vm._v(", vol. 1 of "), _c('em', [_vm._v("Second Temple Studies")]), _vm._v(", ed. P. R. Davies, JSOTSup 117 (Sheffield: JSOT Press, 1991), 84–85.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-854",
      "id": "foot-note-854"
    }
  }, [_vm._v("116")]), _vm._v(". S. Olyan, "), _c('em', [_vm._v("Rites and Rank: Hierarchy in Biblical Representations of Cult")]), _vm._v(" (Princeton: Princeton University Press, 2000), 60; see also S. Olyan, “Purity Ideology in Ezra-Nehemiah as a Tool to Reconstitute the Community,” "), _c('em', [_vm._v("JSJ")]), _vm._v(" 35 (2004): 1–16.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-853",
      "id": "foot-note-853"
    }
  }, [_vm._v("117")]), _vm._v(". See V. Turner, "), _c('em', [_vm._v("Dramas, Fields, and Metaphors: Symbolic Action in Human Society")]), _vm._v(" (Ithaca, NY: Cornell University Press, 1974).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-852",
      "id": "foot-note-852"
    }
  }, [_vm._v("118")]), _vm._v(". F. Gorman, "), _c('em', [_vm._v("The Ideology of Ritual: Space, Time and Status in the Priestly Theology")]), _vm._v(", JSOTSup 91 (Sheffield: JSOT Press, 1990), 28, 29, 232.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-851",
      "id": "foot-note-851"
    }
  }, [_vm._v("119")]), _vm._v(". For a full explanation of the intermarriage crisis in light of Turner’s work, see Moffat, "), _c('em', [_vm._v("Ezra’s Social Drama")]), _vm._v(", 155–63.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-850",
      "id": "foot-note-850"
    }
  }, [_vm._v("120")]), _vm._v(". The work of D. Moffat is a good example of this combination; see Moffat, "), _c('em', [_vm._v("Ezra’s Social Drama")]), _vm._v(", 6.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-849",
      "id": "foot-note-849"
    }
  }, [_vm._v("121")]), _vm._v(". Grabbe, “Mind the Gaps,” 83–104. Grabbe sees the story constructed around three “Foundation Legends,” each centering on the restoration and accomplishments of key leaders (102).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-848",
      "id": "foot-note-848"
    }
  }, [_vm._v("122")]), _vm._v(". C. C. Torrey, for example, believes that Ezra-Nehemiah, except for Neh 1–6, is total fiction ("), _c('em', [_vm._v("The Composition and Historical Value of Ezra-Nehemiah")]), _vm._v(", BZAW [Giessen: J. Ricker, 1896], 65). Grabbe (“Mind the Gaps,” 92) states that Ezra seems to carry out some of the foundational activities that one would have assumed were already accomplished by Joshua, Zerubbabel, or Sheshbazzar, including bringing temple vessels back from exile (Ezra 8:26–28, 33; cf. Ezra 1:8–11) and getting Levites to return with him (Ezra 8:15–20; cf. 2:40–58). Also, Grabbe (90) questions Ezra’s authority over all of the Transeuphrates (Ezra 4:20), since he only exercises religious authority and is not very assertive with that (see Ezra 10:1ff.). Grabbe thinks that the Persian sources embedded in the narrative sound like Jewish propaganda and considers their Aramaic later than the Achaemenid period. He is also uncomfortable with Ezra’s presentation of the “law of God” in Neh 8, when the law has been to a large degree in practice before his coming (e.g., Ezra 3:1–4; 6:19–22). Grabbe has more respect for the Nehemiah Memoir, which describes a man with faults, in conflict with the locals, and with a difficult personality; although it is biased, it does seem to him like an autobiographical perspective.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-847",
      "id": "foot-note-847"
    }
  }, [_vm._v("123")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 4–5.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-846",
      "id": "foot-note-846"
    }
  }, [_vm._v("124")]), _vm._v(". Grabbe (“Mind the Gaps,” 85, 99–100) doubts the amount of 750 talents of silver and 100 talents of gold as well as other precious items (more than 25 tons) that the Persian king and others reputedly contributed to the return, in view of the statement of Herodotus that all of the Transeuphrates produced only 350 talents of silver annually ("), _c('em', [_vm._v("Histories")]), _vm._v(" 3.91) and of Ezra’s cavalier protection of it without an armed escort.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-845",
      "id": "foot-note-845"
    }
  }, [_vm._v("125")]), _vm._v(". See CAP, no. 30, ll. 25–26; Cyrus Cylinder, "), _c('em', [_vm._v("ANET")]), _vm._v(", 316. R. de Vaux, “The Decrees of Cyrus and Darius on the Rebuilding of the Temple,” in "), _c('em', [_vm._v("The Bible and the Ancient Near East")]), _vm._v(", ed. R. de Vaux and D. McHugh (Garden City, NY: Doubleday, 1971), 63–96.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-844",
      "id": "foot-note-844"
    }
  }, [_vm._v("126")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 221.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-843",
      "id": "foot-note-843"
    }
  }, [_vm._v("127")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 223–31, esp. 227.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-842",
      "id": "foot-note-842"
    }
  }, [_vm._v("128")]), _vm._v(". M. Jaffee, "), _c('em', [_vm._v("Dynamics of Texts and Traditions")]), _vm._v(" (Upper Saddle River, NJ: Prentice-Hall, 1997), 56.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-841",
      "id": "foot-note-841"
    }
  }, [_vm._v("129")]), _vm._v(". Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 10–11.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-840",
      "id": "foot-note-840"
    }
  }, [_vm._v("130")]), _vm._v(". For a complete list with explanatory details, see Stern, "), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 360–66.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-839",
      "id": "foot-note-839"
    }
  }, [_vm._v("131")]), _vm._v(". Curiously, Cyrus left Astyages alive and even married his daughter; see Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 17, 32.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-838",
      "id": "foot-note-838"
    }
  }, [_vm._v("132")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 42–43.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-837",
      "id": "foot-note-837"
    }
  }, [_vm._v("133")]), _vm._v(". Laurie E. Pearce, “Identifying Judeans and Judean Identity in the Babylonian Evidence,” in "), _c('em', [_vm._v("Exile and Return: The Babylonian Context")]), _vm._v(", ed. Jonathan Stokl and Caroline Waerzeggers, BZAW 478 (Berlin: de Gruyter, 2015), 20.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-836",
      "id": "foot-note-836"
    }
  }, [_vm._v("134")]), _vm._v(". Cf. M. Stone, "), _c('em', [_vm._v("Scriptures, Sects and Visions: A Profile of Judaism from Ezra to the Jewish Revolts")]), _vm._v(" (Philadelphia: Fortress, 1980), 14.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-835",
      "id": "foot-note-835"
    }
  }, [_vm._v("135")]), _vm._v(". Myers, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxv.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-834",
      "id": "foot-note-834"
    }
  }, [_vm._v("136")]), _vm._v(". Yamauchi, “Ezra-Nehemiah,” 569; Bickerman, "), _c('em', [_vm._v("From Ezra to the Last of the Maccabees")]), _vm._v(", 33.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-833",
      "id": "foot-note-833"
    }
  }, [_vm._v("137")]), _vm._v(". E. Stern ("), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 350) states that under the Babylonians, “almost no material finds remain.” While the country was populated, the numbers were very small, with large parts of towns completely or partially destroyed. Only in the northern part of Judah and probably Ammon were the populations relatively untouched. Stern refers to the “half-empty land” based on the excavations of Y. Shiloh, N. Avigad, and B. Mazar (580–81).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-832",
      "id": "foot-note-832"
    }
  }, [_vm._v("138")]), _vm._v(". Yamauchi (“Ezra-Nehemiah,” 568) notes that the Babylonians apparently did not share the Assyrian policy of mixing captives in order to break down ethnic loyalties and the possibility of rebellion.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-831",
      "id": "foot-note-831"
    }
  }, [_vm._v("139")]), _vm._v(". Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 29.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-830",
      "id": "foot-note-830"
    }
  }, [_vm._v("140")]), _vm._v(". R. Albertz and B. Becking, “Problems and Possibilities: Perspectives on Postexilic Yahwism,” in "), _c('em', [_vm._v("Yahwism after the Exile: Perspectives on Israelite Religion in the Persian Era: Papers Read at the First Meeting of the European Association for Biblical Studies, Utrecht, 6–9 August, 2000")]), _vm._v(", ed. R. Albertz and B. Becking (Assen: Van Gorcum, 2003), xi–xxi, esp. xi; E. Stern, “What Happened to the Cult Figurines? Israelite Religion Purified after the Exile,” "), _c('em', [_vm._v("BAR")]), _vm._v(" 15 (1989): 22–29, 53; U. Hübner, “Das Fragment einer Tonfigurine vom Tell el-Milh: Überlegungen zur Funktion der sog. Pfeiler-figurinen in der israelitischen Volksreligion,” "), _c('em', [_vm._v("ZDPV")]), _vm._v(" 105 (1989): 47–55.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-829",
      "id": "foot-note-829"
    }
  }, [_vm._v("141")]), _vm._v(". Kidner, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", 16; Stern, "), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 424.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-828",
      "id": "foot-note-828"
    }
  }, [_vm._v("142")]), _vm._v(". F. Cross, “Personal Names in the Samaria Papyri,” "), _c('em', [_vm._v("BASOR")]), _vm._v(" 344 (November 2006): 75–90.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-827",
      "id": "foot-note-827"
    }
  }, [_vm._v("143")]), _vm._v(". E. Stern and Y. Magen, “Archaeological Evidence for the First Stage of the Samaritan Temple on Mount Gerizim,” "), _c('em', [_vm._v("IEJ")]), _vm._v(" 52 (2002): 49–57.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-826",
      "id": "foot-note-826"
    }
  }, [_vm._v("144")]), _vm._v(". Bickerman, "), _c('em', [_vm._v("From Ezra to the Last of the Maccabees")]), _vm._v(", 36.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-825",
      "id": "foot-note-825"
    }
  }, [_vm._v("145")]), _vm._v(". Myers, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxi.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-824",
      "id": "foot-note-824"
    }
  }, [_vm._v("146")]), _vm._v(". Stern, "), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 426–27.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-823",
      "id": "foot-note-823"
    }
  }, [_vm._v("147")]), _vm._v(". However, the institution of satrapies did not begin with Darius and is firmly attested in the time of Cyrus (Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 64).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-822",
      "id": "foot-note-822"
    }
  }, [_vm._v("148")]), _vm._v(". See Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 68–69, for the difference between the two types of contributions.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-821",
      "id": "foot-note-821"
    }
  }, [_vm._v("149")]), _vm._v(". Both versions carry a polemic, Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 388. “Gifts” are suspect, too, and “voluntary tax” is no doubt according to the desires of the king and not just a loving subject population (Briant, 395). The Murashu family in Babylonia is still thriving under Darius II; they were given lands as payment for collecting the royal taxes and gifts, which were more like fees (Briant, 398).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-820",
      "id": "foot-note-820"
    }
  }, [_vm._v("150")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 404–5.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-819",
      "id": "foot-note-819"
    }
  }, [_vm._v("151")]), _vm._v(". J. Naveh, “Gleanings of Some Pottery Inscriptions,” "), _c('em', [_vm._v("IEJ")]), _vm._v(" 46 (1996): 44–51.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-818",
      "id": "foot-note-818"
    }
  }, [_vm._v("152")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 404–5.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-817",
      "id": "foot-note-817"
    }
  }, [_vm._v("153")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 64.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-816",
      "id": "foot-note-816"
    }
  }, [_vm._v("154")]), _vm._v(". Demotic Papyrus 215 of the Bibliotheque Nationale of Paris; cf. Artaxerxes’s later authorization of Ezra to teach the law of the Jews (Ezra 7:25).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-815",
      "id": "foot-note-815"
    }
  }, [_vm._v("155")]), _vm._v(". Fried, "), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 156.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-814",
      "id": "foot-note-814"
    }
  }, [_vm._v("156")]), _vm._v(". According to Fried ("), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 194–95), this escort did not come to provide safety on the roads, which was a feature of Persian highways (see Herodotus, "), _c('em', [_vm._v("Histories")]), _vm._v(" 5.52), but to man the citadel. Cf. Ezra 8:21–23, where Ezra is embarrassed to ask for an armed escort from the king.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-813",
      "id": "foot-note-813"
    }
  }, [_vm._v("157")]), _vm._v(". Carter, "), _c('em', [_vm._v("Emergence of Yehud")]), _vm._v(", 284; this is a large number of garrisons for a province slightly more than fifty kilometers square.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-812",
      "id": "foot-note-812"
    }
  }, [_vm._v("158")]), _vm._v(". Fried ("), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 200) notes that the presence of foreign troops alone would have brought distress among the local Jewish population (cf. 1 Macc 1:33–36).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-811",
      "id": "foot-note-811"
    }
  }, [_vm._v("159")]), _vm._v(". But the latter was primarily a matter of prestige rather than economics (Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 409). Through the fifth century BCE, Judeans and most of the realm, the Greek cities of Asia Minor being a notable exception, still used weight instead of coinage (Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 406; see Neh 5:4–6).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-810",
      "id": "foot-note-810"
    }
  }, [_vm._v("160")]), _vm._v(". Stern ("), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 567, 582) explains that although the Persians had an official coinage, by the end of the fifth century, the satrapies could mint their own money for local business. He also thinks that the local mint was probably connected to the Jerusalem Temple since several Judean coins have the names of various priests on them, but these coins do not predate the fourth century BCE.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-809",
      "id": "foot-note-809"
    }
  }, [_vm._v("161")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 364.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-808",
      "id": "foot-note-808"
    }
  }, [_vm._v("162")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 371. Stone ("), _c('em', [_vm._v("Scriptures, Sects and Visions")]), _vm._v(", 13) explains that, heretofore, the Mesopotamian empires had written various languages in cuneiform, but the Persians made Aramaic the actual lingua franca of the east.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-807",
      "id": "foot-note-807"
    }
  }, [_vm._v("163")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 368–69; see Neh 2:9; Ezra 8:22, 31.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-806",
      "id": "foot-note-806"
    }
  }, [_vm._v("164")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 474.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-805",
      "id": "foot-note-805"
    }
  }, [_vm._v("165")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 169–70.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-804",
      "id": "foot-note-804"
    }
  }, [_vm._v("166")]), _vm._v(". Cf. Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 171.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-803",
      "id": "foot-note-803"
    }
  }, [_vm._v("167")]), _vm._v(". Myers, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxix.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-802",
      "id": "foot-note-802"
    }
  }, [_vm._v("168")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 473. The term "), _c('em', [_vm._v("wakf")]), _vm._v(" refers to property that the owner has relinquished and dedicated to temple authorities, who must use its income for charitable purposes. Briant describes Darius’s patronage of the interment of an Apis bull (479–80). The satrap was also influential; see the satrap’s naming of a temple administrator for the temple of Khnum at Elephantine (492–491 BCE) (474).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-801",
      "id": "foot-note-801"
    }
  }, [_vm._v("169")]), _vm._v(". A. Finitsis, "), _c('em', [_vm._v("Visions and Eschatology: A Socio-Historical Analysis of Zechariah 1–6")]), _vm._v(", LSTS (London: T & T Clark, 2011), 79–80.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-800",
      "id": "foot-note-800"
    }
  }, [_vm._v("170")]), _vm._v(". J. Blenkinsopp, “Temple and Society in Achaemenid Judah,” in "), _c('em', [_vm._v("Persian Period")]), _vm._v(", vol. 1 of "), _c('em', [_vm._v("Second Temple Studies")]), _vm._v(", ed. P. R. Davies, JSOTSup 117 (Sheffield: JSOT Press, 1991), 26.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-799",
      "id": "foot-note-799"
    }
  }, [_vm._v("171")]), _vm._v(". Although the term “satrap” is used in Ezra 5:3 to designate Tattenai’s position, he was clearly not in control of his own satrapy but subject to the satrap of Babylonia and the Transeuphrates. Nevertheless, tribute must have been organized by subregion, since Tattenai is expected to manage the “royal revenue—that is, from the tribute of Transeuphrates ["), _c('em', [_vm._v("Ebir Nari")]), _vm._v("]” (Ezra 6:8); see Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 487, who suggests that Tattenai’s residence was in Damascus.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-798",
      "id": "foot-note-798"
    }
  }, [_vm._v("172")]), _vm._v(". J. Berquist, “The Social Context of Postexilic Judaism,” in "), _c('em', [_vm._v("Passion, Vitality and Foment")]), _vm._v(": "), _c('em', [_vm._v("The Dynamics of Second Temple Judaism")]), _vm._v(", ed. L. M. Luker (Harrisburg, PA: Trinity Press International, 2001), 38–39. This policy did not continue during Xerxes’s reign, but taxes increased and the burden of the temple’s support fell on the laity (44).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-797",
      "id": "foot-note-797"
    }
  }, [_vm._v("173")]), _vm._v(". Fried ("), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 223) points out that exemption from work corvée would be implied as well since it invariably accompanied taxes and tribute.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-796",
      "id": "foot-note-796"
    }
  }, [_vm._v("174")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 493.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-795",
      "id": "foot-note-795"
    }
  }, [_vm._v("175")]), _vm._v(". Briant ("), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 578) has doubts about Ctesias’s account and so is not sure that Megabyzus ever became a satrap or that he ever revolted. If so, he may be the first satrap to revolt against the Persian emperor.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-794",
      "id": "foot-note-794"
    }
  }, [_vm._v("176")]), _vm._v(". Yamauchi, “Ezra-Nehemiah,” 571; J. Berquist, "), _c('em', [_vm._v("Judaism in Persia’s Shadow: A Social and Historical Approach")]), _vm._v(" (Minneapolis: Fortress, 1995), 110–11. See a full historical discussion of Persian period intrigues in Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 65.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-793",
      "id": "foot-note-793"
    }
  }, [_vm._v("177")]), _vm._v(". Berquist, "), _c('em', [_vm._v("Judaism in Persia’s Shadow")]), _vm._v(", 106.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-792",
      "id": "foot-note-792"
    }
  }, [_vm._v("178")]), _vm._v(". Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 67.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-791",
      "id": "foot-note-791"
    }
  }, [_vm._v("179")]), _vm._v(". B. Becking outlines five elements of Ezra’s commission: (1) surveying the degree of observance of the Jewish law; (2) transferring financial support to the temple in Jerusalem; (3) implementing a funding system at the temple; (4) keeping cultic personnel free of the burden of taxes; and (5) establishing magistrates and judges (“Law as Expression of Religion (Ezra 7–10),” in Becking and Albertz, "), _c('em', [_vm._v("Yahwism after the Exile")]), _vm._v(", 25); cf. other lists in Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", 98; Eskenazi, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", 65; Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 146.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-790",
      "id": "foot-note-790"
    }
  }, [_vm._v("180")]), _vm._v(". See P. Frei, “Zentralgewalt und Lokalautonomie im Achämenidenreich,” in "), _c('em', [_vm._v("Reichsidee und Reichsorganisation im Perserreich")]), _vm._v(", by P. Frei and K. Koch, 2nd ed., OBO 55 (Fribourg: Universitätsverlag, 1996), who examines many contemporary texts and inscriptions from Egypt, Asia Minor, and Palestine. Briant ("), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 510–11) explains that in this way, local populations were, at least in theory, protected from the arbitrary impulses of an unwise satrap. On the other hand, according to L. Fried ("), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 217), the laws were just a nod at justice throughout the empire, a sort of abstraction. In reality, the judges would decide cases at will; their power lay in their position, not in the law codes. For example, as governor, Nehemiah had the authority and power to promulgate whatever law he wished.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-789",
      "id": "foot-note-789"
    }
  }, [_vm._v("181")]), _vm._v(". Cf. J. W. Watts, ed., "), _c('em', [_vm._v("Persia and Torah: The Theory of Imperial Authorization of the Pentateuch")]), _vm._v(", SymS 17 (Atlanta: SBL Press, 2001).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-788",
      "id": "foot-note-788"
    }
  }, [_vm._v("182")]), _vm._v(". Berquist, "), _c('em', [_vm._v("Judaism in Persia’s Shadow")]), _vm._v(", 112; cf. Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 111.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-787",
      "id": "foot-note-787"
    }
  }, [_vm._v("183")]), _vm._v(". Myers ("), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxxiv) cites M. Noth, "), _c('em', [_vm._v("The History of Israel")]), _vm._v(" (1958), 324.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-786",
      "id": "foot-note-786"
    }
  }, [_vm._v("184")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 585.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-785",
      "id": "foot-note-785"
    }
  }, [_vm._v("185")]), _vm._v(". This tension represents a mixture of ethnicities and religions brought on by the Assyrian policy of intermingling captives to reduce loyalties. In addition, there was an age-old conflict between the Northern Kingdom of Israel, represented by the Samarians, and the Southern Kingdom, from which most of the returnees descended (see "), _c('span', {
    attrs: {
      "href": "#/014_Chapter03a#exc12",
      "title": "Link target outside of Sample"
    }
  }, [_vm._v("Excursus 12, “Samarians and Samaritans,” pp. 294–96")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-784",
      "id": "foot-note-784"
    }
  }, [_vm._v("186")]), _vm._v(". According to Josephus, the wall was built over two years and four months ("), _c('em', [_vm._v("Antiquities")]), _vm._v(" 11.5). Perhaps this length of time takes in the whole project, including the construction of towers and gates (Myers, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxxiv).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-783",
      "id": "foot-note-783"
    }
  }, [_vm._v("187")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 605.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-782",
      "id": "foot-note-782"
    }
  }, [_vm._v("188")]), _vm._v(". L. H. Schiffman notes that they agreed to discontinue animal sacrifices and accompanying offerings ("), _c('em', [_vm._v("From Text to Tradition: A History of Judaism in Second Temple and Rabbinic Times")]), _vm._v(" [Hoboken, NJ: Ktav, 1991], 42).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-781",
      "id": "foot-note-781"
    }
  }, [_vm._v("189")]), _vm._v(". Aramaic seals from the fifth or sixth centuries have been found in the Transjordan with the name of the province of Ammon, along with an official inscribed on them (Stern, "), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 552). Briant ("), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 587) suggests that the Persians may even have recognized Tobiah as governor in the Transjordan.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-780",
      "id": "foot-note-780"
    }
  }, [_vm._v("190")]), _vm._v(". Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(", 587.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-779",
      "id": "foot-note-779"
    }
  }, [_vm._v("191")]), _vm._v(". S. Talmon, “‘Exile’ and ‘Restoration’ in the Conceptual World of Ancient Judaism,” in Scott, "), _c('em', [_vm._v("Restoration")]), _vm._v(", 112.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-778",
      "id": "foot-note-778"
    }
  }, [_vm._v("192")]), _vm._v(". J. Milgrom explains the difference in status between priests and Levites from the purification service described in Num 8:5–22 ("), _c('em', [_vm._v("The JPS Torah Commentary: Numbers")]), _vm._v(" [Philadelphia: Jewish Publication Society, 1990], 61). Whereas the priests have a holy status due to their anointing oil, the Levites do not and “are forbidden to enter the Tent or officiate at the altar” (cf. Num 18:3–4).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-777",
      "id": "foot-note-777"
    }
  }, [_vm._v("193")]), _vm._v(". Bickerman, "), _c('em', [_vm._v("From Ezra to the Last of the Maccabees")]), _vm._v(", 18.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-776",
      "id": "foot-note-776"
    }
  }, [_vm._v("194")]), _vm._v(". In this regard, B. Becking (“Law as Expression of Religion,” 23) draws attention to the three verbs in the verse.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-775",
      "id": "foot-note-775"
    }
  }, [_vm._v("195")]), _vm._v(". Talmon, “‘Exile’ and ‘Restoration,’” 134–41.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-774",
      "id": "foot-note-774"
    }
  }, [_vm._v("196")]), _vm._v(". For the older view, see G. von Rad, "), _c('em', [_vm._v("Theologie des Alten Testaments")]), _vm._v(", 6th ed. (Munich: Kaiser, 1969), 1:98–105, 214–15; T. C. Vriezen, "), _c('em', [_vm._v("Hoofdlijnen der Theologie van het Oude Testament")]), _vm._v(", 3rd ed. (Wageningen: Veenman, 1966), esp. 309–20, 435; H. H. Schaeder"), _c('em', [_vm._v(", Esra der Schreiber")]), _vm._v(" (Tübingen: Mohr, 1930). For a modern challenge, see W. Brueggemann, "), _c('em', [_vm._v("Theology of the Old Testament: Testimony, Dispute, Advocacy")]), _vm._v(" (Minneapolis: Fortress, 1997), esp. 93–95, 107–12.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-773",
      "id": "foot-note-773"
    }
  }, [_vm._v("197")]), _vm._v(". Respectively, M. Fishbane, "), _c('em', [_vm._v("Biblical Interpretation in Ancient Israel")]), _vm._v(" (Oxford: Clarendon, 1985); Talmon, “‘Exile’ and ‘Restoration.’”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-772",
      "id": "foot-note-772"
    }
  }, [_vm._v("198")]), _vm._v(". E. P. Sanders, "), _c('em', [_vm._v("Judaism: Practice and Belief, 63 BCE–66 CE")]), _vm._v(" (London: SCM; Philadelphia: Trinity Press International, 1992).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-771",
      "id": "foot-note-771"
    }
  }, [_vm._v("199")]), _vm._v(". Schiffman"), _c('em', [_vm._v(", From Text to Tradition")]), _vm._v(", 4–5.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-770",
      "id": "foot-note-770"
    }
  }, [_vm._v("200")]), _vm._v(". D. Edelman, “Introduction,” in "), _c('em', [_vm._v("The Triumph of Elohim: From Yahwisms to Judaisms")]), _vm._v(", ed. D. V. Edelman, CBET 13 (Kampen: Kok Pharos, 1995), 23–24. According to P. Davies, Judaism begins with first a “series of discrete historical developments” regarding cultural customs, which are then cultivated into a distinct lifestyle “in opposition to ‘Hellenism’ and finally as a set of competing and ever more religious definitions of that culture” (“Scenes from the Early History of Judaism,” in Edelman, "), _c('em', [_vm._v("Triumph of Elohim")]), _vm._v(", 180–81).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-769",
      "id": "foot-note-769"
    }
  }, [_vm._v("201")]), _vm._v(". J. Neusner, "), _c('em', [_vm._v("The Judaism the Rabbis Take for Granted")]), _vm._v(" (Atlanta: Scholars Press, 1994), 12, 18.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-768",
      "id": "foot-note-768"
    }
  }, [_vm._v("202")]), _vm._v(". G. Boccaccini, "), _c('em', [_vm._v("Roots of Rabbinic Judaism: An Intellectual History, From Ezekiel to Daniel")]), _vm._v(" (Grand Rapids: Eerdmans, 2002), 27.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-767",
      "id": "foot-note-767"
    }
  }, [_vm._v("203")]), _vm._v(". Fishbane ("), _c('em', [_vm._v("Biblical Interpretation")]), _vm._v(", 122) gives examples of the extension of Leviticus’s priestly laws to the whole nation in Deuteronomy (cf. Lev 21:5–6 and Deut 14:1–2; Deut 14:21 and Lev 17:15; so also J. Milgrom, "), _c('em', [_vm._v("Leviticus 17–22: A New Translation and Commentary")]), _vm._v(", AB 3A (New York: Doubleday, 2000), 1717).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-766",
      "id": "foot-note-766"
    }
  }, [_vm._v("204")]), _vm._v(". The Torah, although not explicitly labeled "), _c('em', [_vm._v("qādoš")]), _vm._v(", nevertheless takes on a central role, perhaps an even more important one than the temple itself. As an extension of Yahweh in the form of his word, the Torah is treated as holy throughout early Judaism and Christianity (cf. John 1:1).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-765",
      "id": "foot-note-765"
    }
  }, [_vm._v("205")]), _vm._v(". H. K. Harrington, “Holiness and Purity in Ezra-Nehemiah,” 99; Milgrom, "), _c('em', [_vm._v("Leviticus 17–22")]), _vm._v(", 1398.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-764",
      "id": "foot-note-764"
    }
  }, [_vm._v("206")]), _vm._v(". R. Bauckham, "), _c('em', [_vm._v("Jesus and the God of Israel: God Crucified and Other Studies on the New Testament’s Christology of Divine Identity")]), _vm._v(" (Grand Rapids: Eerdmans, 2008), 9. During First Temple times, the existence of other gods was acknowledged even among the Israelite faithful (cf. Judg 11:24). Furthermore, many Israelites worshiped pagan deities, and this became a major point of contention for the preexilic prophets and a primary reason for the exile.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-763",
      "id": "foot-note-763"
    }
  }, [_vm._v("207")]), _vm._v(". See the discussion of R. Bauckham and N. MacDonald in Bauckham, "), _c('em', [_vm._v("Jesus and the God of Israel")]), _vm._v(", 60–71.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-762",
      "id": "foot-note-762"
    }
  }, [_vm._v("208")]), _vm._v(". L. Grabbe is skeptical of what can really be ascertained about Judaic religion in the Persian period but agrees that two aspects differentiated it from other Northwest Semitic cults: “These are worship of a god called Yahweh; the other is the Jerusalem temple,” the first being the more important (“Israel’s Historical Reality after the Exile,” in "), _c('em', [_vm._v("The Crisis of Israelite Religion: Transformation of Religious Tradition in Exilic and Post-Exilic Times")]), _vm._v(", ed. B. Becking and M. Korpel, OTS 42 [Leiden: Brill, 1999], 23, 30). While preexilic Israel worshiped Yahweh, they also worshiped other gods.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-761",
      "id": "foot-note-761"
    }
  }, [_vm._v("209")]), _vm._v(". See R. Kletter, “Pots and Polities: Material Remains of Late Iron Age Judah in Relation to Its Political Borders,” "), _c('em', [_vm._v("BASOR")]), _vm._v(" 314 (1999): 19–54; O. Keel and Ch. Uehlinger, "), _c('em', [_vm._v("Gods, Goddesses, and Images of God in Ancient Israel")]), _vm._v(" (Minneapolis: Fortress, 1992), 387–91.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-760",
      "id": "foot-note-760"
    }
  }, [_vm._v("210")]), _vm._v(". Stern, "), _c('em', [_vm._v("Assyrian, Babylonian, and Persian Periods")]), _vm._v(", 479, 488; cf. S. Ackerman, who does not see a break with the veneration of other gods throughout the entire Old Testament period ("), _c('em', [_vm._v("Under Every Green Tree: Popular Religion in Sixth-Century Judah")]), _vm._v(" [Atlanta: SBL Press 1992], 5–99); cf. also C. Frevel, "), _c('em', [_vm._v("Aschera und der Ausschlie")]), _c('span', {
    staticClass: "greek-italic"
  }, [_vm._v("β")]), _c('em', [_vm._v("lichkeitsanspruch YHWHs")]), _vm._v(", BBB 94 (Weinheim: Beltz Athenäum, 1995).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-759",
      "id": "foot-note-759"
    }
  }, [_vm._v("211")]), _vm._v(". L. W. Hurtado, “Monotheism,” in "), _c('em', [_vm._v("DEJ")]), _vm._v(", 961–62.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-758",
      "id": "foot-note-758"
    }
  }, [_vm._v("212")]), _vm._v(". In the growing debate on who counted as a Jew, S. Weitzman suggests that a Jew was simply one who claimed to adhere to the Law of Moses ("), _c('em', [_vm._v("Surviving Sacrilege: Cultural Persistence in Jewish Antiquity")]), _vm._v(" [Cambridge, MA: Harvard University Press, 2005], 3).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-757",
      "id": "foot-note-757"
    }
  }, [_vm._v("213")]), _vm._v(". S. Schwartz, "), _c('em', [_vm._v("Imperialism and Jewish Society, 200 B.C.E. to 640 C.E.")]), _vm._v(" (Princeton, NJ: Princeton University Press, 2004).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-756",
      "id": "foot-note-756"
    }
  }, [_vm._v("214")]), _vm._v(". Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 53; Eskenazi, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", 58.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-755",
      "id": "foot-note-755"
    }
  }, [_vm._v("215")]), _vm._v(". J. Wright, “Seeking, Finding and Writing in Ezra-Nehemiah,” in Boda and Redditt, "), _c('em', [_vm._v("Unity and Disunity in Ezra-Nehemiah")]), _vm._v(", 279.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-754",
      "id": "foot-note-754"
    }
  }, [_vm._v("216")]), _vm._v(". Fried, "), _c('em', [_vm._v("Priest and the Great King")]), _vm._v(", 214–15, citing Briant, "), _c('em', [_vm._v("Cyrus to Alexander")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-753",
      "id": "foot-note-753"
    }
  }, [_vm._v("217")]), _vm._v(". Cf. Wright, “Seeking, Finding and Writing in Ezra-Nehemiah,” 300–301.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-752",
      "id": "foot-note-752"
    }
  }, [_vm._v("218")]), _vm._v(". Blenkinsopp ("), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 152–55) cites several examples of Deuteronomic law that are basic to the reforms of Ezra and Nehemiah (e.g., Ezra 9:1 and Neh 13:1 build on Deut 7:1–6; 23:1–9), although he admits influence from the priestly traditions as well.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-751",
      "id": "foot-note-751"
    }
  }, [_vm._v("219")]), _vm._v(". See the collected essays in T. Römer and K. Schmid, eds., "), _c('em', [_vm._v("Les dernières rédactions du Pentateuque, de l’Hexateuque et de l’ Ennéateuque")]), _vm._v(", BETL 203 (Leuven: Peeters, 2007); and in T. Römer, ed., "), _c('em', [_vm._v("The Books of Leviticus and Numbers")]), _vm._v(" (Leuven: Peeters, 2007). C. Nihan argues for three stages of composition in Leviticus parallel to the time of Ezra-Nehemiah, during the Persian Period, with closure effected in the late Persian or early Hellenistic period ("), _c('em', [_vm._v("From Priestly Torah to Pentateuch: A Study in the Composition of the Book of Leviticus")]), _vm._v(", FAT 2/25 [Tübingen: Mohr Siebeck, 2007], 19). The final redaction of Leviticus, however, cannot be later than the mid-third century BCE, because of the book’s appearance among early Dead Sea Scrolls. M. Douglas claims that Leviticus was written in opposition to the exclusionary attitude toward foreigners in Ezra-Nehemiah (“Responding to Ezra: The Priests and the Foreign Wives,” "), _c('em', [_vm._v("BibInt")]), _vm._v(" 10 [2002]: 16–23). According to M. Leuchter, Leviticus 1–16 supports Aaronide authority and combats Nehemiah’s support of the Levites (“The Politics of Ritual Rhetoric: A Proposed Sociopolitical Context for the Redaction of Leviticus 1–16,” "), _c('em', [_vm._v("VT")]), _vm._v(" 60 [2010]: 345–65). Not all scholars agree as to the “lateness” of Leviticus. I. Knohl dates the priestly stratum between the mid-tenth and mid-eighth centuries BCE ("), _c('em', [_vm._v("The Sanctuary of Silence: The Priestly Torah and the Holiness School")]), _vm._v(" [Minneapolis: Fortress, 1995], 220–21); Milgrom places the two major sections of Leviticus (P and H) in the last two centuries of the First Temple with final redaction no later than the exile ("), _c('em', [_vm._v("Leviticus 1–16")]), _vm._v(", 12–13, 28–29). The lack of Persian sociolinguistics in Leviticus is an argument favoring an earlier setting for Leviticus; see F. H. Polak, “Sociolinguistics and the Judean Speech Community in the Achaemenid Empire,” in "), _c('em', [_vm._v("Judah and the Judeans in the Persian Period")]), _vm._v(", ed. O. Lipschits and M. Oeming (Winona Lake, IN: Eisenbrauns, 2006), 592, 600–606.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-750",
      "id": "foot-note-750"
    }
  }, [_vm._v("220")]), _vm._v(". Kidner, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", 26–27.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-749",
      "id": "foot-note-749"
    }
  }, [_vm._v("221")]), _vm._v(". S. Japhet, “What May Be Learned from Ezra–Nehemiah about the Composition of the Pentateuch?,” in "), _c('em', [_vm._v("The Formation of the Pentateuch: Bridging the Academic Cultures of Europe, Israel, and North America")]), _vm._v(", ed. J. C. Gertz et al., FAT 111 (Tübingen: Mohr Siebeck, 2016), 543–61; cf. Myers, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", lxii; Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 48; on this question, see also C. Houtman, “Ezra and the Law,” "), _c('em', [_vm._v("OTS")]), _vm._v(" 21 (1981): 91–115; Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", xxxvii–xxxix; Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 233: “There appear to be references to most major strands of the Pentateuch both in passages concerned with the law (e.g., Nehemiah 10) and with the early history of Israel (e.g., Nehemiah 9)”; Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 152–57.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-748",
      "id": "foot-note-748"
    }
  }, [_vm._v("222")]), _vm._v(". Furthermore, Darius’s edict shows familiarity with cultic needs known primarily from Leviticus: bulls, rams, lambs, wheat, salt, wine, and grain (Ezra 6:9–10). Bulls and rams were used for sacrifices of atonement (Lev 1:5; 4:3; 5:15). A lamb was offered every morning and evening along with grain and drink offerings (Lev 6:9–13[2–6]; Num 28:3–4), in addition to national feasts (Lev 23:4–44; Num 28:16–29:40), Sabbaths, and new moon celebrations (Num 28:9–15). Wheat was offered up as a grain offering in a variety of ways (e.g., raw flour, baked dough, toasted bread, or pancakes, Lev 2:1–7). Salt was necessary for all offerings, but especially the grain offering (Lev 2:13). Wine was needed for drink offerings (Exod 29:40; Lev 23:13), which, like the grain offerings, accompanied the meat sacrifices. Grain offerings were cooked with oil (Lev 2:1–7). More particularly, the "), _c('em', [_vm._v("tôdâ")]), _vm._v(" (“thanksgiving sacrifice”) of Lev 7, which is accompanied by loaves of bread (see vv. 12–13), plays a strong role in Nehemiah’s dedication of the wall (Neh 12:31).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-747",
      "id": "foot-note-747"
    }
  }, [_vm._v("223")]), _vm._v(". See the discussion in Williamson, "), _c('em', [_vm._v("Studies in Persian Period History and Historiography")]), _vm._v(", 238.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-746",
      "id": "foot-note-746"
    }
  }, [_vm._v("224")]), _vm._v(". Reinmuth, “Nehemiah 8,” 258. Interestingly, Ezra-Nehemiah’s version of Torah reading does not include Deuteronomy’s children and foreigners in the assembly.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-745",
      "id": "foot-note-745"
    }
  }, [_vm._v("225")]), _vm._v(". For further discussion of the terminology of Leviticus in Ezra-Nehemiah, see H. K. Harrington, “The Use of Leviticus in Ezra-Nehemiah,” "), _c('em', [_vm._v("Journal of Hebrew Scriptures")]), _vm._v(" (2013); M. Boda, "), _c('em', [_vm._v("Praying the Tradition")]), _vm._v(" (Berlin: de Gruyter, 1999), 43–51.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-744",
      "id": "foot-note-744"
    }
  }, [_vm._v("226")]), _vm._v(". J. Milgrom notes the continuation of this spelling in Qumran literature, in 1QM ix 8 and CD xii 16 ("), _c('em', [_vm._v("Leviticus 23–27: A New Translation with Introduction and Commentary")]), _vm._v(", AB 3B [New York: Doubleday, 2000], 2301; see "), _c('em', [_vm._v("BDB")]), _vm._v("). Alternatively, "), _c('em', [_vm._v("gā")]), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ʾ")]), _c('em', [_vm._v("al")]), _vm._v(" (with "), _c('em', [_vm._v("aleph")]), _vm._v(") can be translated “stain” (cf. Isa 63:3).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-743",
      "id": "foot-note-743"
    }
  }, [_vm._v("227")]), _vm._v(". Mary Douglas, “Responding to Ezra: The Priests and the Foreign Wives,” "), _c('em', [_vm._v("BibInt")]), _vm._v(" 10 (2002): 16–23.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-742",
      "id": "foot-note-742"
    }
  }, [_vm._v("228")]), _vm._v(". Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 155–56, surveys the use of the law throughout the monarchical period.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-741",
      "id": "foot-note-741"
    }
  }, [_vm._v("229")]), _vm._v(". Milgrom, "), _c('em', [_vm._v("Leviticus 1–16: A New Translation with Introduction and Commentary")]), _vm._v(", AB 3 (New York: Doubleday, 1991), 12.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-740",
      "id": "foot-note-740"
    }
  }, [_vm._v("230")]), _vm._v(". L. Grabbe, “The Law of Moses in the Ezra Tradition: More Virtual Than Real,” in Watts, "), _c('em', [_vm._v("Persia and Torah")]), _vm._v(", 91–114.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-739",
      "id": "foot-note-739"
    }
  }, [_vm._v("231")]), _vm._v(". When compared with MT, LXX presents a different order of furnishing in the Tabernacle, with some abbreviations, omissions, and added details; see D. W. Gooding, "), _c('em', [_vm._v("The Account of the Tabernacle: Translations and Textual Problems of the Greek Exodus")]), _vm._v(" (Cambridge: Cambridge University Press, 1959), 99–101.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-738",
      "id": "foot-note-738"
    }
  }, [_vm._v("232")]), _vm._v(". E. Ben Zvi, “Inclusion and Exclusion from Israel as Conveyed by the Use of the Term ‘Israel’ in Post-Monarchic Biblical Texts,” in "), _c('em', [_vm._v("The Pitcher Is Broken: Memorial Essays for Gösta Ahlström")]), _vm._v(", ed. S. W. Holloway and L. K. Handy (Sheffield: Sheffield Academic, 1995), 127; Fishbane, "), _c('em', [_vm._v("Biblical Interpretation")]), _vm._v(", 91–95.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-737",
      "id": "foot-note-737"
    }
  }, [_vm._v("233")]), _vm._v(". L. H. Schiffman, "), _c('em', [_vm._v("Qumran and Jerusalem: Studies in the DSS and the History of Judaism")]), _vm._v(" (Grand Rapids: Eerdmans, 2010), 193.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-736",
      "id": "foot-note-736"
    }
  }, [_vm._v("234")]), _vm._v(". Albertz and Becking, “Problems and Possibilities,” xii.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-735",
      "id": "foot-note-735"
    }
  }, [_vm._v("235")]), _vm._v(". Jaffee, "), _c('em', [_vm._v("Dynamics of Texts and Traditions")]), _vm._v(", 62.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-734",
      "id": "foot-note-734"
    }
  }, [_vm._v("236")]), _vm._v(". This table expands on data from Blenkinsopp, "), _c('em', [_vm._v("Ezra-Nehemiah")]), _vm._v(", 153–55.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-733",
      "id": "foot-note-733"
    }
  }, [_vm._v("237")]), _vm._v(". Eskenazi, “Unity and Disunity in Ezra-Nehemiah,” 326; cf. Reinmuth, “Nehemiah 8,” 19, 21.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-732",
      "id": "foot-note-732"
    }
  }, [_vm._v("238")]), _vm._v(". Schiffman, “Pre-Maccabean Halakhah,” 185.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-731",
      "id": "foot-note-731"
    }
  }, [_vm._v("239")]), _vm._v(". See a similar list in Schiffman, “Pre-Maccabean Halakhah,” 186–87. J. Blenkinsopp also adds religious homogeneity and political autonomy within the empire, stating that this ideal becomes a model for the later Maccabees, who achieved an independent nation of Jews in imperial times ("), _c('em', [_vm._v("Judaism: The First Phase: The Place of Ezra and Nehemiah in the Origins of Judaism")]), _vm._v(" [Grand Rapids: Eerdmans, 2009], 188). The dynasty usurped both the high priest and king positions and enforced its will by using the military as well as foreign mercenaries. According to the books of Maccabees, Nehemiah was the role model of a governor who enforced Jewish lifestyle.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-730",
      "id": "foot-note-730"
    }
  }, [_vm._v("240")]), _vm._v(". L. H. Schiffman, "), _c('em', [_vm._v("Who Was a Jew? Rabbinic and Halakhic Perspectives on the Jewish-Christian Schism")]), _vm._v(" (Hoboken, NJ: Ktav, 1985), 14–17.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-729",
      "id": "foot-note-729"
    }
  }, [_vm._v("241")]), _vm._v(". See W. Loader, "), _c('em', [_vm._v("Enoch, Levi, and Jubilees on Sexuality: Attitudes toward Sexuality in the Early Enoch Literature, the Aramaic Levi Document, and the Book of Jubilees")]), _vm._v(" (Grand Rapids: Eerdmans, 2007); "), _c('em', [_vm._v("The Dead Sea Scrolls on Sexuality: Attitudes towards Sexuality in Sectarian and Related Literature at Qumran")]), _vm._v(" (Grand Rapids: Eerdmans, 2009). For a more in-depth treatment of the intermarriage injunctions of Ezra-Nehemiah and their legacy in the Dead Sea Scrolls, see Harrington, “Intermarriage in the Qumran Texts,” 251–79.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-728",
      "id": "foot-note-728"
    }
  }, [_vm._v("242")]), _vm._v(". Schiffman, “Pre-Maccabean Halakhah,” 186; cf. 194: “Certain fundamentals of Pharisaic views on temple and purity issues, as well as a host of other halakhic topics, already existed not only in the Hasmonean period but even before.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-727",
      "id": "foot-note-727"
    }
  }, [_vm._v("243")]), _vm._v(". Schiffman, “Pre-Maccabean Halakhah,” 187.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-726",
      "id": "foot-note-726"
    }
  }, [_vm._v("244")]), _vm._v(". Milgrom, "), _c('em', [_vm._v("Leviticus 1–16")]), _vm._v(", 359–61. Milgrom also offers the explanation that only the resident non-Israelites were prohibited, because they were like the seven Canaanite nations of Deut 7, which were "), _c('em'), _c('span', {
    staticClass: "chari"
  }, [_vm._v("ḥ")]), _c('em', [_vm._v("erem")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-725",
      "id": "foot-note-725"
    }
  }, [_vm._v("245")]), _vm._v(". See L. H. Schiffman, "), _c('em', [_vm._v("Halakhah at Qumran")]), _vm._v(", Studies in Judaism in Late Antiquity 16 (Leiden: Brill, 1975), 54–60. Schiffman (“Pre-Maccabean Halakhah,” 191) writes, “Much of this exegesis is based on analogy to parallel passages or language (like rabbinic "), _c('em', [_vm._v("heqeš")]), _vm._v(") or harmonization of material on one subject which is scattered in the Torah.” Cf. J. Milgrom, “The Scriptural Foundations and Derivations in the Laws of Purity of the Temple Scroll,” in "), _c('em', [_vm._v("Archaeology and History in the Dead Sea Scrolls: The New York University Conference in Memory of Yigael Yadin")]), _vm._v(", ed. L. H. Schiffman, JSOTSup 8 (Sheffield: JSOT Press, 1990), 83–99.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-724",
      "id": "foot-note-724"
    }
  }, [_vm._v("246")]), _vm._v(". M. Avi-Yonah, "), _c('em', [_vm._v("The Herodian Period")]), _vm._v(" (New Brunswick, NJ: Rutgers University Press, 1975), 333; S. Safrai and M. Stern, eds., "), _c('em', [_vm._v("The Jewish People in the First Century")]), _vm._v(" (Assen; Amsterdam: Van Gorcum, 1976), 2:914–33. Williamson ("), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", 281–82) argues that these liturgical elements were already in place by Nehemiah’s day and were simply utilized on this occasion. They cannot be taken as evidence for an actual synagogue practice; nevertheless, the presence of these conventions in a single service outside of the Temple this early is significant.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-723",
      "id": "foot-note-723"
    }
  }, [_vm._v("247")]), _vm._v(". L. Levine, "), _c('em', [_vm._v("The Ancient Synagogue: The First Thousand Years")]), _vm._v(" (New Haven: Yale University Press, 2000), 139. Regular Jewish Torah reading sessions probably gave impetus to the creation of the LXX in the third century BCE. The Qumran sect engaged in ongoing Torah study sessions, but Philo implies that even the Essenes who lived throughout the land gathered in synagogues on the Sabbath to read and study the Torah ("), _c('em', [_vm._v("That Every Good Person Is Free")]), _vm._v(" 75–76, 81–83; see also "), _c('em', [_vm._v("Hypothetica")]), _vm._v(" 9.1; Josephus, "), _c('em', [_vm._v("Jewish War")]), _vm._v(" 2.124); so also the Therapeutae in Egypt (Philo, "), _c('em', [_vm._v("On the Contemplative Life")]), _vm._v(" 30–31; "), _c('em', [_vm._v("Every Good Person")]), _vm._v(" 81–84); see Luke 4:17–21.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-722",
      "id": "foot-note-722"
    }
  }, [_vm._v("248")]), _vm._v(". L. Levine, "), _c('em', [_vm._v("Ancient Synagogue")]), _vm._v(", 319–23"), _c('em', [_vm._v(".")]), _vm._v(" Josephus reports that the high priest stood on a platform to read from the Torah ("), _c('em', [_vm._v("Antiquities")]), _vm._v(" 4.209–211). Likewise, according to the Mishnah, King Agrippa read a section of the Torah from “a platform of wood in the Temple courtyard” (m. So"), _c('span', {
    staticClass: "chari-r"
  }, [_vm._v("ṭ")]), _vm._v("ah 7:8, translation mine)"), _c('em', [_vm._v(".")])]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-721",
      "id": "foot-note-721"
    }
  }, [_vm._v("249")]), _vm._v(". L. Levine, "), _c('em', [_vm._v("Ancient Synagogue")]), _vm._v(", 38–39.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-720",
      "id": "foot-note-720"
    }
  }, [_vm._v("250")]), _vm._v(". Blenkinsopp, "), _c('em', [_vm._v("Judaism")]), _vm._v(", 182–88.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-719",
      "id": "foot-note-719"
    }
  }, [_vm._v("251")]), _vm._v(". S. J. D. Cohen, “From the Bible to the Talmud: The Prohibition of Intermarriage,” "), _c('em', [_vm._v("Hebrew Annual Review")]), _vm._v(" 7 (1983), 23; C. E. Hayes, "), _c('em', [_vm._v("Gentile Impurities and Jewish Identities: Intermarriage and Conversion from the Bible to the Talmud")]), _vm._v(" (Oxford: Oxford University Press, 2002), 24–26.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-718",
      "id": "foot-note-718"
    }
  }, [_vm._v("252")]), _vm._v(". According to the priestly system, a gentile who wished to reside among Israel, a "), _c('em', [_vm._v("gēr")]), _vm._v(", could adopt Israel’s religion. The "), _c('em', [_vm._v("gēr")]), _vm._v(" was required to maintain the purity laws of Israel (Lev 17:15–16). In addition, Num 15:16 requires a purification offering that would atone for both the sins of Israel and those of the "), _c('em', [_vm._v("gēr")]), _vm._v(". Thus, according to the Torah, a "), _c('em', [_vm._v("gēr")]), _vm._v(" is obligated to maintain purity, and purifications are effective for him.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-717",
      "id": "foot-note-717"
    }
  }, [_vm._v("253")]), _vm._v(". Williamson ("), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", 136) notes the more forceful language of Ezra 9:11 over Lev 18:24–30.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-716",
      "id": "foot-note-716"
    }
  }, [_vm._v("254")]), _vm._v(". J. M. Baumgarten, “The Disqualification of Priests in 4Q Fragments of the ‘Damascus Document,’ a Specimen of the Recovery of Pre-Rabbinic Halakha,” in "), _c('em', [_vm._v("The Madrid Qumran Congress")]), _vm._v(", ed. J. Trebolle-Barrera and L. V. Montaner, STDJ 11 (Leiden: E. J. Brill, 1992), 2:513; cf. E. Regev, “Pure Individualism: The Idea of Non-Priestly Purity in Ancient Judaism,” "), _c('em', [_vm._v("JSJ")]), _vm._v(" 31 (2000): 176–202.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-715",
      "id": "foot-note-715"
    }
  }, [_vm._v("255")]), _vm._v(". J. M. Baumgarten (“Disqualification of Priests in 4Q Fragments,” 508) was one of the first to point out the blur between moral and ritual impurity at Qumran. Also, the impurity of scale disease is referred to as the work of a malevolent spirit, not just a condition in need of the prescribed purifications of Leviticus 14 (4Q272 i 1–16).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-714",
      "id": "foot-note-714"
    }
  }, [_vm._v("256")]), _vm._v(". For a survey of relevant Qumran texts and discussion, see H. K. Harrington, "), _c('em', [_vm._v("The Purity Texts")]), _vm._v(", Companion to the Qumran Scrolls 5 (London: T&T Clark, 2004), 112–27.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-713",
      "id": "foot-note-713"
    }
  }, [_vm._v("257")]), _vm._v(". Blenkinsopp, “Temple and Society in Achaemenid Judah,” 53.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-712",
      "id": "foot-note-712"
    }
  }, [_vm._v("258")]), _vm._v(". The temple is second only to Yahweh himself for the returnees, according to Grabbe, “Israel’s Historical Reality after the Exile,” 30.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-711",
      "id": "foot-note-711"
    }
  }, [_vm._v("259")]), _vm._v(". P. R. Ackroyd, “The Temple Vessels—A Continuity Theme,” in "), _c('em', [_vm._v("Studies in the Religion of Ancient Israel")]), _vm._v(", ed. G. W. Anderson et al., VTSup 23 (Leiden: Brill, 1972), 178. See other links between the two temple building projects in J. Clauss, “Understanding the Mixed Marriages of Ezra-Nehemiah in the Light of Temple-Building and the Book’s Concept of Jerusalem,” in Frevel, "), _c('em', [_vm._v("Mixed Marriages")]), _vm._v(", 112–13; Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", 48.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-710",
      "id": "foot-note-710"
    }
  }, [_vm._v("260")]), _vm._v(". Schiffman, "), _c('em', [_vm._v("From Text to Tradition")]), _vm._v(", 45.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-709",
      "id": "foot-note-709"
    }
  }, [_vm._v("261")]), _vm._v(". The majority of scholars maintain that the Qumranite self-concept as a sanctuary was only a temporary measure as the sect awaited the messianic era with a renewed sanctuary and purified cult; see CD iv 15–18; v 6–7; vi 11–13; 1QM ii 1–6; E. Regev, “Abominated Temple and a Holy Community: The Formation of the Notions of Purity and Impurity in Qumran,” "), _c('em', [_vm._v("DSD")]), _vm._v(" 10 (2003): 253, 271.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-708",
      "id": "foot-note-708"
    }
  }, [_vm._v("262")]), _vm._v(". P. Trudinger, "), _c('em', [_vm._v("The Psalms of the Tamid Service: A Liturgical Text from the Second Temple")]), _vm._v(" (Leiden: Brill, 2004), 50–51.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-707",
      "id": "foot-note-707"
    }
  }, [_vm._v("263")]), _vm._v(". Kidner, "), _c('em', [_vm._v("Ezra and Nehemiah")]), _vm._v(", 24.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-706",
      "id": "foot-note-706"
    }
  }, [_vm._v("264")]), _vm._v(". Eskenazi ("), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", 176, 179) claims that Ezra-Nehemiah is thematically different from Chronicles since it downplays its heroes and affirms the people.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-705",
      "id": "foot-note-705"
    }
  }, [_vm._v("265")]), _vm._v(". Eskenazi, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", 84–85. Against Eskenazi’s view, however, see Breneman, "), _c('em', [_vm._v("Ezra, Nehemiah, Esther")]), _vm._v(", 115, who says that the dimensions of the temple in Ezra 6:3 may be a scribal error.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-704",
      "id": "foot-note-704"
    }
  }, [_vm._v("266")]), _vm._v(". Eskenazi, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", 121; contra Blenkinsopp, “Ezra-Nehemiah,” 307, 307n4, who says the term is never used that way. On the other hand, the author of the Temple Scroll often refers to the house of God and the city interchangeably. Ezra-Nehemiah reflects an early stage of expansion of the boundary of holiness around the temple to its environs.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-703",
      "id": "foot-note-703"
    }
  }, [_vm._v("267")]), _vm._v(". For a list of the extra purity requirements required by the Temple Scroll in the temple city, see Harrington, "), _c('em', [_vm._v("Purity Texts")]), _vm._v(", 51.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-702",
      "id": "foot-note-702"
    }
  }, [_vm._v("268")]), _vm._v(". Fishbane, "), _c('em', [_vm._v("Biblical Interpretation")]), _vm._v(", 123. Also, note the parallel “remnant” language in Isa 6:13: Israel will be destroyed by its enemies down to the stump of its “holy seed.” The phrase in Isaiah is not in the LXX and is likely a scribal addition (Williamson, "), _c('em', [_vm._v("Ezra, Nehemiah")]), _vm._v(", 132). Ezra’s decree is drastic compared to the laws of the Pentateuch. In essence, he combines the notion of “holy nation” (Deut 7:6) with that of the “seed of Abraham” (Isa 41:8; Jer 33:26; Ps 105:6; 2 Chr 20:7).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-701",
      "id": "foot-note-701"
    }
  }, [_vm._v("269")]), _vm._v(". Fishbane ("), _c('em', [_vm._v("Biblical Interpretation")]), _vm._v(", 114) calls this identity “ritualized ethnicity.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-700",
      "id": "foot-note-700"
    }
  }, [_vm._v("270")]), _vm._v(". For further discussion of holiness in Second Temple Judaism, see H. K. Harrington, "), _c('em', [_vm._v("Holiness: Rabbinic Judaism and the Graeco-Roman World")]), _vm._v(" (London: Routledge, 2001); see also Milgrom"), _c('em', [_vm._v(", Leviticus 17–22")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-699",
      "id": "foot-note-699"
    }
  }, [_vm._v("271")]), _vm._v(". Of course, Ammon is a contemporary problem too (see Neh 13). The writer has added in the Ammonites, Moabites, and Egyptians (in LXX and 1 Esd 8:68, Edomites) from Deut 23’s list of marriage candidates that could only be included in Israel after several generations.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-698",
      "id": "foot-note-698"
    }
  }, [_vm._v("272")]), _vm._v(". Y. Kaufman, "), _c('em', [_vm._v("History of the Israelite Faith")]), _vm._v(" (Hebrew) (Jerusalem: Bialik Institute, 1956), 8:284ff. The Pentateuch warns against Canaanite intermarriage but does not dictate specific measures to be taken in cases of violation.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-697",
      "id": "foot-note-697"
    }
  }, [_vm._v("273")]), _vm._v(". Eskenazi, "), _c('em', [_vm._v("In an Age of Prose")]), _vm._v(", 112–21. A parallel development can be detected with regard to the written word: from royal decree supporting the temple to Ezra’s combination of both scribe and priest to the community at large that is taught and read to (Neh 8:8) and that finally learns to read the Word of God for itself (Neh 9:3). As J. Wright (“Seeking, Finding and Writing in Ezra-Nehemiah,” 301) puts it, “At a time when the high priesthood is corrupt, they stand in the midst of the fortified Jerusalem surrounded by the sanctified Wall (3:1ff.; see also 12:27ff.) and engage in the activity of seeking-and-finding with the help of a "), _c('span', {
    staticClass: "hebrew"
  }, [_vm._v("פחה")]), _vm._v(" devoted to the Torah and a "), _c('span', {
    staticClass: "hebrew"
  }, [_vm._v("ספר")]), _vm._v(" at his side.”")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-696",
      "id": "foot-note-696"
    }
  }, [_vm._v("274")]), _vm._v(". Eskenazi, “Unity and Disunity in Ezra-Nehemiah,” 322.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-695",
      "id": "foot-note-695"
    }
  }, [_vm._v("275")]), _vm._v(". T. C. Eskenazi, “The Missions of Ezra and Nehemiah,” in Lipschits and Oeming, "), _c('em', [_vm._v("Judah and the Judeans")]), _vm._v(", 509–29. S. J. D. Cohen, "), _c('em', [_vm._v("The Beginnings of Jewishness: Boundaries, Varieties, Uncertainties")]), _vm._v(" (Berkeley: University of California Press, 1999), 104–5, argues that the term “Jew” in both Hebrew and Greek texts ("), _c('em', [_vm._v("Yehudi")]), _vm._v(", "), _c('em', [_vm._v("Iudaios")]), _vm._v(") before the second century BCE does not denote an adherent of a religion or particular way of life, but Eskenazi counters that, according to Ezra-Nehemiah, Ezra built just such a Jewish community (“Missions of Ezra and Nehemiah,” 511).")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-694",
      "id": "foot-note-694"
    }
  }, [_vm._v("276")]), _vm._v(". Many Second Temple authors refer to heaven as the “temple” where the angels worship God (e.g., 4Q400 1 i 13; 4Q405 23 ii 11; cf. the temples in heaven of 1 En. 14) or where Christ makes priestly intercession for the saints (Heb 8:2–4). For discussion of the Aramaic Levi Document, see Jonas D. Greenfield, Michael E. Stone, and Esther Eshel, "), _c('em', [_vm._v("The Aramaic Levi Document: Edition, Translation and Commentary")]), _vm._v(", SVTP 19 (Leiden: Brill, 2004), 74–75.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-693",
      "id": "foot-note-693"
    }
  }, [_vm._v("277")]), _vm._v(". Bickerman, "), _c('em', [_vm._v("From Ezra to the Last of the Maccabees")]), _vm._v(", 10.")]), _vm._v(" "), _c('p', {
    staticClass: "footnotes"
  }, [_c('a', {
    attrs: {
      "href": "#/#footnote-692",
      "id": "foot-note-692"
    }
  }, [_vm._v("278")]), _vm._v(". See arguments for Christianity from the perspective of rationalism in C. S. Lewis, "), _c('em', [_vm._v("Mere Christianity")]), _vm._v(" (London: Macmillan, 1952).")]), _vm._v(" "), _c('div', {
    staticClass: "img"
  }, [_c('p', {
    staticClass: "image",
    attrs: {
      "id": "m1"
    }
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/96.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "image-captionc"
  }, [_vm._v("Yehud in the Persian Period")])]), _vm._v(" "), _c('p', {
    staticClass: "image",
    attrs: {
      "id": "fig1"
    }
  }, [_c('img', {
    attrs: {
      "alt": "Image",
      "src": "images/97.jpg"
    }
  })])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-a235dfd0","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/010_Chapter01.vue
// module id = 84
// module chunks = 0